import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { supabase } from './supabase'
import { t, LANGS, VARS_BY_LANG } from './i18n'
import { calculerIndexation, estDuPourIndexation } from './indexation-engine'
import { calculerIndexationFrance } from './indexation-france'

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || ''
}

const STRIPE_STARTER_URL = process.env.REACT_APP_STRIPE_STARTER_URL || 'https://buy.stripe.com/test_6oU3cw2Cj1ASdJs5wYf7i04'
const STRIPE_PRO_URL     = process.env.REACT_APP_STRIPE_PRO_URL     || 'https://buy.stripe.com/test_6oU8wQdgX93k5cWe3uf7i05'

const stripeUrl = (base, userId, email) =>
  `${base}?client_reference_id=${userId}&prefilled_email=${encodeURIComponent(email)}`

// ─── DEVISES ──────────────────────────────────────────────────
export const CURRENCIES = [
  { code: 'EUR', symbol: '€', locale: 'fr-FR', label: 'Euro (€)' },
  { code: 'GBP', symbol: '£', locale: 'en-GB', label: 'Pound (£)' },
  { code: 'USD', symbol: '$', locale: 'en-US', label: 'Dollar ($)' },
  { code: 'CHF', symbol: 'CHF', locale: 'fr-CH', label: 'Franc (CHF)' },
  { code: 'CAD', symbol: '$', locale: 'fr-CA', label: 'CAD ($)' },
]

// Contexte global devise — mis à jour depuis App
// eslint-disable-next-line no-unused-vars
let _currency = 'EUR'
let _locale   = 'fr-FR'
let _symbol   = '€'
let _langLocale = 'fr-FR' // locale basée sur la langue (pour les dates)

export function setCurrencyContext(currency, locale) {
  _currency = currency
  _locale   = locale
  _symbol   = CURRENCIES.find(c => c.code === currency)?.symbol || '€'
}

export function setLangLocale(lang) {
  const localeMap = { fr: 'fr-FR', en: 'en-GB', nl: 'nl-BE' }
  _langLocale = localeMap[lang] || 'fr-FR'
}

// Helper formatage montant
export function fmt(amount) {
  const n = Number(amount || 0)
  return `${n.toLocaleString(_locale)} ${_symbol}`
}

// Helper formatage date
export function fmtDate(dateStr, options) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString(_langLocale, options)
}

// Accès au locale courant (pour les composants)
export function getLocale() { return _locale }
export function getSymbol() { return _symbol }

// ─── LIMITES PAR PLAN ─────────────────────────────────────────
const PLAN_LIMITS = {
  free:       { biens: 1,        locataires: 1,        pdf: false, label: 'Gratuit' },
  starter:    { biens: 4,        locataires: Infinity, pdf: true,  label: 'Starter' },
  pro:        { biens: 10,       locataires: Infinity, pdf: true,  label: 'Pro' },
  enterprise: { biens: Infinity, locataires: Infinity, pdf: true,  label: 'Enterprise' },
}

// Pages réservées au plan Pro et entreprise Puis Starter
const PRO_ONLY_PAGES = ['messagerie', 'echeances', 'baux', 'notifications']
const STARTER_PLUS_PAGES = ['interventions', 'indexation', 'calendrier']
const PAYS_INDEXATION = ['BE', 'FR']  // pays où l'indexation existe (FR = phase 2)

// ─── GÉNÉRATION AUTO AU 1er DU MOIS ──────────────────────────
// Crée une ligne "pending" par locataire actif si elle n'existe
// pas encore pour le mois en cours. Silencieux, idempotent.
async function generateMissingPaiements(locatairesActifs, biens) {
  if (!locatairesActifs.length) return

  const now       = new Date()
  const annee     = now.getFullYear()
  const mois      = String(now.getMonth() + 1).padStart(2, '0')
  const moisDebut = `${annee}-${mois}-01`
  const moisFin   = `${annee}-${mois}-28`

  const { data: existing } = await supabase
    .from('paiements')
    .select('locataire_id')
    .gte('mois', moisDebut)
    .lte('mois', moisFin)

  const dejaCrees = new Set((existing || []).map(p => p.locataire_id))

  const toInsert = locatairesActifs
  .filter(loc => !dejaCrees.has(loc.id))
  .map(loc => {
    const bien = biens.find(b => b.id === loc.bien_id)
    if (!bien) return null

    const loyerNu  = Number(bien.loyer || 0)
    const chargesM = Number(bien.charges || 0)
    let montant  = loyerNu + chargesM
    let charges  = chargesM
    if (loc.date_entree) {
      const entree = new Date(loc.date_entree)
      if (entree.getFullYear() === now.getFullYear() && entree.getMonth() === now.getMonth()) {
        const joursMois = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
        const joursRestants = joursMois - entree.getDate() + 1
        const prorata = joursRestants / joursMois
        montant = Math.round((loyerNu + chargesM) * prorata * 100) / 100
        charges = Math.round(chargesM * prorata * 100) / 100
      }
    }

    return {
      bien_id:       loc.bien_id,
      locataire_id:  loc.id,
      mois:          moisDebut,
      montant,
      charges,
      montant_paye:  null,
      statut:        'pending',
      date_paiement: null,
    }
  })
  .filter(Boolean)
  if (toInsert.length) {
    await supabase.from('paiements').insert(toInsert)
  }
  }

// ─── COMPOSANTS UTILITAIRES ───────────────────────────────────
function UpgradeBanner({ plan, resource, userEmail, userId, onGoToPricing, lang = 'fr' }) {
  const isStarter  = plan === 'starter'
  const targetPlan = isStarter ? 'Pro' : 'Starter'
  const targetUrl  = isStarter
    ? stripeUrl(STRIPE_PRO_URL, userId, userEmail)
    : stripeUrl(STRIPE_STARTER_URL, userId, userEmail)
  const messages = {
     biens:      { free: t(lang,'upgrade_biens_free'), starter: t(lang,'upgrade_biens_starter') },
     locataires: { free: t(lang,'upgrade_locs_free') },
  }
  const msg = messages[resource]?.[plan] || t(lang,'upgrade_limite_gen')
  return (
    <div style={{ background: 'linear-gradient(135deg,#fff8e1,#fff3cd)', border: '1.5px solid #f5c842', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#7a5c00', marginBottom: 4 }}>{t(lang,'upgrade_limite_lbl').replace('{plan}', PLAN_LIMITS[plan]?.label)}</div>
        <div style={{ fontSize: 13, color: '#9a7700', lineHeight: 1.5 }}>{msg}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button className="btn btn-ghost btn-sm" onClick={onGoToPricing}>{t(lang,'voir_plans')}</button>
        <button className="btn btn-sm" style={{ background: '#1D9E75', color: '#fff', borderColor: '#1D9E75' }} onClick={() => window.open(targetUrl, '_blank')}>{t(lang,'passer_plan').replace('{plan}', targetPlan)} →</button>
      </div>
    </div>
  )
}


function ProUpgradePrompt({ onGoToPricing, lang = 'fr', titleKey = 'pro_feature_titre', descKey = 'pro_feature_desc', btnKey = 'voir_plan_pro' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: '60px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: 48 }}>⭐</div>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, color: '#0d2b1f' }}>{t(lang, titleKey)}</div>
      <div style={{ fontSize: 15, color: '#888', maxWidth: 380, lineHeight: 1.6 }}>
        {t(lang, descKey)}
      </div>
      <button className="btn btn-primary" style={{ fontSize: 15, padding: '12px 28px' }} onClick={onGoToPricing}>
        {t(lang, btnKey)}
      </button>
    </div>
  )
}
function PlanBadge({ plan, customLabel, lang = 'fr' }) {
  const styles = {
    free:    { bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)', label: 'Gratuit' },
    starter: { bg: 'rgba(29,158,117,0.2)',   color: '#1D9E75',              label: '⚡ Starter' },
    pro:     { bg: 'rgba(29,158,117,0.35)',   color: '#4de8b4',              label: '⭐ Pro' },
    enterprise: { bg: 'rgba(77,232,180,0.25)',  color: '#4de8b4',              label: customLabel  || '🏢 Enterprise' },
  }
  const s = styles[plan] || styles.free
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: s.bg, color: s.color, display: 'inline-block', marginTop: 4 }}>{s.label}</span>
}

// Badge statut + partiel
function Badge({ statut, montant, montantPaye, lang = 'fr' }) {
  if (statut === 'paid')    return <span className="badge badge-paid">{t(lang,'paye_btn')} ✓</span>
  if (statut === 'late')    return <span className="badge badge-late">{t(lang,'retard_tab')} !</span>
  if (statut === 'partial') {
    const solde = Number(montant) - Number(montantPaye || 0)
    return (
      <span className="badge badge-partial" title={`${t(lang,'solde_rest')} ${fmt(solde)}`}>
        {t(lang,'partiels')}
      </span>
    )
  }
  return <span className="badge badge-pending">{t(lang,'att_tab')}</span>
}

// Modal paiement partiel
function PartialModal({ paiement, onConfirm, onCancel, lang = 'fr' }) {
  const [montantPaye, setMontantPaye] = useState(paiement.montant_paye || '')
  const solde = Number(paiement.montant) - Number(montantPaye || 0)
  const valid = montantPaye > 0 && montantPaye < paiement.montant
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{t(lang, 'paie_partiel')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 14, color: '#555' }}>
            {t(lang, 'loyer_att')} : <strong>{fmt(paiement.montant)}</strong>
          </div>
          <div className="form-group">
            <label>{t(lang, 'montant_recu')}</label>
            <input
              type="number"
              min="1"
              max={paiement.montant - 1}
              value={montantPaye}
              onChange={e => setMontantPaye(e.target.value)}
              placeholder={`Max ${paiement.montant - 1}`}
              autoFocus
            />
          </div>
          {montantPaye > 0 && (
            <div style={{ background: '#fef9f0', border: '1.5px solid #F5D08A', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#854F0B' }}>
              {t(lang,'recu_lbl')} : <strong>{fmt(montantPaye)}</strong>
              &nbsp;·&nbsp;
              {t(lang,'solde_rest')} : <strong>{fmt(solde > 0 ? solde : 0)}</strong>
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onCancel}>{t(lang,'annuler_btn')}</button>
          <button
            className="btn btn-primary"
            style={{ background: '#854F0B', borderColor: '#854F0B' }}
            disabled={!valid}
            onClick={() => onConfirm(Number(montantPaye))}
          >
            {t(lang,'enregistrer')}
          </button>
        </div>
      </div>
    </div>
  )
}

function ConfirmModal({ message, onConfirm, onCancel, confirmLabel, danger = false, lang = 'fr' }) {
  confirmLabel = confirmLabel || t(lang, 'confirmer')
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{t(lang, 'confirmer')}</div>
        <div className="modal-confirm-text">{message}</div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onCancel}>{t(lang, 'annuler_btn')}</button>
          <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  )
}

function EditBienModal({ bien, userId, onSave, onCancel, lang = 'fr' }) {
  const [form, setForm] = useState({ adresse: bien.adresse, type_bien: bien.type_bien || 'Appartement', loyer: bien.loyer, charges: bien.charges || 0, valeur: bien.valeur || '', jour_echeance: bien.jour_echeance || 1, pays: bien.pays || 'BE', region: bien.region || '', peb: bien.peb || '', dpe: bien.dpe || '', departement: bien.departement || '' })
  const [loading, setLoading] = useState(false)
  const [onglet, setOnglet]   = useState('infos')
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })

  const handleSave = async () => {
    setLoading(true)
    const nouveauLoyer = parseFloat(form.loyer)
    const ancienLoyer = parseFloat(bien.loyer)

    await supabase.from('biens').update({ adresse: form.adresse, type_bien: form.type_bien, loyer: nouveauLoyer, charges: parseFloat(form.charges) || 0, valeur: form.valeur ? parseFloat(form.valeur) : null, jour_echeance: parseInt(form.jour_echeance) || 1, pays: form.pays, region: form.pays === 'BE' ? (form.region || null) : null, peb: form.pays === 'BE' ? (form.peb || null) : null, dpe: form.pays === 'FR' ? (form.dpe || null) : null, departement: form.pays === 'FR' ? (form.departement || null) : null }).eq('id', bien.id)

    // Tracer le changement manuel de loyer (si modifié)
    if (userId && !isNaN(nouveauLoyer) && nouveauLoyer !== ancienLoyer) {
      const pct = ancienLoyer > 0 ? Math.round(((nouveauLoyer / ancienLoyer - 1) * 100) * 100) / 100 : null
      await supabase.from('loyer_historique').insert({
        user_id:     userId,
        bien_id:     bien.id,
        loyer_avant: ancienLoyer,
        loyer_apres: nouveauLoyer,
        pourcentage: pct,
        source:      'manuel',
      })
    }

    setLoading(false); onSave()
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{t(lang, 'modifier_bien')}</div>

        {/* Onglets */}
        <div className="dossier-tabs" style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #eee' }}>
          <button className={`dossier-tab ${onglet === 'infos' ? 'active' : ''}`} onClick={() => setOnglet('infos')}>
            🏠 {t(lang,'loc_panel_infos') || 'Infos'}
          </button>
          <button className={`dossier-tab ${onglet === 'historique' ? 'active' : ''}`} onClick={() => setOnglet('historique')}>
            📈 {t(lang,'idx_hist_onglet')}
          </button>
        </div>

        {/* ── ONGLET INFOS ── */}
        {onglet === 'infos' && (
          <>
            <div className="form-grid">
              <div className="form-group full"><label>{t(lang,'adresse')} *</label><input name="adresse" value={form.adresse} onChange={handleChange} /></div>
              <div className="form-group"><label>{t(lang,'type_bien')}</label><select name="type_bien" value={form.type_bien} onChange={handleChange}><option>{t(lang,'appartement')}</option><option>{t(lang,'studio')}</option><option>{t(lang,'maison')}</option><option>{t(lang,'kot')}</option><option>{t(lang,'commerce')}</option></select></div>
              <div className="form-group"><label>{t(lang,'loyer')} *</label><input name="loyer" type="number" value={form.loyer} onChange={handleChange} /></div>
              <div className="form-group"><label>{t(lang,'charges')}</label><input name="charges" type="number" value={form.charges} onChange={handleChange} /></div>
              <div className="form-group"><label>{t(lang,'valeur')}</label><input name="valeur" type="number" value={form.valeur} onChange={handleChange} /></div>
              <div className="form-group">
                <label>{t(lang,'jour_ech')}</label>
                <input name="jour_echeance" type="number" min="1" max="28" value={form.jour_echeance} onChange={handleChange} placeholder="1" />
              </div>
              <div className="form-group">
                <label>{t(lang,'pays_lbl')}</label>
                <select name="pays" value={form.pays} onChange={handleChange}>
                  <option value="BE">Belgique</option>
                  <option value="FR">France</option>
                  <option value="LU">Luxembourg</option>
                  <option value="OTHER">{t(lang,'autre_pays')}</option>
                </select>
              </div>
              {form.pays === 'BE' && (
                <>
                  <div className="form-group">
                    <label>{t(lang,'region_lbl')}</label>
                    <select name="region" value={form.region} onChange={handleChange}>
                      <option value="">—</option>
                      <option value="wallonie">{t(lang,'region_wallonie')}</option>
                      <option value="bruxelles">{t(lang,'region_bruxelles')}</option>
                      <option value="flandre">{t(lang,'region_flandre')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t(lang,'peb_lbl')}</label>
                    <select name="peb" value={form.peb} onChange={handleChange}>
                      <option value="">—</option>
                      <option value="A+">A+</option><option value="A">A</option>
                      <option value="B">B</option><option value="C">C</option>
                      <option value="D">D</option><option value="E">E</option>
                      <option value="F">F</option><option value="G">G</option>
                    </select>
                  </div>
                </>
              )}
              {form.pays === 'FR' && (
                <>
                  <div className="form-group">
                    <label>{t(lang,'dpe_lbl')}</label>
                    <select name="dpe" value={form.dpe} onChange={handleChange}>
                      <option value="">—</option>
                      <option value="A">A</option><option value="B">B</option>
                      <option value="C">C</option><option value="D">D</option>
                      <option value="E">E</option><option value="F">F</option>
                      <option value="G">G</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t(lang,'departement_lbl')}</label>
                    <input name="departement" value={form.departement} onChange={handleChange} placeholder={t(lang,'departement_ph')} maxLength={3} />
                  </div>
                </>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={onCancel}>{t(lang,'annuler_btn')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={loading}>{loading ? t(lang,'enreg_en_cours') : t(lang,'enregistrer')}</button>
            </div>
          </>
        )}

        {/* ── ONGLET HISTORIQUE ── */}
        {onglet === 'historique' && (
          <div style={{ paddingTop: 8 }}>
            <HistoriqueLoyers bienId={bien.id} userId={userId} loyerActuel={parseFloat(form.loyer)} lang={lang} />
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={onCancel}>{t(lang,'fermer') || t(lang,'annuler_btn')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function EditLocataireModal({ locataire, biens, onSave, onCancel, lang = 'fr' }) {
  const [form, setForm] = useState({ prenom: locataire.prenom, nom: locataire.nom, email: locataire.email || '', telephone: locataire.telephone || '', bien_id: locataire.bien_id, date_entree: locataire.date_entree })
  const [loading, setLoading] = useState(false)
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })
  const handleSave = async () => {
    setLoading(true)
    await supabase.from('locataires').update({ prenom: form.prenom, nom: form.nom, email: form.email || null, telephone: form.telephone || null, bien_id: form.bien_id, date_entree: form.date_entree }).eq('id', locataire.id)
    setLoading(false); onSave()
  }
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{t(lang,'modifier_locataire')}</div>
        <div className="form-grid">
          <div className="form-group"><label>{t(lang,'prenom')} *</label><input name="prenom" value={form.prenom} onChange={handleChange} /></div>
          <div className="form-group"><label>{t(lang,'nom')} *</label><input name="nom" value={form.nom} onChange={handleChange} /></div>
          <div className="form-group"><label>{t(lang,'email')}</label><input name="email" type="email" value={form.email} onChange={handleChange} /></div>
          <div className="form-group"><label>{t(lang,'telephone')}</label><input name="telephone" value={form.telephone} onChange={handleChange} /></div>
          <div className="form-group full"><label>{t(lang,'bien_loue')}</label><select name="bien_id" value={form.bien_id} onChange={handleChange}>{biens.map(b => <option key={b.id} value={b.id}>{b.adresse.split(',')[0]}</option>)}</select></div>
          <div className="form-group"><label>{t(lang,'date_entree')}</label><input name="date_entree" type="date" value={form.date_entree} onChange={handleChange} /></div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onCancel}>{t(lang,'annuler_btn')}</button>
          <button className="btn btn-blue" onClick={handleSave} disabled={loading}>{loading ? t(lang,'enreg_en_cours') : t(lang,'enregistrer')}</button>
        </div>
      </div>
    </div>
  )
}

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
  html { box-sizing: border-box; overflow-x: hidden; }
  *, *::before, *::after { box-sizing: inherit; margin: 0; padding: 0; }
  body { overflow-x: hidden; width: 100%; }
  #root { overflow-x: hidden; width: 100%; }
  body { font-family: 'DM Sans', sans-serif; background: #f7f7f5; color: #1a1a1a; }

  .auth-page { display: grid; grid-template-columns: 1fr 1fr; min-height: 100vh; overflow: visible; }
  @media (max-width: 768px) {
    .auth-page { grid-template-columns: 1fr; height: auto; min-height: 100vh; }
    .auth-left { display: none; }
    .auth-right { padding: 32px 20px; align-items: flex-start; padding-top: 60px; }
    .auth-form-wrap { gap: 20px; }
  }
  .auth-left { background: #0d2b1f; display: flex; flex-direction: column; justify-content: space-between; padding: 48px; position: relative; overflow: hidden; }
  .auth-left::before { content: ''; position: absolute; width: 600px; height: 600px; border-radius: 50%; background: radial-gradient(circle, rgba(29,158,117,0.25) 0%, transparent 70%); top: -100px; left: -100px; pointer-events: none; }
  .auth-brand-icon { font-size: 36px; margin-bottom: 16px; }
  .auth-brand-name { font-family: 'DM Serif Display', serif; font-size: 42px; color: #fff; line-height: 1.1; margin-bottom: 12px; }
  @media (max-width: 768px) { .mobile-header .auth-brand-name { color: #0d2b1f; font-size: 32px; } }
  .auth-brand-name em { color: #1D9E75; font-style: italic; }
  .auth-brand-tagline { font-size: 15px; color: rgba(255,255,255,0.5); line-height: 1.6; max-width: 280px; }
  .auth-features { position: relative; z-index: 1; display: flex; flex-direction: column; gap: 16px; }
  .auth-feature { display: flex; align-items: center; gap: 14px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 14px 18px; }
  .auth-feature-icon { font-size: 20px; }
  .auth-feature-text { font-size: 13px; color: rgba(255,255,255,0.7); line-height: 1.4; }
  .auth-feature-text strong { color: #fff; display: block; margin-bottom: 2px; font-weight: 500; }
  .auth-footer { font-size: 12px; color: rgba(255,255,255,0.3); }
  .auth-right { background: #fff; display: flex; align-items: center; justify-content: center; padding: 48px; }
  .auth-form-wrap { width: 100%; max-width: 360px; display: flex; flex-direction: column; gap: 28px; }
  .auth-form-title { font-family: 'DM Serif Display', serif; font-size: 30px; color: #0d2b1f; }
  .auth-form-sub { font-size: 14px; color: #999; margin-top: 6px; }
  .auth-fields { display: flex; flex-direction: column; gap: 14px; }
  .auth-field { display: flex; flex-direction: column; gap: 6px; }
  .auth-field label { font-size: 12px; font-weight: 600; color: #555; text-transform: uppercase; letter-spacing: 0.06em; }
  .auth-field input { padding: 13px 16px; border: 1.5px solid #e5e5e5; border-radius: 10px; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1a1a; outline: none; background: #fafafa; transition: border-color 0.2s; }
  .auth-field input:focus { border-color: #1D9E75; background: #fff; box-shadow: 0 0 0 4px rgba(29,158,117,0.08); }
  .auth-submit { padding: 14px; background: #0d2b1f; color: #fff; border: none; border-radius: 10px; font-size: 15px; font-family: 'DM Sans', sans-serif; font-weight: 600; cursor: pointer; transition: background 0.2s; }
  .auth-submit:hover { background: #1D9E75; }
  .auth-submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .auth-msg { font-size: 13px; padding: 12px 16px; border-radius: 8px; }
  .auth-msg.error { background: #faece7; color: #993C1D; }
  .auth-msg.success { background: #e1f5ee; color: #0F6E56; }
  .auth-switch { text-align: center; font-size: 13px; color: #999; padding-top: 8px; border-top: 1px solid #f0f0f0; }
  .auth-switch button { background: none; border: none; color: #1D9E75; font-weight: 600; cursor: pointer; font-size: 13px; font-family: 'DM Sans', sans-serif; }

  .app { display: flex; height: 100vh; overflow: hidden; width: 100%; max-width: 100%; }
  .sidebar { width: 220px; background: #0d2b1f; display: flex; flex-direction: column; padding: 28px 0; flex-shrink: 0; overflow-y: auto; }
  .logo { padding: 0 20px 32px; }
  .logo h1 { font-family: 'DM Serif Display', serif; font-size: 20px; color: #fff; }
  .logo p { font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 3px; }
  .nav-item { display: flex; align-items: center; gap: 10px; padding: 11px 20px; cursor: pointer; font-size: 14px; color: rgba(255,255,255,0.5); border-left: 3px solid transparent; transition: all 0.15s; }
  .nav-item:hover { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.9); }
  .nav-item.active { color: #fff; background: rgba(29,158,117,0.15); border-left-color: #1D9E75; font-weight: 500; }
  .sidebar-footer { margin-top: auto; padding: 16px 20px; border-top: 1px solid rgba(255,255,255,0.08); }
  .user-email { font-size: 12px; color: rgba(255,255,255,0.35); margin-bottom: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .logout-btn { font-size: 12px; color: rgba(255,255,255,0.4); cursor: pointer; background: none; border: none; font-family: 'DM Sans', sans-serif; margin-top: 8px; display: block; }
  .logout-btn:hover { color: #fff; }

  .main { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 36px 36px 48px; display: flex; flex-direction: column; gap: 24px; background: #f7f7f5; min-width: 0; width: 0; }
  .page-title { font-family: 'DM Serif Display', serif; font-size: 26px; color: #0d2b1f; }
  .page-sub { font-size: 14px; color: #999; margin-top: 4px; }
  .page-header-row { display: flex; justify-content: space-between; align-items: center; }

  .stats-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
  .stat-card { background: #fff; border: 1px solid #eee; border-radius: 14px; padding: 22px; min-width: 0; overflow: hidden; }
  .stat-label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 12px; font-weight: 600; word-break: break-word; line-height: 1.3; }
  .stat-value { font-family: 'DM Serif Display', serif; font-size: 32px; color: #0d2b1f; }
  .stat-value.green { color: #1D9E75; }
  .stat-value.red { color: #D85A30; }
  .stat-value.orange { color: #854F0B; }
  .stat-value.purple { color: #6B4FBB; }
  .stat-sub { font-size: 12px; color: #aaa; margin-top: 6px; }

  .card { background: #fff; border: 1px solid #eee; border-radius: 14px; }
  .card-header { padding: 18px 22px; border-bottom: 1px solid #f5f5f5; display: flex; justify-content: space-between; align-items: center; }
  .card-title { font-size: 14px; font-weight: 600; color: #0d2b1f; }
  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

  .row { display: flex; align-items: center; gap: 12px; padding: 14px 22px; border-bottom: 1px solid #f8f8f8; font-size: 14px; }
  .row:last-child { border-bottom: none; }
  .row:hover { background: #fafaf8; }
  .row-main { flex: 1; min-width: 0; }
  .row-name { font-weight: 500; color: #1a1a1a; }
  .row-sub { font-size: 12px; color: #aaa; margin-top: 2px; }
  .row-amount { font-weight: 600; white-space: nowrap; color: #0d2b1f; text-align: right; }
  .row-amount-sub { font-size: 11px; color: #aaa; font-weight: 400; }
  .row-actions { display: flex; gap: 6px; flex-shrink: 0; }

  .badge { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 20px; white-space: nowrap;  max-width: 120px; overflow: hidden; text-overflow: ellipsis; }
  .badge-paid    { background: #e1f5ee; color: #0F6E56; }
  .badge-pending { background: #faeeda; color: #854F0B; }
  .badge-late    { background: #faece7; color: #993C1D; }
  .badge-partial { background: #ede8fb; color: #6B4FBB; }
  .badge-inactive { background: #f0f0f0; color: #999; }

  .icon-wrap { width: 38px; height: 38px; border-radius: 10px; background: #e8f5ef; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 17px; }
  .avatar { width: 38px; height: 38px; border-radius: 50%; background: #e6f1fb; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 13px; font-weight: 600; color: #185FA5; }
  .avatar.inactive { background: #f0f0f0; color: #aaa; }
  .empty { padding: 44px; text-align: center; color: #bbb; font-size: 14px; }

  .btn { font-size: 13px; padding: 7px 14px; border-radius: 8px; border: 1.5px solid #1D9E75; color: #1D9E75; background: none; cursor: pointer; font-weight: 600; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
  .btn:hover { background: #e8f5ef; }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-primary { background: #0d2b1f; color: #fff; border-color: #0d2b1f; }
  .btn-primary:hover { background: #1D9E75; border-color: #1D9E75; }
  .btn-blue { background: #185FA5; color: #fff; border-color: #185FA5; }
  .btn-blue:hover { background: #0d4a82; }
  .btn-purple { background: #6B4FBB; color: #fff; border-color: #6B4FBB; }
  .btn-purple:hover { background: #5a3fa8; }
  .btn-danger { background: none; color: #D85A30; border-color: #D85A30; }
  .btn-danger:hover { background: #faece7; }
  .btn-sm { font-size: 12px; padding: 4px 10px; }
  .btn-ghost { border-color: #e5e5e5; color: #888; }
  .btn-ghost:hover { background: #f5f5f5; color: #333; }

  .loading { display: flex; align-items: center; justify-content: center; height: 100vh; font-size: 15px; color: #999; }

  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; overflow-y: auto; justify-content: center; z-index: 100; padding: 24px; }
  .modal { background: #fff; border-radius: 20px; padding: 32px; width: 100%; max-width: 520px; max-height: 90vh; overflow-y: auto; }
  @media (max-width: 768px) { .modal { padding: 20px 16px; border-radius: 16px; max-height: 85vh; } }
  .modal-title { font-family: 'DM Serif Display', serif; font-size: 22px; color: #0d2b1f; }
  .modal-actions { display: flex; gap: 10px; justify-content: flex-end; }
  .modal-confirm-text { font-size: 14px; color: #555; line-height: 1.5; }

  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .form-group { display: flex; flex-direction: column; gap: 6px; }
  .form-group label { font-size: 11px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.05em; }
  .form-group input, .form-group select { padding: 10px 12px; border: 1.5px solid #e0e0e0; border-radius: 8px; font-size: 14px; font-family: 'DM Sans', sans-serif; background: #fff; color: #1a1a1a; outline: none; }
  .form-group input:focus, .form-group select:focus { border-color: #1D9E75; box-shadow: 0 0 0 3px rgba(29,158,117,0.08); }
  .form-group.full { grid-column: 1 / -1; }
  .form-card { border-radius: 14px; padding: 24px; display: flex; flex-direction: column; gap: 16px; }
  .form-card.green  { background: #f0faf5; border: 1.5px solid #9FE1CB; }
  .form-card.blue   { background: #eef5fc; border: 1.5px solid #B5D4F4; }
  .form-card.orange { background: #fef9f0; border: 1.5px solid #F5D08A; }
  .form-card h3 { font-size: 15px; font-weight: 600; }
  .form-card.green h3  { color: #0F6E56; }
  .form-card.blue h3   { color: #185FA5; }
  .form-card.orange h3 { color: #854F0B; }
  .form-actions { display: flex; gap: 10px; justify-content: flex-end; }
  .success-msg { background: #e1f5ee; color: #0F6E56; padding: 10px 16px; border-radius: 8px; font-size: 13px; font-weight: 500; }
  .error-msg   { background: #faece7; color: #993C1D; padding: 10px 16px; border-radius: 8px; font-size: 13px; }
  .statut-btns { display: flex; gap: 6px; flex-wrap: wrap; }

  /* TABS */
  .tabs { display: flex; gap: 4px; background: #f0f0ee; border-radius: 10px; padding: 4px; width: fit-content; }
  .tab { padding: 7px 18px; border-radius: 7px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; background: none; font-family: 'DM Sans', sans-serif; color: #888; transition: all 0.15s; }
  .tab.active { background: #fff; color: #0d2b1f; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }

  /* CHIP ÉCHÉANCE */
  .echeance-chip { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 20px; background: #eef5fc; color: #185FA5; white-space: nowrap; margin-left: 6px; }

  /* DOSSIER LOCATAIRE */
  .dossier-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 200; display: flex; align-items: flex-start; justify-content: flex-end; }
  .dossier-panel { background: #fff; width: min(640px, 100vw); max-width: 100vw; height: 100vh; overflow-y: auto; display: flex; flex-direction: column; box-shadow: -8px 0 40px rgba(0,0,0,0.15); }
  .dossier-header { background: #0d2b1f; padding: 24px 28px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; }
  .dossier-header-name { font-family: 'DM Serif Display', serif; font-size: 22px; color: #fff; }
  .dossier-header-sub { font-size: 13px; color: rgba(255,255,255,0.5); margin-top: 4px; }
  .dossier-close { background: rgba(255,255,255,0.1); border: none; color: #fff; width: 32px; height: 32px; border-radius: 50%; cursor: pointer; font-size: 18px; display: flex; align-items: center; justify-content: center; }
  .dossier-tabs { display: flex; border-bottom: 1px solid #eee; padding: 0 28px; flex-shrink: 0; }
  .dossier-tab { padding: 14px 16px; font-size: 13px; font-weight: 600; color: #aaa; cursor: pointer; border-bottom: 2px solid transparent; transition: all 0.15s; font-family: 'DM Sans', sans-serif; background: none; border-top: none; border-left: none; border-right: none; }
  .dossier-tab.active { color: #0d2b1f; border-bottom-color: #1D9E75; }
  .dossier-body { padding: 24px 28px; flex: 1; display: flex; flex-direction: column; gap: 20px; }
  .doc-type-badge { font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 20px; white-space: nowrap; }
  .doc-row { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid #f5f5f5; }
  .doc-row:last-child { border-bottom: none; }
  .doc-icon { width: 38px; height: 38px; border-radius: 10px; background: #f0faf5; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
  .doc-main { flex: 1; min-width: 0; }
  .doc-name { font-size: 14px; font-weight: 500; color: #1a1a1a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .doc-meta { font-size: 12px; color: #aaa; margin-top: 2px; }
  .upload-zone { border: 2px dashed #d0e8df; border-radius: 12px; padding: 28px; text-align: center; cursor: pointer; transition: all 0.2s; }
  .upload-zone:hover { border-color: #1D9E75; background: #f0faf5; }
  .upload-zone input { display: none; }

  /* NOTIFICATIONS */
  .notif-page { display: flex; flex-direction: column; gap: 24px; }
  .notif-section { background: #fff; border: 1px solid #eee; border-radius: 14px; overflow: hidden; }
  .notif-section-header { padding: 18px 22px; border-bottom: 1px solid #f5f5f5; display: flex; align-items: center; justify-content: space-between; }
  .notif-section-title { font-size: 15px; font-weight: 600; color: #0d2b1f; display: flex; align-items: center; gap: 10px; }
  .notif-section-body { padding: 22px; display: flex; flex-direction: column; gap: 18px; }
  .notif-section-body.disabled { opacity: 0.4; pointer-events: none; }
  .toggle-wrap { display: flex; align-items: center; gap: 10px; cursor: pointer; }
  .toggle { width: 42px; height: 24px; background: #e0e0e0; border-radius: 12px; position: relative; transition: background 0.2s; flex-shrink: 0; cursor: pointer; border: none; }
  .toggle.on { background: #1D9E75; }
  .toggle::after { content: ''; position: absolute; width: 18px; height: 18px; background: #fff; border-radius: 50%; top: 3px; left: 3px; transition: left 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
  .toggle.on::after { left: 21px; }
  .toggle-label { font-size: 14px; font-weight: 500; color: #333; }
  .notif-row { display: flex; flex-direction: column; gap: 6px; }
  .notif-row label { font-size: 11px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.05em; }
  .notif-row input[type=number] { width: 80px; padding: 8px 12px; border: 1.5px solid #e0e0e0; border-radius: 8px; font-size: 14px; font-family: 'DM Sans', sans-serif; outline: none; }
  .notif-row input[type=number]:focus { border-color: #1D9E75; }
  .notif-row input[type=text] { width: 100%; padding: 10px 12px; border: 1.5px solid #e0e0e0; border-radius: 8px; font-size: 14px; font-family: 'DM Sans', sans-serif; outline: none; }
  .notif-row input[type=text]:focus { border-color: #1D9E75; }
  .notif-row textarea { width: 100%; padding: 10px 12px; border: 1.5px solid #e0e0e0; border-radius: 8px; font-size: 13px; font-family: 'DM Sans', sans-serif; outline: none; resize: vertical; min-height: 100px; line-height: 1.6; }
  .notif-row textarea:focus { border-color: #1D9E75; }
  .vars-hint { display: flex; flex-wrap: wrap; gap: 6px; }
  .var-chip { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 6px; background: #f0f0ee; color: #555; font-family: monospace; cursor: default; }
  .notif-save-bar { display: flex; justify-content: flex-end; align-items: center; gap: 12px; padding: 20px 0 0; }

  /* ── CONFINEMENT GLOBAL MOBILE ── */
  @media (max-width: 768px) {
    * { max-width: 100%; }
    img, video, iframe, table { max-width: 100%; }
    pre { white-space: pre-wrap; word-break: break-all; }
    .row-amount { font-size: 13px; white-space: nowrap; }
    .row-actions { flex-shrink: 0; }
    .row-main { min-width: 0; flex: 1; overflow: hidden; }
    .row-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .card-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
    .page-header-row button { font-size: 12px; padding: 8px 12px; }
  }

  /* ── RESPONSIVE MOBILE ── */
  @media (max-width: 768px) {
    .mobile-header .auth-brand-name { color: #0d2b1f; }
    .app { height: auto; min-height: 100vh; width: 100vw; max-width: 100vw; overflow-x: hidden; }
    .sidebar { display: none; }
    .main { padding: 16px 14px 90px; gap: 14px; overflow-x: hidden; width: 100%; min-width: 0; max-width: 100%; }
    .stats-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
    .stat-card { padding: 14px; min-width: 0; overflow: hidden; }
    .stat-value { font-size: 20px; }
    .stat-label { font-size: 10px; margin-bottom: 8px; }
    .two-col { grid-template-columns: 1fr; gap: 12px; }
    .page-header-row { flex-wrap: wrap; gap: 10px; }
    .page-title { font-size: 22px; }
    .modal-overlay { align-items: flex-end; padding: 0; }
    .modal { border-radius: 20px 20px 0 0; width: 100%; padding: 24px 20px 100px; max-height: 92vh; overflow-y: auto; }
    .dossier-overlay { align-items: flex-end; }
    .dossier-panel { width: 100%; height: 92vh; border-radius: 20px 20px 0 0; }
    .row { flex-wrap: wrap; gap: 8px; padding: 12px 14px; }
    .row-actions { width: 100%; justify-content: flex-end; flex-wrap: wrap; }
    .statut-btns { flex-wrap: wrap; gap: 4px; }
    .form-grid { grid-template-columns: 1fr; }
    .form-group.full { grid-column: 1; }
    .pricing-grid { grid-template-columns: 1fr; }
    .tabs { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .tab { font-size: 12px; padding: 7px 10px; white-space: nowrap; flex-shrink: 0; }
    .card-header { flex-wrap: wrap; gap: 8px; }
    .card { overflow: hidden; }
    .ech-card { flex-wrap: wrap; }
    .dossier-tabs { overflow-x: auto; flex-wrap: nowrap; -webkit-overflow-scrolling: touch; }
    .dossier-tab { white-space: nowrap; flex-shrink: 0; }
    .mobile-nav {
      display: flex; position: fixed; bottom: 0; left: 0; right: 0; z-index: 200;
      background: #0d2b1f; border-top: 1px solid rgba(255,255,255,0.08);
      padding: 8px 4px calc(8px + env(safe-area-inset-bottom));
    }
    .mobile-nav-item {
      flex: 1; display: flex; flex-direction: column; align-items: center;
      gap: 2px; cursor: pointer; padding: 4px 2px;
      color: rgba(255,255,255,0.4); font-size: 9px; font-weight: 600;
      border: none; background: none; font-family: 'DM Sans', sans-serif;
      transition: color 0.15s; min-width: 0; overflow: hidden; position: relative;
      -webkit-tap-highlight-color: transparent;
    }
    .mobile-nav-item.active { color: #1D9E75; }
    .mobile-nav-item .nav-icon { font-size: 20px; line-height: 1; }
    .mobile-nav-badge {
      position: absolute; top: 0; right: calc(50% - 18px);
      width: 8px; height: 8px; border-radius: 50%;
      background: #D85A30; border: 1.5px solid #0d2b1f;
    }
    .mobile-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px 0 8px;
    }
    .mobile-header-logo { font-family: 'DM Serif Display', serif; font-size: 20px; color: #0d2b1f; }
    .mobile-header-logo em { color: #1D9E75; font-style: italic; }
  }
  @media (min-width: 769px) {
    .mobile-nav { display: none; }
    .mobile-header { display: none; }
  }

  /* ÉCHEANCES */
  .ech-card { background: #fff; border: 1.5px solid #eee; border-radius: 14px; padding: 20px 22px; display: flex; align-items: center; gap: 16px; transition: box-shadow 0.2s; }
  .ech-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
  .ech-card.urgent { border-color: #f5c842; background: linear-gradient(135deg, #fff8e1, #fffdf0); }
  .ech-card.critique { border-color: #e57373; background: linear-gradient(135deg, #faece7, #fff5f5); }
  .ech-card.traitee { opacity: 0.5; }
  .ech-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
  .ech-icon.renouvellement { background: #eef5fc; }
  .ech-icon.indexation { background: #e1f5ee; }
  .ech-info { flex: 1; min-width: 0; }
  .ech-title { font-size: 14px; font-weight: 600; color: #0d2b1f; margin-bottom: 4px; }
  .ech-meta { font-size: 12px; color: #aaa; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .ech-days { font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 20px; white-space: nowrap; }
  .ech-days.ok       { background: #e1f5ee; color: #0F6E56; }
  .ech-days.soon     { background: #faeeda; color: #854F0B; }
  .ech-days.urgent   { background: #fff8e1; color: #7a5c00; }
  .ech-days.critique { background: #faece7; color: #993C1D; }
  .ech-days.past     { background: #f0f0f0; color: #999; }

  /* ALERTES BADGE */
  .alerte-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; border-radius: 9px; background: #D85A30; color: #fff; font-size: 10px; font-weight: 700; padding: 0 5px; margin-left: auto; }

  /* PRICING */
  .pricing-page { display: flex; flex-direction: column; gap: 40px; }
  .pricing-hero { text-align: center; padding: 20px 0 0; }
  .pricing-hero-title { font-family: 'DM Serif Display', serif; font-size: clamp(24px, 5vw, 36px); color: #0d2b1f; margin-bottom: 12px; }
  .pricing-hero-sub { font-size: 16px; color: #888; max-width: 480px; margin: 0 auto; line-height: 1.6; }
  .pricing-badge { display: inline-block; background: #e1f5ee; color: #0F6E56; font-size: 12px; font-weight: 700; padding: 4px 12px; border-radius: 20px; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 16px; }
  .pricing-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
    @media (max-width: 900px) { .pricing-grid { grid-template-columns: 1fr; } }
  .pricing-card { background: #fff; border: 1.5px solid #eee; border-radius: 20px; padding: 32px; display: flex; flex-direction: column; gap: 24px; position: relative; transition: transform 0.2s, box-shadow 0.2s; }
  .pricing-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(0,0,0,0.08); }
  .pricing-card.popular { border-color: #1D9E75; box-shadow: 0 8px 32px rgba(29,158,117,0.15); }
  .pricing-card.current { border-color: #185FA5; box-shadow: 0 8px 32px rgba(24,95,165,0.12); }
  .popular-tag { position: absolute; top: -13px; left: 50%; transform: translateX(-50%); background: #1D9E75; color: #fff; font-size: 11px; font-weight: 700; padding: 4px 16px; border-radius: 20px; white-space: nowrap; letter-spacing: 0.05em; text-transform: uppercase; }
  .current-tag { position: absolute; top: -13px; left: 50%; transform: translateX(-50%); background: #185FA5; color: #fff; font-size: 11px; font-weight: 700; padding: 4px 16px; border-radius: 20px; white-space: nowrap; letter-spacing: 0.05em; }
  .pricing-plan { font-size: 13px; font-weight: 700; color: #999; text-transform: uppercase; letter-spacing: 0.08em; }
  .pricing-price { display: flex; align-items: baseline; gap: 4px; }
  .pricing-amount { font-family: 'DM Serif Display', serif; font-size: 48px; color: #0d2b1f; }
  .pricing-currency { font-size: 20px; color: #0d2b1f; font-weight: 600; margin-right: 2px; }
  .pricing-period { font-size: 14px; color: #aaa; }
  .pricing-desc { font-size: 14px; color: #888; line-height: 1.5; }
  .pricing-features { display: flex; flex-direction: column; gap: 10px; }
  .pricing-feature { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #444; }
  .pricing-feature-icon { width: 20px; height: 20px; border-radius: 50%; background: #e1f5ee; display: flex; align-items: center; justify-content: center; font-size: 11px; flex-shrink: 0; }
  .pricing-feature-icon.no { background: #f5f5f5; }
  .pricing-cta { padding: 14px; border-radius: 10px; font-size: 15px; font-family: 'DM Sans', sans-serif; font-weight: 600; cursor: pointer; border: none; transition: all 0.2s; text-align: center; display: block; }
  .pricing-cta.free { background: #f5f5f5; color: #555; }
  .pricing-cta.starter { background: #0d2b1f; color: #fff; }
  .pricing-cta.starter:hover { background: #1D9E75; }
  .pricing-cta.pro { background: #1D9E75; color: #fff; }
  .pricing-cta.pro:hover { background: #0F6E56; }
  .pricing-cta.current-plan { background: #e8f0fb; color: #185FA5; cursor: default; }
  .pricing-faq { background: #fff; border: 1px solid #eee; border-radius: 16px; padding: 32px; display: flex; flex-direction: column; gap: 20px; }
  .pricing-faq-title { font-family: 'DM Serif Display', serif; font-size: 22px; color: #0d2b1f; }
  .faq-item { border-bottom: 1px solid #f5f5f5; padding-bottom: 16px; }
  .faq-item:last-child { border-bottom: none; padding-bottom: 0; }
  .faq-q { font-size: 14px; font-weight: 600; color: #1a1a1a; margin-bottom: 6px; }
  .faq-a { font-size: 14px; color: #888; line-height: 1.6; }
  .pricing-guarantee { text-align: center; font-size: 13px; color: #aaa; padding: 8px 0; }
  .pricing-guarantee strong { color: #555; }
`

const PAGES = ['dashboard', 'todo', 'calendrier', 'biens', 'locataires', 'locations', 'paiements', 'messagerie', 'echeances', 'indexation', 'baux', 'notifications', 'interventions', 'parametres', 'corbeille', 'pricing']
const PAGE_ICONS = ['📊', '✅', '📅', '🏠', '👤', '📋', '💶', '💬', '📅', '📈', '📄', '🔔', '🔧', '⚙️', '🗑️', '⭐']
const PAGE_LABEL_KEYS = ['dashboard', 'todo', 'cal_menu', 'biens', 'locataires', 'locations', 'paiements', 'messagerie', 'echeances', 'idx_menu', 'baux', 'notifications', 'interv_titre', 'parametres', 'corbeille', 'pricing']

// ─── EXPORT EXCEL ─────────────────────────────────────────────
async function exportExcel({ biens, locataires, paiements }) {
  const XLSX = await import('xlsx')

  // ── Onglet Biens ──────────────────────────────────────────
  const biensData = biens.map(b => ({
    'Adresse':        b.adresse,
    'Type':           b.type_bien || '',
    'Loyer (€)':      Number(b.loyer),
    'Charges (€)':    Number(b.charges || 0),
    'Valeur (€)':     b.valeur ? Number(b.valeur) : '',
    'Rendement (%)':  b.valeur ? ((b.loyer * 12 / b.valeur) * 100).toFixed(2) : '',
    'Échéance (jour)': b.jour_echeance || 1,
  }))

  // ── Onglet Locataires ─────────────────────────────────────
  const locData = locataires.map(l => ({
    'Prénom':          l.prenom,
    'Nom':             l.nom,
    'Email':           l.email || '',
    'Téléphone':       l.telephone || '',
    'Date d\'entrée':  l.date_entree || '',
    'Date de sortie':  l.date_sortie || '',
    'Statut':          l.date_sortie ? 'Inactif' : 'Actif',
    'Profession':      l.profession || '',
    'Employeur':       l.employeur || '',
    'Contact urgence': l.contact_urgence_nom ? `${l.contact_urgence_nom} · ${l.contact_urgence_tel || ''}` : '',
    'Notes':           l.notes || '',
  }))

  // ── Onglet Paiements ──────────────────────────────────────
  const paieData = paiements.map(p => ({
    'Locataire':      `${p.locataires?.prenom || ''} ${p.locataires?.nom || ''}`.trim(),
    'Bien':           p.biens?.adresse || '',
    'Mois':           fmtDate(p.mois, { month: 'long', year: 'numeric' }),
    'Loyer attendu (€)': Number(p.montant),
    'Montant reçu (€)':  p.statut === 'paid' ? Number(p.montant) : p.statut === 'partial' ? Number(p.montant_paye || 0) : 0,
    'Solde dû (€)':      p.statut === 'partial' ? Number(p.montant) - Number(p.montant_paye || 0) : p.statut === 'paid' ? 0 : Number(p.montant),
    'Statut':            p.statut === 'paid' ? 'Payé' : p.statut === 'partial' ? 'Partiel' : p.statut === 'late' ? 'En retard' : 'En attente',
    'Date paiement':     p.date_paiement || '',
  }))

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(biensData),    'Biens')
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(locData),      'Locataires')
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(paieData),     'Paiements')

  const now      = new Date()
  const filename = `domavix-export-${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}.xlsx`
  XLSX.writeFile(wb, filename)
}

// ─── BOUTON EXPORT ────────────────────────────────────────────
function ExportButton({ biens, locataires, paiements, plan, small = false, lang = 'fr' }) {
  const [exporting, setExporting] = useState(false)

  if (plan !== 'pro' && plan !== 'starter' && plan !== 'enterprise') return null

  const handleExport = async () => {
    setExporting(true)
    await exportExcel({ biens, locataires, paiements })
    setExporting(false)
  }

  return (
    <button
      className={`btn ${small ? 'btn-sm' : ''} btn-ghost`}
      style={{ color: '#1D9E75', borderColor: '#9FE1CB' }}
      onClick={handleExport}
      disabled={exporting}
    >
      {exporting ? '⏳ ...' : `📊 ${t(lang,'Excel')}`}
    </button>
  )
}

// ─── GÉNÉRATION PDF QUITTANCE ─────────────────────────────────
async function generateQuittancePDF({ paiement, locataire, bien, proprio, lang = 'fr' }) {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const fmtMoney = (n) =>
  Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .replace(/[\u202f\u00a0]/g, ' ')

  const moisLabel  = fmtDate(paiement.mois, { month: 'long', year: 'numeric' })
  const datePayStr = paiement.date_paiement
    ? fmtDate(paiement.date_paiement)
    : fmtDate()

    const qLabels = {
  fr: {
    titre:       'QUITTANCE DE LOYER',
    bailleur:    'BAILLEUR',
    locataire:   'LOCATAIRE',
    bien:        'BIEN LOUÉ',
    designation: 'DÉSIGNATION',
    montantLbl:  'MONTANT',
    loyerNu:     `Loyer nu — ${moisLabel}`,
    charges:     'Charges locatives',
    total:       'TOTAL ENCAISSÉ',
    recu:        `Paiement reçu le : ${datePayStr}`,
    fait:        `Fait le ${datePayStr}`,
    signature:   'Signature du bailleur : ___________________',
    footer:      'Document généré par Domavix · domavix.com',
    declaration: (proprioNom, locPrenom, locNom, totalStr, moisLbl) =>
      `Je soussigné(e) ${proprioNom}, bailleur du logement désigné ci-dessus, déclare avoir reçu de ${locPrenom} ${locNom} la somme de ${totalStr} € au titre du loyer et des charges du mois de ${moisLbl}, et lui en donne quittance, sous réserve de tous mes droits.`,
  },
  en: {
    titre:       'RENT RECEIPT',
    bailleur:    'LANDLORD',
    locataire:   'TENANT',
    bien:        'PROPERTY',
    designation: 'DESCRIPTION',
    montantLbl:  'AMOUNT',
    loyerNu:     `Rent — ${moisLabel}`,
    charges:     'Service charges',
    total:       'TOTAL RECEIVED',
    recu:        `Payment received on: ${datePayStr}`,
    fait:        `Signed on ${datePayStr}`,
    signature:   'Landlord signature: ___________________',
    footer:      'Generated by Domavix · domavix.com',
    declaration: (proprioNom, locPrenom, locNom, totalStr, moisLbl) =>
      `I, the undersigned ${proprioNom}, landlord of the above-mentioned property, hereby acknowledge receipt from ${locPrenom} ${locNom} of the sum of ${totalStr} € for rent and charges for the month of ${moisLbl}, and issue this receipt without prejudice to any of my rights.`,
  },
  nl: {
    titre:       'HUURKWITANTIE',
    bailleur:    'VERHUURDER',
    locataire:   'HUURDER',
    bien:        'EIGENDOM',
    designation: 'OMSCHRIJVING',
    montantLbl:  'BEDRAG',
    loyerNu:     `Huur — ${moisLabel}`,
    charges:     'Servicekosten',
    total:       'TOTAAL ONTVANGEN',
    recu:        `Betaling ontvangen op: ${datePayStr}`,
    fait:        `Opgemaakt op ${datePayStr}`,
    signature:   'Handtekening verhuurder: ___________________',
    footer:      'Gegenereerd door Domavix · domavix.com',
    declaration: (proprioNom, locPrenom, locNom, totalStr, moisLbl) =>
      `Ik, ondergetekende ${proprioNom}, verhuurder van het hierboven vermelde pand, verklaar ontvangen te hebben van ${locPrenom} ${locNom} het bedrag van ${totalStr} € als huur en kosten voor de maand ${moisLbl}, en geef hierbij kwijting onder voorbehoud van alle rechten.`,
  },
  }
  const ql = qLabels[lang] || qLabels.fr

  // ── En-tête fond vert ────────────────────────────────────────
  doc.setFillColor(13, 43, 31)
  doc.rect(0, 0, 210, 38, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.text('Domavix', 14, 16)
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(180, 220, 200)
  doc.text('Gestion locative · domavix.com', 14, 23)
  doc.setFontSize(14)
  doc.setTextColor(29, 158, 117)
  doc.setFont('helvetica', 'bold')
  doc.text(ql.titre, 14, 33)
  doc.setTextColor(200, 200, 200)
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.text(`${moisLabel.toUpperCase()}`, 170, 33, { align: 'right' })
  // ── Blocs Bailleur / Locataire ───────────────────────────────
  const total    = Number(paiement.montant)
  const charges  = paiement.charges != null ? Number(paiement.charges) : Number(bien.charges || 0)
  const loyerNu  = total - charges
  const totalStr = fmtMoney(total)

  doc.setTextColor(13, 43, 31)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.text(ql.bailleur, 14, 50)
  doc.text(ql.locataire, 110, 50)
  doc.setDrawColor(29, 158, 117)
  doc.setLineWidth(0.5)
  doc.line(14, 52, 60, 52)
  doc.line(110, 52, 156, 52)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(60, 60, 60)
  let yB = 58
  doc.text(proprio.nom || '—', 14, yB)
  if (proprio.adresse)   { yB += 5; doc.text(proprio.adresse, 14, yB) }
  if (proprio.ville)     { yB += 5; doc.text(proprio.ville, 14, yB) }
  if (proprio.telephone) { yB += 5; doc.text(proprio.telephone, 14, yB) }

  let yL = 58
  doc.text(`${locataire.prenom || ''} ${locataire.nom || ''}`.trim(), 110, yL)
  if (locataire.email) { yL += 5; doc.text(locataire.email, 110, yL) }

  // ── Bien loué ────────────────────────────────────────────────
  const yBien = Math.max(yB, yL) + 12
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(13, 43, 31)
  doc.text(ql.bien, 14, yBien)
  doc.line(14, yBien + 2, 60, yBien + 2)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(60, 60, 60)
  doc.text(bien.adresse || '—', 14, yBien + 8)

  // ── Tableau montants ─────────────────────────────────────────
  let yT = yBien + 20
  doc.setFillColor(13, 43, 31)
  doc.rect(14, yT, 182, 9, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.text(ql.designation, 18, yT + 6)
  doc.text(ql.montantLbl, 192, yT + 6, { align: 'right' })

  yT += 9
  doc.setTextColor(60, 60, 60)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setFillColor(247, 247, 245)
  doc.rect(14, yT, 182, 9, 'F')
  doc.text(ql.loyerNu, 18, yT + 6)
  doc.text(`${fmtMoney(loyerNu)} €`, 192, yT + 6, { align: 'right' })

  yT += 9
  doc.rect(14, yT, 182, 9)
  doc.text(ql.charges, 18, yT + 6)
  doc.text(`${fmtMoney(charges)} €`, 192, yT + 6, { align: 'right' })

  yT += 9
  doc.setFillColor(29, 158, 117)
  doc.rect(14, yT, 182, 10, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text(ql.total, 18, yT + 7)
  doc.text(`${totalStr} €`, 192, yT + 7, { align: 'right' })

  // ── Déclaration ──────────────────────────────────────────────
  yT += 20
  doc.setTextColor(60, 60, 60)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  const declText = ql.declaration(proprio.nom || '—', locataire.prenom || '', locataire.nom || '', totalStr, moisLabel)
  const declLines = doc.splitTextToSize(declText, 182)
  doc.text(declLines, 14, yT)
  yT += declLines.length * 5 + 8

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.text(ql.recu, 14, yT)

  // ── Date + signature ─────────────────────────────────────────
  yT += 14
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text(ql.fait, 14, yT)
  yT += 10
  doc.text(ql.signature, 14, yT)

  // ── Footer ───────────────────────────────────────────────────
  doc.setFontSize(8)
  doc.setTextColor(160, 160, 160)
  doc.text(ql.footer, 105, 287, { align: 'center' })

  return doc
}


const DOC_TYPES_KEYS = ['Bail', 'Identite', 'Revenus', 'Assurance', 'Autre']

function getDocTypes(lang) {
  const types = {
    fr: ['Bail', 'Identité', 'Revenus', 'Assurance', 'Autre'],
    en: ['Lease', 'Identity', 'Income', 'Insurance', 'Other'],
    nl: ['Huurcontract', 'Identiteit', 'Inkomen', 'Verzekering', 'Andere'],
  }
  return types[lang] || types.fr
}

function DocBadge({ type }) {
  const palette = {
    'Bail':         { bg: '#eef5fc', color: '#185FA5' },
    'Lease':        { bg: '#eef5fc', color: '#185FA5' },
    'Huurcontract': { bg: '#eef5fc', color: '#185FA5' },
    'Identité':     { bg: '#f0faf5', color: '#0F6E56' },
    'Identity':     { bg: '#f0faf5', color: '#0F6E56' },
    'Identiteit':   { bg: '#f0faf5', color: '#0F6E56' },
    'Revenus':      { bg: '#fef9f0', color: '#854F0B' },
    'Income':       { bg: '#fef9f0', color: '#854F0B' },
    'Inkomen':      { bg: '#fef9f0', color: '#854F0B' },
  }
  const s = palette[type] || { bg: '#f0f0f0', color: '#666' }
  return (
    <span className="doc-type-badge" style={{ background: s.bg, color: s.color }}>
      {type}
    </span>
  )
}

function BailGenerator({ locataire, bien, proprio, userId, lang = 'fr' }) {
  const [formBail, setFormBail] = useState({
    date_fin:      '',
    garantie:      '',
    garantie_mois: 2,
    clauses:       '',
    loyer:         bien?.loyer || '',
    charges:       bien?.charges || 0,
  })
  const [typeBail, setTypeBail]   = useState(null)
  const [generated] = useState(false)
  const garantieInitRef           = useRef(false)  

  // Charger le type de bail du locataire
  useEffect(() => {
    if (!locataire.type_bail_id) return
    supabase.from('types_bail').select('*').eq('id', locataire.type_bail_id).single()
      .then(({ data }) => { if (data) setTypeBail(data) })
  }, [locataire.type_bail_id])

  // Pré-remplir la garantie une seule fois
  useEffect(() => {
    if (garantieInitRef.current) return
    const loyer = Number(bien?.loyer || 0)
    if (loyer > 0) {
      garantieInitRef.current = true
      setFormBail(f => ({ ...f, garantie: loyer * 2, loyer }))
    }
  }, [bien])

   return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ background: '#f0faf5', border: '1px solid #9FE1CB', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#0F6E56' }}>
        ℹ️ {t(lang, 'bail_prefill_info')}
      </div>
      {!proprio?.proprio_nom && <div className="error-msg">{t(lang, 'prop_incomplet')}</div>}
      <div className="form-card green">
        <h3>👤 {t(lang, 'bailleur_lbl')}</h3>
        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.7 }}>
          <div><strong>{proprio?.proprio_nom || '—'}</strong></div>
          {proprio?.proprio_adresse   && <div>{proprio.proprio_adresse}</div>}
          {proprio?.proprio_ville     && <div>{proprio.proprio_ville}</div>}
          {proprio?.proprio_telephone && <div>{proprio.proprio_telephone}</div>}
        </div>
      </div>
      <div className="form-card blue">
        <h3>🏠 {t(lang, 'bail_apercu')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13, color: '#555' }}>
          <div><strong>{t(lang, 'locataire_lbl')}</strong><br />{locataire.prenom} {locataire.nom}</div>
          <div><strong>{t(lang, 'bien_lbl')}</strong><br />{bien?.adresse || '—'}</div>
          <div><strong>{t(lang, 'loyer')}</strong><br />{fmt(bien?.loyer || 0)}</div>
          <div><strong>{t(lang, 'charges')}</strong><br />{fmt(bien?.charges || 0)}</div>
          <div><strong>{t(lang, 'date_entree')}</strong><br />{fmtDate(locataire.date_entree)}</div>
          {typeBail && <div><strong>{t(lang, 'type_bail')}</strong><br />{typeBail.nom}</div>}
        </div>
      </div>
      <div className="form-card orange">
        <h3>📋 {t(lang, 'clauses_part')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t(lang, 'date_fin_bail')}</label>
            <input type="date" value={formBail.date_fin} onChange={e => setFormBail(f => ({ ...f, date_fin: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t(lang, 'garantie_lbl')}</label>
            <input type="number" value={formBail.garantie} onChange={e => setFormBail(f => ({ ...f, garantie: e.target.value }))} placeholder={`${(bien?.loyer || 0) * 2}`} />
          </div>
          <div className="form-group">
            <label>{t(lang, 'garantie_mois_lbl')}</label>
            <select value={formBail.garantie_mois} onChange={e => setFormBail(f => ({ ...f, garantie_mois: Number(e.target.value) }))}>
              <option value={1}>1 mois</option>
              <option value={2}>2 mois</option>
              <option value={3}>3 mois</option>
            </select>
          </div>
          <div className="form-group full">
            <label>{t(lang, 'clauses_part')}</label>
            <textarea style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 13, outline: 'none', resize: 'vertical', minHeight: 100, lineHeight: 1.6 }}
              placeholder={t(lang, 'ph_clauses')} value={formBail.clauses} onChange={e => setFormBail(f => ({ ...f, clauses: e.target.value }))} />
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#aaa', background: '#f7f7f5', borderRadius: 8, padding: '10px 14px', lineHeight: 1.6 }}>
        ⚠️ {t(lang, 'bail_disclaimer')}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
        {generated && <span style={{ fontSize: 13, color: '#1D9E75', fontWeight: 600 }}>✅ PDF généré !</span>}
        <div className="info-box" style={{ marginTop: 16 }}>
          📋 {t(lang, 'bail_coming_soon')}
        </div>
      </div>
    </div>
  )
}

function DossierPanel({ locataire, biens, userId, plan, planCustomBiens = null, proprio, onClose, onRefresh, lang = 'fr' }) {
  const [emailExpanded, setEmailExpanded] = useState(null)
  const [onglet, setOnglet]     = useState('infos')
  const [infos, setInfos]       = useState({
    date_naissance: locataire.date_naissance || '',
    numero_national: locataire.numero_national || '',
    profession: locataire.profession || '',
    employeur: locataire.employeur || '',
    contact_urgence_nom: locataire.contact_urgence_nom || '',
    contact_urgence_tel: locataire.contact_urgence_tel || '',
    notes: locataire.notes || '',
    type_bail_id:    locataire.type_bail_id || '',
    date_signature:  locataire.date_signature || '',
    indice_reference: locataire.indice_reference || '',
    indice_nom:      locataire.indice_nom || '',
    lang:            locataire.lang || 'fr',
  })
  const [savingInfos, setSavingInfos] = useState(false)
  const [savedInfos, setSavedInfos]   = useState(false)
  const [docs, setDocs]               = useState([])
  const [loadingDocs, setLoadingDocs] = useState(true)
  const [uploading, setUploading]     = useState(false)
  const [uploadForm, setUploadForm]   = useState({ type_doc: 'Bail', notes: '' })
  const [messages, setMessages]       = useState([])
  const [newMsg, setNewMsg]           = useState('')
  const [sendingMsg, setSendingMsg]   = useState(false)
  const [emails, setEmails]           = useState([])
  const [loadingEmails, setLoadingEmails] = useState(false)
  const [filtreDossierEmail, setFiltreDossierEmail] = useState('tous')
  const fileInputRef = useRef(null)
  const messagesEndRef = useRef(null)

  const [typesBail, setTypesBail] = useState([])
  const bien = biens.find(b => b.id === locataire.bien_id)

  useEffect(() => {
    supabase.from('types_bail').select('id, nom, duree_mois, indexation_indice').eq('user_id', userId)
      .then(({ data }) => setTypesBail(data || []))
  }, [userId])

  const docLimites    = { free: 0, starter: 5, pro: 20, enterprise: Infinity }
  const docLimite     = planCustomBiens ? Infinity : (docLimites[plan] ?? 0)
  const MAX_FILE_SIZE = { free: 0, starter: 5, pro: 10, enterprise: 20 }[plan] || 5 // MB

  const loadDocs = useCallback(async () => {
    setLoadingDocs(true)
    const { data } = await supabase
      .from('documents')
      .select('*')
      .eq('locataire_id', locataire.id)
      .order('created_at', { ascending: false })
    setDocs(data || [])
    setLoadingDocs(false)
  }, [locataire.id])

  useEffect(() => { loadDocs() }, [loadDocs])

  const loadEmails = useCallback(async () => {
    setLoadingEmails(true)
    const { data } = await supabase
      .from('notifications_log')
      .select('*')
      .eq('locataire_id', locataire.id)
      .order('sent_at', { ascending: false })
      .limit(50)
    setEmails(data || [])
    setLoadingEmails(false)
  }, [locataire.id])

  const handleInvite = async () => {
    if (!locataire.email) { alert(t(lang, 'inv_no_email')); return }
    const token = await getToken()
    const res = await fetch('/api/router', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, },
      body: JSON.stringify({
      action:      'invite-locataire',
      email:       locataire.email,
      prenom:      locataire.prenom,
      locataireId: locataire.id,  
      userId:      userId,         
    }),
    })
    if (res.ok) {
      await supabase.from('locataires').update({ invite_sent_at: new Date().toISOString() }).eq('id', locataire.id)
      alert(t(lang, 'invitation_ok', { email: locataire.email }))
      onRefresh()
    } else {
      alert(t(lang, 'inv_error'))
    }
  }

  const loadMessages = useCallback(async () => {
    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('locataire_id', locataire.id)
      .order('created_at', { ascending: true })
    setMessages(data || [])
    await supabase.from('messages').update({ lu: true }).eq('locataire_id', locataire.id).eq('expediteur', 'locataire')
  }, [locataire.id])

  const handleSendMessage = async () => {
    if (!newMsg.trim()) return
    setSendingMsg(true)
    await supabase.from('messages').insert({
      locataire_id:    locataire.id,
      proprio_user_id: userId,
      expediteur:      'proprietaire',
      contenu:         newMsg.trim(),
      lu:              false,
    })
    setNewMsg('')
    setSendingMsg(false)
    await loadMessages()
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
  }

  const handleSaveInfos = async () => {
    setSavingInfos(true)
    const { error } = await supabase.from('locataires').update({
      date_naissance:      infos.date_naissance || null,
      numero_national:     infos.numero_national || null,
      profession:          infos.profession || null,
      employeur:           infos.employeur || null,
      contact_urgence_nom: infos.contact_urgence_nom || null,
      contact_urgence_tel: infos.contact_urgence_tel || null,
      notes:               infos.notes || null,
      type_bail_id:        infos.type_bail_id || null,
      date_signature:      infos.date_signature || null,
      indice_reference:    infos.indice_reference ? parseFloat(infos.indice_reference) : null,
      indice_nom:          infos.indice_nom || null,
      lang:                infos.lang || 'fr',
    }).eq('id', locataire.id)
    setSavingInfos(false)
    if (!error) { setSavedInfos(true); setTimeout(() => setSavedInfos(false), 2500); onRefresh() }
    else alert('Erreur : ' + error.message)
  }

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (docLimite !== Infinity && docs.length >= docLimite) {
    alert(t(lang,'doc_limite_atteinte').replace('{n}', docLimite)); return
    }
    const maxBytes = MAX_FILE_SIZE * 1024 * 1024
    if (file.size > maxBytes) {
      alert(t(lang,'doc_taille_max').replace('{n}', MAX_FILE_SIZE)); return
    }
    setUploading(true)
    const ext    = file.name.split('.').pop()
    const path   = `${userId}/${locataire.id}/${Date.now()}.${ext}`
    const taille = file.size < 1024 * 1024
      ? `${(file.size / 1024).toFixed(0)} Ko`
      : `${(file.size / 1024 / 1024).toFixed(1)} Mo`
    const { error: upErr } = await supabase.storage.from('documents').upload(path, file)
    if (upErr) { alert(t(lang,'erreur_upload') + upErr.message); setUploading(false); return }
    const { data: urlData } = supabase.storage.from('documents').getPublicUrl(path)
    await supabase.from('documents').insert({
      locataire_id: locataire.id,
      user_id:      userId,
      nom:          file.name,
      type_doc:     uploadForm.type_doc,
      url:          urlData?.publicUrl || path,
      taille,
      notes:        uploadForm.notes || null,
    })
    setUploading(false)
    setUploadForm({ type_doc: 'Bail', notes: '' })

    if (fileInputRef.current) fileInputRef.current.value = ''

    loadDocs()
  }

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(t(lang,'supprimer_doc_confirm'))) return
    const urlParts = doc.url.split('/documents/')
    if (urlParts[1]) await supabase.storage.from('documents').remove([urlParts[1]])
    await supabase.from('documents').delete().eq('id', doc.id)
    loadDocs()
  }

  return (
    <div className="dossier-overlay" onClick={onClose}>
      <div className="dossier-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="dossier-header">
          <div>
            <div className="dossier-header-name">{locataire.prenom} {locataire.nom}</div>
            <div className="dossier-header-sub">{bien?.adresse || t(lang,'aucun_bien_loc')} · {t(lang,'entre_le')} {locataire.date_entree ? fmtDate(locataire.date_entree) : '—'}</div>
          </div>
          <button className="dossier-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div className="dossier-tabs">
          <button className={`dossier-tab ${onglet === 'infos' ? 'active' : ''}`} onClick={() => setOnglet('infos')}>👤 {t(lang,'informations')}</button>
          <button className={`dossier-tab ${onglet === 'docs' ? 'active' : ''}`} onClick={() => setOnglet('docs')}>
            📁 {t(lang,'documents')} {docs.length > 0 && `(${docs.length})`}
          </button>
          {(plan === 'pro' || plan === 'enterprise') && (
            <button className={`dossier-tab ${onglet === 'messages' ? 'active' : ''}`} onClick={() => { setOnglet('messages'); loadMessages() }}>
              💬 {t(lang,'messagerie')}
            </button>
          )}
          {(plan === 'pro' || plan === 'enterprise') && (
            <button className={`dossier-tab ${onglet === 'emails' ? 'active' : ''}`} onClick={() => { setOnglet('emails'); loadEmails() }}>
              📬 {t(lang,'emails_tab')} {emails.length > 0 && `(${emails.length})`}
            </button>
          )}

           {(plan === 'pro' || plan === 'enterprise') && (
            <button
              className={`dossier-tab ${onglet === 'bail' ? 'active' : ''}`}
              onClick={() => setOnglet('bail')}
            >
              📄 {t(lang,'bail')}
            </button>
          )}
          {(plan === 'pro' || plan === 'enterprise') && (
            <button
              className="dossier-tab"
              style={{ marginLeft: 'auto', color: locataire.invite_sent_at ? '#1D9E75' : '#185FA5' }}
              onClick={handleInvite}
              title={locataire.invite_sent_at ? `${t(lang,'invit_le')} ${new Date(locataire.invite_sent_at).toLocaleDateString()}` : t(lang,'invite')}
            >
              {locataire.invite_sent_at ? t(lang,'invite_ok') : `📨 ${t(lang,'invite')}`}
            </button>
          )}
        </div>

        {/* Body */}
        <div className="dossier-body">

          {/* ── ONGLET INFOS ── */}
          {onglet === 'infos' && (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0d2b1f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t(lang,'infos_base')}</div>
              <div className="form-grid">
                <div className="form-group"><label>{t(lang,'prenom')}</label><input value={locataire.prenom} disabled style={{ background: '#f5f5f5', color: '#aaa' }} /></div>
                <div className="form-group"><label>{t(lang,'nom')}</label><input value={locataire.nom} disabled style={{ background: '#f5f5f5', color: '#aaa' }} /></div>
                <div className="form-group"><label>{t(lang,'email')}</label><input value={locataire.email || ''} disabled style={{ background: '#f5f5f5', color: '#aaa' }} /></div>
                <div className="form-group"><label>{t(lang,'telephone')}</label><input value={locataire.telephone || ''} disabled style={{ background: '#f5f5f5', color: '#aaa' }} /></div>
                <div className="form-group">
                  <label>{t(lang,'lang_comm')}</label>
                  <select value={infos.lang} onChange={e => setInfos({ ...infos, lang: e.target.value })}>
                    <option value="fr">🇫🇷 Français</option>
                    <option value="en">🇬🇧 English</option>
                    <option value="nl">🇧🇪 Nederlands</option>
                  </select>
                </div>
              </div>

              <div style={{ fontSize: 13, fontWeight: 600, color: '#0d2b1f', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>{t(lang,'infos_comp')}</div>
              <div className="form-grid">
                <div className="form-group"><label>{t(lang,'date_naiss')}</label><input type="date" value={infos.date_naissance} onChange={e => setInfos({ ...infos, date_naissance: e.target.value })} /></div>
                <div className="form-group"><label>{t(lang,'num_nat')}</label><input placeholder="00-00-00-000-00" value={infos.numero_national} onChange={e => setInfos({ ...infos, numero_national: e.target.value })} /></div>
                <div className="form-group"><label>{t(lang,'profession')}</label><input placeholder={t(lang,'ph_profession')} value={infos.profession} onChange={e => setInfos({ ...infos, profession: e.target.value })} /></div>
                <div className="form-group"><label>{t(lang,'employeur')}</label><input placeholder={t(lang,'ph_employeur')} value={infos.employeur} onChange={e => setInfos({ ...infos, employeur: e.target.value })} /></div>
                <div className="form-group"><label>{t(lang,'contact_urg')}</label><input placeholder={t(lang,'ph_contact_nom')} value={infos.contact_urgence_nom} onChange={e => setInfos({ ...infos, contact_urgence_nom: e.target.value })} /></div>
                <div className="form-group"><label>{t(lang,'contact_tel')}</label><input placeholder={t(lang,'ph_telephone')} value={infos.contact_urgence_tel} onChange={e => setInfos({ ...infos, contact_urgence_tel: e.target.value })} /></div>
                <div className="form-group full">
                  <label>{t(lang,'notes')}</label>
                  <textarea style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 14, outline: 'none', resize: 'vertical', minHeight: 80 }}
                    placeholder={t(lang,'ph_notes')} value={infos.notes} onChange={e => setInfos({ ...infos, notes: e.target.value })} />
                </div>
              </div>

              <div style={{ fontSize: 13, fontWeight: 600, color: '#0d2b1f', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>{t(lang,'bail')}</div>
              <div className="form-grid">
                <div className="form-group full">
                  <label>{t(lang,'type_bail')}</label>
                  <select value={infos.type_bail_id || ''} onChange={e => setInfos({ ...infos, type_bail_id: e.target.value || null })}>
                    <option value="">{t(lang,'aucun')}</option>
                    {typesBail.map(tb => <option key={tb.id} value={tb.id}>{tb.nom}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>{t(lang,'date_sign')}</label><input type="date" value={infos.date_signature || ''} onChange={e => setInfos({ ...infos, date_signature: e.target.value })} /></div>
                <div className="form-group"><label>{t(lang,'indice_ref')}</label><input type="number" placeholder={t(lang,'ph_indice_ref')} value={infos.indice_reference || ''} onChange={e => setInfos({ ...infos, indice_reference: e.target.value })} /></div>
                <div className="form-group full"><label>{t(lang,'nom_indice')}</label><input placeholder={t(lang,'ph_indice_nom')} value={infos.indice_nom || ''} onChange={e => setInfos({ ...infos, indice_nom: e.target.value })} /></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                {savedInfos && <span style={{ fontSize: 13, color: '#1D9E75', fontWeight: 600 }}>{t(lang,'enregistre')}</span>}
                <button className="btn btn-primary" onClick={handleSaveInfos} disabled={savingInfos}>
                  {savingInfos ? t(lang,'enreg_en_cours') : t(lang,'enregistrer')}
                </button>
              </div>
            </>
          )}

          {/* ── ONGLET DOCUMENTS ── */}
          {onglet === 'docs' && (
            <>
              {docLimite === 0 && (
                <div style={{ background: '#fff8e1', border: '1.5px solid #f5c842', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#7a5c00' }}>
                  {t(lang,'doc_locked')}
                </div>
              )}
              {docLimite !== 0 && (
                <>
                  <div style={{ background: '#f7f7f5', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0d2b1f' }}>
                      ➕ {t(lang,'ajouter_doc')}
                      {docLimite !== Infinity && <span style={{ fontWeight: 400, color: '#aaa', marginLeft: 8 }}>{docs.length} / {docLimite}</span>}
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>{t(lang,'type_doc')}</label>
                        <select value={uploadForm.type_doc} onChange={e => setUploadForm({ ...uploadForm, type_doc: e.target.value })}>
                          {getDocTypes(lang).map((label, i) => <option key={DOC_TYPES_KEYS[i]} value={label}>{label}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{t(lang,'notes_opt')}</label>
                        <input placeholder={t(lang,'ph_notes_doc')} value={uploadForm.notes} onChange={e => setUploadForm({ ...uploadForm, notes: e.target.value })} />
                      </div>
                    </div>
                    <label className="upload-zone" style={{ cursor: docLimite !== Infinity && docs.length >= docLimite ? 'not-allowed' : 'pointer', opacity: docLimite !== Infinity && docs.length >= docLimite ? 0.5 : 1 }}>
                      <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleUpload} disabled={uploading || (docLimite !== Infinity && docs.length >= docLimite)} />
                      {uploading
                        ? <div style={{ fontSize: 14, color: '#1D9E75' }}>{t(lang,'envoi_cours')}</div>
                        : <div>
                            <div style={{ fontSize: 24, marginBottom: 6 }}>📎</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>{t(lang,'cliquer_fich')}</div>
                            <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>{t(lang,'formats_acc')}</div>
                          </div>
                      }
                      {docLimite !== Infinity && docs.length >= docLimite && (
                      <div style={{ background: 'linear-gradient(135deg,#fff8e1,#fff3cd)', border: '1.5px solid #f5c842', borderRadius: 10, padding: '14px 16px', fontSize: 13, color: '#7a5c00', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <span>🔒 {t(lang,'doc_limit_reached').replace('{n}', docLimite).replace('{plan}', plan === 'starter' ? 'Starter' : 'Gratuit')}</span>
                        <button
                          className="btn btn-sm"
                          style={{ background: '#1D9E75', color: '#fff', borderColor: '#1D9E75' }}
                          onClick={() => window.open(stripeUrl(STRIPE_PRO_URL, userId, ''), '_blank')}
                        >
                          {t(lang,'doc_limit_upgrade')} →
                        </button>
                      </div>
                      )}
                    </label>
                  </div>
                  {loadingDocs
                    ? <div className="empty">{t(lang,'chargement')}</div>
                    : docs.length === 0
                      ? <div className="empty">{t(lang,'aucun_doc')}</div>
                      : <div className="card">
                          <div className="card-header"><span className="card-title">{docs.length} {t(lang,'nb_docs')}</span></div>
                          {docs.map(doc => (
                            <div className="doc-row" key={doc.id} style={{ padding: '12px 22px' }}>
                              <div className="doc-icon">{doc.nom?.endsWith('.pdf') ? '📄' : doc.nom?.match(/\.(jpg|jpeg|png)$/i) ? '🖼️' : '📎'}</div>
                              <div className="doc-main">
                                <div className="doc-name">{doc.nom}</div>
                                <div className="doc-meta">
                                  <DocBadge type={doc.type_doc} />
                                  {doc.taille && <span style={{ marginLeft: 6 }}>{doc.taille}</span>}
                                  {doc.notes && <span style={{ marginLeft: 6 }}>· {doc.notes}</span>}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <a href={doc.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-ghost" style={{ textDecoration: 'none' }}>⬇️</a>
                                <button className="btn btn-sm btn-danger" onClick={() => handleDeleteDoc(doc)}>🗑️</button>
                              </div>
                            </div>
                          ))}
                        </div>
                  }
                </>
              )}
            </>
          )}

          {/* ── ONGLET MESSAGES ── */}
          {onglet === 'messages' && (
            <>
              <div style={{ flex: 1, overflowY: 'auto', maxHeight: 420, display: 'flex', flexDirection: 'column', gap: 0 }}>
                {messages.length === 0
                  ? <div className="empty">{t(lang,'aucun_msg')}</div>
                  : messages.map(m => (
                    <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.expediteur === 'proprietaire' ? 'flex-end' : 'flex-start', padding: '8px 0' }}>
                      <div style={{ maxWidth: '75%', padding: '10px 14px', borderRadius: 14, background: m.expediteur === 'proprietaire' ? '#0d2b1f' : '#f0f0ee', color: m.expediteur === 'proprietaire' ? '#fff' : '#1a1a1a', fontSize: 14, lineHeight: 1.5, borderBottomRightRadius: m.expediteur === 'proprietaire' ? 4 : 14, borderBottomLeftRadius: m.expediteur === 'locataire' ? 4 : 14 }}>
                        {m.contenu}
                      </div>
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 3 }}>
                        {m.expediteur === 'proprietaire' ? t(lang,'vous') : `${locataire.prenom} ${locataire.nom}`} · {new Date(m.created_at).toLocaleDateString()}
                        {m.expediteur === 'locataire' && !m.lu && <span style={{ marginLeft: 6, color: '#D85A30', fontWeight: 700 }}>●</span>}
                      </div>
                    </div>
                  ))
                }
                <div ref={messagesEndRef} />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 'auto', paddingTop: 12, borderTop: '1px solid #f0f0ee' }}>
                <textarea
                  style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10, fontFamily: "'DM Sans', sans-serif", fontSize: 14, outline: 'none', resize: 'none', minHeight: 60 }}
                  placeholder={t(lang,'votre_msg')}
                  value={newMsg}
                  onChange={e => setNewMsg(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage() } }}
                />
                <button className="btn btn-primary" onClick={handleSendMessage} disabled={sendingMsg || !newMsg.trim()} style={{ alignSelf: 'flex-end', padding: '10px 18px' }}>
                  {sendingMsg ? '...' : t(lang,'envoyer')}
                </button>
              </div>
            </>
          )}

          {/* ── ONGLET EMAILS ── */}
          {onglet === 'emails' && (
            <>
              <div style={{ fontSize: 13, color: '#888', background: '#f0faf5', border: '1px solid #9FE1CB', borderRadius: 8, padding: '10px 14px' }}>
                ℹ️ {t(lang,'emails_info')}
              </div>
              {loadingEmails
                ? <div className="empty">{t(lang,'chargement')}</div>
                : emails.length === 0
                  ? <div className="empty">{t(lang,'emails_aucun')}</div>
                  : <div className="card">
                      <div className="card-header" style={{ flexWrap: 'wrap', gap: 8 }}>
                      <span className="card-title">📬 {emails.length} {t(lang,'emails_nb')}</span>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {['tous', 'relance', 'quittance', 'generation', 'rappel', 'invitation', 'intervention'].map(f => {
                          const count = f === 'tous' ? emails.length : emails.filter(e => e.type === f).length
                          if (count === 0 && f !== 'tous') return null
                          const labels = { tous: 'Tous', relance: '🔴', quittance: '📄', generation: '📬', rappel: '⏰', invitation: '📨', intervention: '🔧' }
                          return (
                            <button key={f}
                              className={`btn btn-sm ${filtreDossierEmail === f ? 'btn-primary' : 'btn-ghost'}`}
                              onClick={() => setFiltreDossierEmail(f)}
                            >
                              {labels[f]} {f !== 'tous' ? '' : 'Tous'} ({count})
                            </button>
                          )
                        })}
                      </div>
                    </div>
                      {emails.filter(e => filtreDossierEmail === 'tous' || e.type === filtreDossierEmail).map(e => {
                      const icons  = { generation: '📬', rappel: '⏰', relance: '🔴', quittance: '📄' }
                      const labels = {
                        generation: t(lang,'email_generation'),
                        rappel:     t(lang,'email_rappel'),
                        relance:    t(lang,'email_relance'),
                        quittance:  t(lang,'email_quittance'),
                        invitation:  t(lang,'email_invitation'),
                      }
                      return (
                        <div key={e.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <div className="row" style={{ cursor: 'pointer', borderBottom: 'none' }}
                            onClick={() => setEmailExpanded(emailExpanded === e.id ? null : e.id)}>
                            <div style={{ fontSize: 22, flexShrink: 0 }}>{icons[e.type] || '📧'}</div>
                            <div className="row-main">
                              <div className="row-name">{labels[e.type] || e.type}</div>
                              <div className="row-sub">
                                {e.recipient || locataire.email}
                                {e.sujet && <span style={{ marginLeft: 8, fontStyle: 'italic' }}>· {e.sujet}</span>}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', fontSize: 12, color: '#aaa', flexShrink: 0 }}>
                              {e.sent_at ? new Date(e.sent_at).toLocaleDateString(_langLocale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </div>
                            <span style={{ fontSize: 11, color: '#aaa', flexShrink: 0 }}>{emailExpanded === e.id ? '▲' : '▼'}</span>
                          </div>
                          {emailExpanded === e.id && (
                            <div style={{ padding: '12px 22px 16px', background: '#f9f9f9', borderTop: '1px solid #f0f0f0' }}>
                              {e.corps
                                ? <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: e.corps }} />
                                : <div style={{ fontSize: 13, color: '#aaa', fontStyle: 'italic' }}>{t(lang,'contenu_indispo')}</div>
                              }
                            </div>
                          )}
                        </div>
                      )
                    })}
                    </div>
              }
            </>
          )}
          {onglet === 'bail' && (
            <BailGenerator
              locataire={locataire}
              bien={bien}
              proprio={proprio}
              userId={userId}
              lang={lang}
            />
          )}

        </div>
      </div>
    </div>
  )
}

//Ponto
// ══════════════════════════════════════════════════════════════

function PontoSection({ userId, plan, lang = 'fr', setPage }) {
  const [connection, setConnection] = useState(null)
  const [loading, setLoading]       = useState(true)
  const [syncing, setSyncing]       = useState(false)
  const [syncResult, setSyncResult] = useState(null)
  const [syncError, setSyncError]   = useState(null)

  const loadConnection = useCallback(async () => {
    const { data } = await supabase
      .from('ponto_connections')
      .select('account_name, account_iban, last_sync_at, ponto_client_id, pending_matches, access_token')
      .eq('user_id', userId)
      .single()
    setConnection(data || null)
    setLoading(false)
  }, [userId])

  useEffect(() => { loadConnection() }, [loadConnection])

    const pendingToReview = syncResult?.toReview?.length > 0
    ? syncResult.toReview
    : (connection?.pending_matches || [])

  const handleDisconnect = async () => {
    if (!window.confirm(`${t(lang,'ponto_deconnecter')} ?`)) return
    await supabase.from('ponto_connections').delete().eq('user_id', userId)
    setConnection(null)
    setSyncResult(null)
  }

  const handleSync = async () => {
    setSyncing(true)
    setSyncResult(null)
    setSyncError(null)
    try {
      const token = await getToken()
      const res   = await fetch('/api/router', {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ action: 'ponto-sync' }),
      })
      const result = await res.json()
      if (!res.ok) {
        setSyncError(result.error || 'Erreur de synchronisation')
      } else {
        setSyncResult(result)
        await loadConnection()
      }
    } catch (e) {
      setSyncError(e.message)
    }
    setSyncing(false)
  }

  if (plan !== 'pro' && plan !== 'enterprise') {
  return (
    <div className="card" style={{ opacity: 0.75 }}>
      <div className="card-header">
        <span className="card-title">
          🏦 {t(lang,'ponto_titre')}
          <span style={{ fontSize: 10, fontWeight: 700, background: '#e1f5ee', color: '#0F6E56', padding: '2px 8px', borderRadius: 10, marginLeft: 6 }}>
            via Ponto
          </span>
          <span style={{ fontSize: 9, fontWeight: 700, background: '#B8860B', color: '#fff', padding: '2px 6px', borderRadius: 4, marginLeft: 6, letterSpacing: 0.5 }}>
            PRO
          </span>
        </span>
      </div>
      <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 13, color: '#888', lineHeight: 1.6 }}>
          {t(lang,'ponto_desc')}
        </div>
        <button
          onClick={() => setPage('pricing')}
          style={{ alignSelf: 'flex-start', background: '#0d2b1f', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          ⭐ {t(lang,'passer_pro') || 'Passer au plan Pro'}
        </button>
      </div>
    </div>
  )
  }

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">
          🏦 {t(lang,'ponto_titre')}
          <span style={{ fontSize: 10, fontWeight: 700, background: '#e1f5ee', color: '#0F6E56', padding: '2px 8px', borderRadius: 10, marginLeft: 6 }}>
            via Ponto
          </span>
        </span>
      </div>
      <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>

        <div style={{ fontSize: 13, color: '#888', background: '#f0faf5', border: '1px solid #9FE1CB', borderRadius: 8, padding: '10px 14px', lineHeight: 1.6 }}>
          ℹ️ {t(lang,'ponto_desc')}
        </div>

        {loading ? (
          <div style={{ color: '#aaa', fontSize: 13 }}>{t(lang,'chargement')}</div>

        ) : (connection?.ponto_client_id || connection?.access_token) ? (
          <>
            {/* ── Compte connecté ── */}
            <div style={{ background: '#f0faf5', border: '1.5px solid #9FE1CB', borderRadius: 12, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 28 }}>🏦</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>
                  {connection.account_name || t(lang,'ponto_compte')}
                </div>
                {connection.account_iban && (
                  <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{connection.account_iban}</div>
                )}
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                  {connection.ponto_client_id && (
                    <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                      ID : {connection.ponto_client_id.slice(0, 8)}••••
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                  {connection.last_sync_at
                    ? `${t(lang,'ponto_derniere_sync')} ${new Date(connection.last_sync_at).toLocaleString(_langLocale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                    : t(lang,'ponto_jamais_sync')}
                </div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, background: '#e1f5ee', color: '#0F6E56', padding: '4px 10px', borderRadius: 20 }}>
                {t(lang,'ponto_connecte')}
              </span>
            </div>

            {/* ── Résultat sync ── */}
            {syncResult && (
  <div style={{ background: '#f0faf5', border: '1px solid #9FE1CB', borderRadius: 10, padding: '12px 16px', fontSize: 13 }}>
    <div style={{ fontWeight: 600, color: '#0F6E56', marginBottom: 6 }}>{t(lang,'ponto_sync_ok')}</div>
    <div style={{ color: '#555', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      <span>📥 {syncResult.credits} transaction{syncResult.credits > 1 ? 's' : ''} reçue{syncResult.credits > 1 ? 's' : ''}</span>
      <span>✓ {syncResult.matched} loyer{syncResult.matched > 1 ? 's' : ''} validé{syncResult.matched > 1 ? 's' : ''}</span>
      {syncResult.toReview?.length > 0 && <span style={{ color: '#854F0B' }}>⚠️ {syncResult.toReview.length} à vérifier</span>}
      {syncResult.unmatched > 0 && <span style={{ color: '#aaa' }}>○ {syncResult.unmatched} non identifié{syncResult.unmatched > 1 ? 's' : ''}</span>}
    </div>

    
    <div style={{ fontSize: 11, color: '#aaa', display: 'flex', alignItems: 'center', gap: 6 }}>
      {t(lang,'ponto_valider_dans')}{' '}
      <button className="btn btn-sm btn-ghost" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setPage && setPage('paiements')}>
        l'onglet Paiements →
      </button>
    </div>
  </div>
)}

{pendingToReview.length > 0 && (
  <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: '#854F0B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {t(lang,'ponto_confirmer')}
    </div>
    {pendingToReview.map((r, i) => (
      <div key={i} style={{ background: '#fef9f0', border: '1px solid #F5D08A', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#854F0B', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 600 }}>{r.locataire}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span>{r.montant}€ · {r.date}</span>
          <button
            className="btn btn-sm"
            style={{ background: '#1D9E75', color: '#fff', borderColor: '#1D9E75', fontSize: 11, padding: '3px 8px' }}
            onClick={async () => {
              try {
                const token = await getToken()
                const res = await fetch('/api/router', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'ponto-confirm', paiementId: r.paiementId, date: r.date }),
              })
              if (res.ok) {
                setSyncResult(s => s ? ({ ...s, toReview: s.toReview.filter((_, j) => j !== i) }) : null)
                await loadConnection()
              }
              } catch (e) { console.error(e) }
            }}
          >
            ✓ Confirmer
          </button>
          <button
            className="btn btn-sm btn-danger"
            style={{ fontSize: 11, padding: '3px 8px' }}
            onClick={async () => {
            try {
              const token = await getToken()
              await fetch('/api/router', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'ponto-refuse', paiementId: r.paiementId }),
              })
              await loadConnection()
            } catch (e) { console.error(e) }
            }}
            >
              ✕
            </button>
        </div>
      </div>
    ))}
          </div>
        )}
              

            {syncError && <div className="error-msg">{syncError}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button className="btn btn-ghost btn-sm" style={{ color: '#D85A30', borderColor: '#D85A30' }} onClick={handleDisconnect}>
                {t(lang,'ponto_deconnecter')}
              </button>
              <button className="btn btn-primary" onClick={handleSync} disabled={syncing}>
                {syncing ? `⏳ ${t(lang,'traitement_en_cours')}` : t(lang,'ponto_sync_now')}
              </button>
            </div>
          </>

        ) : (
          <>
            
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: '#aaa', flex: 1 }}>
                {t(lang,'ponto_securise')}
              </div>
              <button
              className="btn btn-primary"
              onClick={async () => {
              const { data: { user } } = await supabase.auth.getUser()
              window.location.href = `/api/ponto-auth?userId=${user.id}`
            }}
            >
              {t(lang,'connecter_banque')}
            </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
  function PontoSyncButton({ userId, plan, onSynced, lang = 'fr' }) {
  const [syncing, setSyncing]   = useState(false)
  const [result, setResult]     = useState(null)
  const [hasConn, setHasConn]   = useState(null)

  useEffect(() => {
    if (!userId || (plan !== 'pro' && plan !== 'starter' && plan !== 'enterprise')) return
    supabase
      .from('ponto_connections')
      .select('account_id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .then(({ count }) => setHasConn(count > 0))
  }, [userId, plan])

   if (!hasConn) return null

  const handleSync = async () => {
    setSyncing(true)
    setResult(null)
    try {
      const token = await getToken()
      const res   = await fetch('/api/router', {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ action: 'ponto-sync' }),
      })
      const data = await res.json()
      setResult(data)
      if (data.matched > 0 && onSynced) onSynced()
      setTimeout(() => setResult(null), 5000)
    } catch (e) {
      setResult({ error: e.message })
    }
    setSyncing(false)
  }

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
      {result && !result.error && (
        <div style={{ position: 'absolute', top: -36, right: 0, background: result.matched > 0 ? '#0d2b1f' : '#555', color: '#fff', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 8, whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          {result.matched > 0 ? `✅ ${result.matched} loyer${result.matched > 1 ? 's' : ''} validé${result.matched > 1 ? 's' : ''}` : '○ Aucun nouveau paiement'}
          {result.toReview?.length > 0 && ` · ⚠️ ${result.toReview.length} à vérifier`}
        </div>
      )}
      {result?.error && (
        <div style={{ position: 'absolute', top: -36, right: 0, background: '#D85A30', color: '#fff', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 8, whiteSpace: 'nowrap' }}>
          ⚠️ {result.error}
        </div>
      )}
      <button className="btn btn-sm btn-ghost" style={{ color: '#1D9E75', borderColor: '#9FE1CB' }} onClick={handleSync} disabled={syncing}>
        {syncing ? '⏳' : '🏦'} {syncing ? t(lang,'traitement_en_cours') : 'Sync Ponto'}
      </button>
    </div>
  )
}

// ─── PAGE PARAMÈTRES ──────────────────────────────────────────
function ParametresPage({ userId, userEmail, lang = 'fr', onLangChange, plan = 'free', setPage }) {
  const [form, setForm]     = useState({ proprio_nom: '', proprio_adresse: '', proprio_ville: '', proprio_telephone: '', currency: 'EUR', locale: 'fr-FR', lang: 'fr' })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving]   = useState(false)
  const [saved, setSaved]     = useState(false)

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('profiles').select('proprio_nom, proprio_adresse, proprio_ville, proprio_telephone, currency, locale, lang, interventions_enabled').eq('id', userId).single()
      if (data) setForm({
        proprio_nom:       data.proprio_nom || '',
        proprio_adresse:   data.proprio_adresse || '',
        proprio_ville:     data.proprio_ville || '',
        proprio_telephone: data.proprio_telephone || '',
        currency:          data.currency || 'EUR',
        locale:            data.locale   || 'fr-FR',
        lang:              data.lang     || 'fr',
        interventions_enabled: data.interventions_enabled !== false,
      })
      setLoading(false)
    }
    load()
  }, [userId])

  const handleSave = async () => {
    setSaving(true)
    const { error } = await supabase
      .from('profiles')
      .upsert({
        id:                userId,
        proprio_nom:       form.proprio_nom,
        proprio_adresse:   form.proprio_adresse,
        proprio_ville:     form.proprio_ville,
        proprio_telephone: form.proprio_telephone,
        currency:          form.currency,
        locale:            form.locale,
        lang:              form.lang,
        interventions_enabled: form.interventions_enabled,
      }, { onConflict: 'id' })

    setSaving(false)
    if (error) {
      console.error('Erreur sauvegarde paramètres:', error)
      alert(t(lang,'erreur_generique') + error.message)
    } else {
      setCurrencyContext(form.currency, form.locale)
      if (onLangChange) { onLangChange(form.lang); setLangLocale(form.lang); localStorage.setItem('domavix_lang', form.lang) }
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    }
  }

  const handleCurrencyChange = (code) => {
    const curr = CURRENCIES.find(c => c.code === code)
    setForm({ ...form, currency: code, locale: curr?.locale || 'fr-FR' })
  }

  if (loading) return <div className="empty">{t(lang,'chargement')}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <div className="page-title">{t(lang,'param_titre')}</div>
        <div className="page-sub">{t(lang,'param_sub')}</div>
      </div>

      {/* Langue */}
      <div className="card">
        <div className="card-header"><span className="card-title">🌐 {t(lang, 'langue_iface')}</span></div>
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 13, color: '#888', background: '#f0faf5', border: '1px solid #9FE1CB', borderRadius: 8, padding: '10px 14px' }}>
            ℹ️ {t(lang, 'langue_sub')}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {LANGS.map(l => (
              <button key={l.code}
                onClick={() => { setForm({ ...form, lang: l.code }); if (onLangChange) { onLangChange(l.code); localStorage.setItem('domavix_lang', l.code) } }}
                style={{
                  padding: '10px 20px', borderRadius: 10, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
                  border: form.lang === l.code ? '2px solid #1D9E75' : '2px solid #e0e0e0',
                  background: form.lang === l.code ? '#e8f5ef' : '#fff',
                  color: form.lang === l.code ? '#0F6E56' : '#555',
                  transition: 'all 0.15s',
                }}
              >
                {l.flag} {l.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#aaa' }}>
            {t(lang, 'lang_note')}
          </div>
        </div>
      </div>

      {/* Devise */}
      <div className="card">
        <div className="card-header"><span className="card-title">💱 {t(lang,'devise_fmt')}</span></div>
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-grid">
            <div className="form-group full">
              <label>{t(lang,'devise')}</label>
              <select value={form.currency} onChange={e => handleCurrencyChange(e.target.value)}>
                {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ fontSize: 13, color: '#888', background: '#f7f7f5', borderRadius: 8, padding: '10px 14px' }}>
            {t(lang,'apercu')} : <strong>{fmt(1234.56)}</strong>
          </div>
        </div>
      </div>

      {/* Infos proprio */}
      <div className="card">
        <div className="card-header"><span className="card-title">👤 {t(lang,'infos_prop')}</span></div>
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 13, color: '#888', background: '#f0faf5', border: '1px solid #9FE1CB', borderRadius: 8, padding: '10px 14px' }}>
            ℹ️ {t(lang,'infos_prop_sub')}
          </div>
          <div className="form-grid">
            <div className="form-group full">
              <label>{t(lang,'nom_complet')} *</label>
              <input placeholder={t(lang,'ph_proprio_nom')} value={form.proprio_nom} onChange={e => setForm({ ...form, proprio_nom: e.target.value })} />
            </div>
            <div className="form-group full">
              <label>{t(lang,'adresse')}</label>
              <input placeholder={t(lang,'ph_proprio_adr')} value={form.proprio_adresse} onChange={e => setForm({ ...form, proprio_adresse: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t(lang,'ville')}</label>
              <input placeholder={t(lang,'ph_proprio_ville')} value={form.proprio_ville} onChange={e => setForm({ ...form, proprio_ville: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t(lang,'telephone')}</label>
              <input placeholder={t(lang,'ph_proprio_tel')} value={form.proprio_telephone} onChange={e => setForm({ ...form, proprio_telephone: e.target.value })} />
            </div>
            <div className="form-group full">
              <label>{t(lang,'email_cpt')}</label>
              <input value={userEmail} disabled style={{ background: '#f5f5f5', color: '#aaa' }} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
            {saved && <span style={{ fontSize: 13, color: '#1D9E75', fontWeight: 600 }}>{t(lang,'enregistre')}</span>}
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t(lang,'enreg_en_cours') : t(lang,'enregistrer')}</button>
          </div>
        </div>
      </div>
      <div className="card">
      <div className="card-header"><span className="card-title">🔑 {t(lang, 'chgt_mdp')}</span></div>
      <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: '#888', background: '#f0faf5', border: '1px solid #9FE1CB', borderRadius: 8, padding: '10px 14px' }}>
          ℹ️ {t(lang, 'reset_email_info')} <strong>{userEmail}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={async () => {
            await supabase.auth.resetPasswordForEmail(userEmail, { redirectTo: 'https://domavix.com/app' })
            alert(t(lang, 'email_envoye'))
          }}>
            {t(lang, 'chgt_mdp')} →
          </button>
        </div>
      </div>
    </div>
          {/* Module Interventions */}
      {(plan === 'pro' || plan === 'enterprise') && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">🔧 {t(lang, 'module_interv_title')}</span>
          </div>
          <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: '#888', lineHeight: 1.6 }}>
              {t(lang, 'module_interv_desc')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                className={`toggle ${form.interventions_enabled ? 'on' : ''}`}
                onClick={async () => {
                  const newVal = !form.interventions_enabled
                  setForm({ ...form, interventions_enabled: newVal })
                  await supabase.from('profiles').update({ interventions_enabled: newVal }).eq('id', userId)
                }}
              />
              <span style={{ fontSize: 14, fontWeight: 500, color: '#333' }}>
                {form.interventions_enabled ? `✅ ${t(lang, 'module_interv_on')}` : `⛔ ${t(lang, 'module_interv_off')}`}
              </span>
            </div>
          </div>
        </div>
      )}
      <PontoSection userId={userId} plan={plan} lang={lang} setPage={setPage} />


  {/* ── ZONE DANGER ── */}
<div className="card" style={{ border: '1.5px solid #fca5a5', marginTop: 8 }}>
  <div style={{ fontSize: 13, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
    ⚠️ {t(lang,'zone_danger')}
  </div>
  <div style={{ fontSize: 13, color: '#666', marginBottom: 16, lineHeight: 1.6 }}>
    {t(lang,'suppr_compte_desc')}
  </div>
  <button
    className="btn btn-danger"
    onClick={async () => {
      const confirm1 = window.confirm(t(lang,'suppr_compte_confirm1'))
      if (!confirm1) return
      const confirm2 = window.confirm(t(lang,'suppr_compte_confirm2'))
      if (!confirm2) return
      try {
        const token = await getToken()
        const res = await fetch('/api/router', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'delete-account' }),
        })
        if (res.ok) {
          await supabase.auth.signOut()
          window.location.reload()
        } else {
          const err = await res.json()
          alert(t(lang,'erreur_generique') + (err.error || ''))
        }
      } catch(e) {
        alert(t(lang,'erreur_reseau'))
      }
    }}
  >
    🗑️ {t(lang,'suppr_compte_btn')}
  </button>
</div>
    </div>
  )
}

// ─── HOOK MOBILE ─────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

// ─── MESSAGERIE GLOBALE ───────────────────────────────────────
function MessageriePage({ userId, locataires, lang = 'fr' }) {
  const [fils, setFils]           = useState([])   // { locataire, messages, nonLus }
  const [selected, setSelected]   = useState(null) // locataire_id sélectionné
  const [newMsg, setNewMsg]       = useState('')
  const [sending, setSending]     = useState(false)
  const [loading, setLoading]     = useState(true)
  const [showFil, setShowFil]         = useState(false)
  const messagesEndRef                 = useRef(null)

  const locatairesActifs = locataires.filter(l => !l.date_sortie)

  const loadFils = useCallback(async () => {
    if (!locatairesActifs.length) { setLoading(false); return }

    const { data: msgs } = await supabase
      .from('messages')
      .select('*')
      .eq('proprio_user_id', userId)
      .order('created_at', { ascending: true })

    // Grouper par locataire
    const grouped = locatairesActifs.map(loc => {
      const locMsgs = (msgs || []).filter(m => m.locataire_id === loc.id)
      const nonLus  = locMsgs.filter(m => !m.lu && m.expediteur === 'locataire').length
      return { locataire: loc, messages: locMsgs, nonLus }
    }).sort((a, b) => {
      // Trier par dernier message + non lus en premier
      const lastA = a.messages[a.messages.length - 1]?.created_at || ''
      const lastB = b.messages[b.messages.length - 1]?.created_at || ''
      if (b.nonLus !== a.nonLus) return b.nonLus - a.nonLus
      return lastB.localeCompare(lastA)
    })

    setFils(grouped)
    if (!selected && grouped.length) setSelected(grouped[0].locataire.id)
    setLoading(false)
  }, [userId, locatairesActifs, selected])

  useEffect(() => { loadFils() }, [loadFils])

  // Realtime
  useEffect(() => {
    const channel = supabase.channel('messagerie-proprio')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `proprio_user_id=eq.${userId}` }, () => loadFils())
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [userId, loadFils])

  // Scroll vers le bas seulement quand on change de fil ou reçoit un nouveau message
  const prevSelectedRef = useRef(null)
  useEffect(() => {
    if (selected !== prevSelectedRef.current) {
      prevSelectedRef.current = selected
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
    }
  }, [selected])

  const handleSend = async () => {
    if (!newMsg.trim() || !selected) return
    setSending(true)
    await supabase.from('messages').insert({
      locataire_id:    selected,
      proprio_user_id: userId,
      expediteur:      'proprietaire',
      contenu:         newMsg.trim(),
      lu:              false,
    })
    // Marquer les messages du locataire comme lus
    await supabase.from('messages').update({ lu: true }).eq('locataire_id', selected).eq('expediteur', 'locataire')
    setNewMsg('')
    setSending(false)
    await loadFils()
  }

  const handleSelectFil = async (locId) => {
    setSelected(locId)
    setShowFil(true)   // sur mobile, affiche le fil
    await supabase.from('messages').update({ lu: true }).eq('locataire_id', locId).eq('expediteur', 'locataire')
    await loadFils()
  }

  const totalNonLus = fils.reduce((s, f) => s + f.nonLus, 0)
  const selectedFil = fils.find(f => f.locataire.id === selected)

  if (loading) return <div className="empty">{t(lang,'chargement')}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <div style={{ marginBottom: 20 }}>
        <div className="page-title">
          {t(lang,'messagerie_titre')}
          {totalNonLus > 0 && (
            <span style={{ marginLeft: 10, fontSize: 13, background: '#D85A30', color: '#fff', padding: '3px 10px', borderRadius: 20, fontFamily: "'DM Sans', sans-serif", fontWeight: 700 }}>
              {totalNonLus} {totalNonLus > 1 ? t(lang,'nouveaux_x') : t(lang,'nouveau_x')}
            </span>
          )}
        </div>
        <div className="page-sub">{t(lang,'msg_sub')}</div>
      </div>

      {locatairesActifs.length === 0
        ? <div className="card"><div className="empty">{t(lang,'aucun_loc')} {t(lang,'actif_pour_linstant')}</div></div>
        : <MessagerieFils
            fils={fils}
            selected={selected}
            showFil={showFil}
            selectedFil={selectedFil}
            newMsg={newMsg}
            sending={sending}
            messagesEndRef={messagesEndRef}
            onSelectFil={handleSelectFil}
            onBack={() => setShowFil(false)}
            onNewMsg={setNewMsg}
            onSend={handleSend}
            lang={lang}
          />
      }
    </div>
  )
}

// ─── MESSAGERIE FILS (rendu responsive) ──────────────────────
function MessagerieFils({ fils, selected, showFil, selectedFil, newMsg, sending, messagesEndRef, onSelectFil, onBack, onNewMsg, onSend, lang = 'fr' }) {
  const isMobile = useIsMobile()
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'clamp(200px, 30%, 280px) 1fr', gap: 16, height: isMobile ? 'auto' : 600 }}>

      {/* Liste des fils */}
      {(!isMobile || !showFil) && (
        <div className="card" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="card-header"><span className="card-title">{fils.length} {t(lang,'conv_titre')}</span></div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {fils.map(fil => {
              const lastMsg = fil.messages[fil.messages.length - 1]
              const isSelected = selected === fil.locataire.id
              return (
                <div key={fil.locataire.id} onClick={() => onSelectFil(fil.locataire.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer', background: isSelected ? '#f0faf5' : 'transparent', borderLeft: isSelected ? '3px solid #1D9E75' : '3px solid transparent', borderBottom: '1px solid #f8f8f8', transition: 'all 0.15s' }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#e6f1fb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#185FA5', flexShrink: 0 }}>
                    {fil.locataire.prenom?.[0]}{fil.locataire.nom?.[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>{fil.locataire.prenom} {fil.locataire.nom}</div>
                      {fil.nonLus > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: '#D85A30', color: '#fff', padding: '2px 7px', borderRadius: 20 }}>{fil.nonLus}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#aaa', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {lastMsg ? (lastMsg.expediteur === 'proprietaire' ? `${t(lang,'vous')} : ` : '') + lastMsg.contenu : t(lang,'aucun_msg')}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Fil de messages */}
      {(!isMobile || showFil) && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: isMobile ? 'calc(100vh - 160px)' : '100%' }}>
          {selectedFil ? (
            <>
              <div className="card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isMobile && <button className="btn btn-sm btn-ghost" onClick={onBack}>{t(lang,'retour')}</button>}
                  <span className="card-title">{selectedFil.locataire.prenom} {selectedFil.locataire.nom}</span>
                </div>
                <span style={{ fontSize: 12, color: '#aaa' }}>{selectedFil.locataire.email}</span>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {selectedFil.messages.length === 0
                  ? <div className="empty">{t(lang,'aucun_msg')}</div>
                  : selectedFil.messages.map(m => (
                    <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.expediteur === 'proprietaire' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                      <div style={{ maxWidth: '70%', padding: '10px 14px', borderRadius: 14, background: m.expediteur === 'proprietaire' ? '#0d2b1f' : '#f0f0ee', color: m.expediteur === 'proprietaire' ? '#fff' : '#1a1a1a', fontSize: 14, lineHeight: 1.5, borderBottomRightRadius: m.expediteur === 'proprietaire' ? 4 : 14, borderBottomLeftRadius: m.expediteur === 'locataire' ? 4 : 14 }}>
                        {m.contenu}
                      </div>
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 3 }}>
                        {m.expediteur === 'proprietaire' ? t(lang,'vous') : selectedFil.locataire.prenom} · {new Date(m.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))
                }
                <div ref={messagesEndRef} />
              </div>
              <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0ee', display: 'flex', gap: 10 }}>
                <textarea style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10, fontFamily: "'DM Sans', sans-serif", fontSize: 14, outline: 'none', resize: 'none', minHeight: 48 }}
                  placeholder={t(lang,'votre_msg')} value={newMsg} rows={2}
                  onChange={e => onNewMsg(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend() } }}
                />
                <button className="btn btn-primary" onClick={onSend} disabled={sending || !newMsg.trim()} style={{ alignSelf: 'flex-end', padding: '10px 20px' }}>
                  {sending ? '...' : t(lang,'envoyer')}
                </button>
              </div>
            </>
          ) : (
            <div className="empty">{t(lang,'sel_conv')}</div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── GÉNÉRATION AUTOMATIQUE DES ÉCHÉANCES ─────────────────────
const getCategoriesBail = (lang) => [
  { value: 'residentiel_369', label: t(lang,'bail_cat_369'),   duree: 36,  preavis: 3 },
  { value: 'habitation_9ans', label: t(lang,'bail_cat_9ans'),  duree: 108, preavis: 6 },
  { value: 'court_terme',     label: t(lang,'bail_cat_court'), duree: 36,  preavis: 3 },
  { value: 'commercial',      label: t(lang,'bail_cat_comm'),  duree: 36,  preavis: 6 },
  { value: 'autre',           label: t(lang,'bail_cat_autre'), duree: 12,  preavis: 1 },
]

async function generateEcheancesForLocataire(locataire, typeBail, userId, lang = 'fr') {
  if (!locataire.date_signature || !typeBail) return 0

  const dateSignature = new Date(locataire.date_signature)
  const echeancesToCreate = []
  const now = new Date()

  // ── Renouvellements ──────────────────────────────────────────
  if (typeBail.duree_mois) {
    let dateRenouv = new Date(dateSignature)
    dateRenouv.setMonth(dateRenouv.getMonth() + typeBail.duree_mois)

    // Avancer jusqu'à la prochaine échéance future
    while (dateRenouv < now) {
      dateRenouv.setMonth(dateRenouv.getMonth() + typeBail.duree_mois)
    }

    // Une seule échéance — la prochaine
    echeancesToCreate.push({
      user_id:       userId,
      locataire_id:  locataire.id,
      type:          'renouvellement',
      date_echeance: dateRenouv.toISOString().split('T')[0],
      rappel_jours:  typeBail.rappel_renouvellement_jours || 90,
      note:          `Bail ${getCategoriesBail(lang).find(c => c.value === typeBail.categorie)?.label || ''} · Préavis ${typeBail.preavis_mois} mois`,
      traitee:       false,
    })
  }

  // ── Indexations ──────────────────────────────────────────────
  if (typeBail.indexation_auto && typeBail.indexation_mois) {
    let dateIdx = new Date(dateSignature)
    dateIdx.setMonth(dateIdx.getMonth() + typeBail.indexation_mois)

    // Avancer jusqu'à la prochaine future
    while (dateIdx < now) {
      dateIdx.setMonth(dateIdx.getMonth() + typeBail.indexation_mois)
    }

    echeancesToCreate.push({
      user_id:       userId,
      locataire_id:  locataire.id,
      type:          'indexation',
      date_echeance: dateIdx.toISOString().split('T')[0],
      rappel_jours:  typeBail.rappel_indexation_jours || 30,
      note:          `Indice ${locataire.indice_nom || typeBail.indexation_indice || 'configurable'}${locataire.indice_reference ? ` · Valeur ref: ${locataire.indice_reference}` : ''}`,
      traitee:       false,
    })
  }

  if (!echeancesToCreate.length) return 0

  // Éviter les doublons — vérifier les échéances existantes
  const { data: existing } = await supabase
    .from('echeances')
    .select('date_echeance, type')
    .eq('locataire_id', locataire.id)

  const existingKeys = new Set((existing || []).map(e => `${e.type}-${e.date_echeance}`))
  const toInsert = echeancesToCreate.filter(e => !existingKeys.has(`${e.type}-${e.date_echeance}`))

  if (toInsert.length > 0) {
    await supabase.from('echeances').insert(toInsert)
  }
  return toInsert.length
}

// ─── TYPES DE BAUX ────────────────────────────────────────────
function TypesBauxPage({ userId, locataires, onEcheancesUpdated, lang = 'fr' }) {
  const [typesBail, setTypesBail]     = useState([])
  const [loading, setLoading]         = useState(true)
  const [showForm, setShowForm]       = useState(false)
  const [editType, setEditType]       = useState(null)
  const [form, setForm] = useState({
    nom: '', categorie: 'residentiel_369', duree_mois: 36,
    reconduction_tacite: true, preavis_mois: 3,
    indexation_auto: true, indexation_mois: 12, indexation_indice: 'ABEX',
    rappel_renouvellement_jours: 90, rappel_indexation_jours: 30, notes: '',
  })

  const loadData = useCallback(async () => {
    const { data } = await supabase.from('types_bail').select('*').eq('user_id', userId).order('created_at')
    setTypesBail(data || [])
    setLoading(false)
  }, [userId])

  useEffect(() => { loadData() }, [loadData])

  const handleCategorieChange = (cat) => {
    const preset = getCategoriesBail(lang).find(c => c.value === cat)
    setForm(f => ({ ...f, categorie: cat, duree_mois: preset?.duree || 12, preavis_mois: preset?.preavis || 1 }))
  }

  const handleSave = async () => {
    if (!form.nom) return
    const payload = { ...form, user_id: userId, duree_mois: parseInt(form.duree_mois), preavis_mois: parseInt(form.preavis_mois), indexation_mois: parseInt(form.indexation_mois), rappel_renouvellement_jours: parseInt(form.rappel_renouvellement_jours), rappel_indexation_jours: parseInt(form.rappel_indexation_jours) }
    if (editType) await supabase.from('types_bail').update(payload).eq('id', editType.id)
    else await supabase.from('types_bail').insert(payload)
    setShowForm(false); setEditType(null)
    setForm({ nom: '', categorie: 'residentiel_369', duree_mois: 36, reconduction_tacite: true, preavis_mois: 3, indexation_auto: true, indexation_mois: 12, indexation_indice: 'ABEX', rappel_renouvellement_jours: 90, rappel_indexation_jours: 30, notes: '' })
    loadData()
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t(lang,'suppr_bail_confirm'))) return
    await supabase.from('types_bail').delete().eq('id', id)
    loadData()
  }

  if (loading) return <div className="empty">{t(lang,'chargement')}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="page-header-row">
        <div>
          <div className="page-title">{t(lang,'baux_titre')}</div>
          <div className="page-sub">{t(lang,'baux_sub')}</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setEditType(null) }}>
          {showForm ? t(lang,'annuler') : t(lang,'nouveau_bail')}
        </button>
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="form-card green">
          <h3>📋 {editType ? t(lang,'modifier_b') : t(lang,'nouveau_bail')}</h3>
          <div className="form-grid">
            <div className="form-group full">
              <label>{t(lang,'nom_modele')}</label>
              <input placeholder={t(lang,'ph_nom_bail')} value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t(lang,'categorie')}</label>
              <select value={form.categorie} onChange={e => handleCategorieChange(e.target.value)}>
                {getCategoriesBail(lang).map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t(lang,'duree_mois')}</label>
              <input type="number" min="1" max="360" value={form.duree_mois} onChange={e => setForm({ ...form, duree_mois: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t(lang,'preavis')}</label>
              <input type="number" min="1" max="12" value={form.preavis_mois} onChange={e => setForm({ ...form, preavis_mois: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t(lang,'rappel_renouv')}</label>
              <select value={form.rappel_renouvellement_jours} onChange={e => setForm({ ...form, rappel_renouvellement_jours: e.target.value })}>
                <option value={30}>{t(lang,'un_mois_avant')}</option>
                <option value={60}>{t(lang,'deux_mois_avant')}</option>
                <option value={90}>{t(lang,'trois_mois_avant')}</option>
                <option value={180}>{t(lang,'six_mois_avant')}</option>
              </select>
            </div>

            {/* Séparateur indexation */}
            <div className="form-group full" style={{ borderTop: '1px solid #d0e8df', paddingTop: 12, marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ marginBottom: 0 }}>{t(lang,'index_auto')}</label>
                <button className={`toggle ${form.indexation_auto ? 'on' : ''}`} onClick={() => setForm({ ...form, indexation_auto: !form.indexation_auto })} />
              </div>
            </div>
            {form.indexation_auto && (
              <>
                <div className="form-group">
                  <label>{t(lang,'freq_mois')}</label>
                  <input type="number" min="1" max="24" value={form.indexation_mois} onChange={e => setForm({ ...form, indexation_mois: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>{t(lang,'indice_ref2')}</label>
                  <input placeholder={t(lang,'ph_indice_ref2')} value={form.indexation_indice} onChange={e => setForm({ ...form, indexation_indice: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>{t(lang,'rappel_idx')}</label>
                  <select value={form.rappel_indexation_jours} onChange={e => setForm({ ...form, rappel_indexation_jours: e.target.value })}>
                    <option value={14}>{t(lang,'quatorze_j_avant')}</option>
                    <option value={30}>{t(lang,'un_mois_avant')}</option>
                    <option value={60}>{t(lang,'deux_mois_avant')}</option>
                  </select>
                </div>
              </>
            )}
            <div className="form-group full">
              <label>{t(lang,'notes')}</label>
              <input placeholder={t(lang,'infos_complementaires')} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleSave}>{editType ? t(lang,'modifier_b') : t(lang,'creer_modele')}</button>
          </div>
        </div>
      )}

      {/* Liste des types */}
      {typesBail.length === 0
        ? <div className="card"><div className="empty">{t(lang,'aucun_bail')}</div></div>
        : typesBail.map(tb => {
          const cat = getCategoriesBail(lang).find(c => c.value === tb.categorie)
          const locCount = locataires.filter(l => l.type_bail_id === tb.id && !l.date_sortie).length
          return (
            <div key={tb.id} className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">📋 {tb.nom}</div>
                  <div style={{ fontSize: 12, color: '#aaa', marginTop: 3 }}>{cat?.label} · {locCount} {t(lang,'nb_loc')}</div>
                </div>
                <div className="row-actions">
                  <button className="btn btn-sm btn-ghost" onClick={() => { setEditType(t); setForm({ nom: tb.nom, categorie: tb.categorie, duree_mois: tb.duree_mois, reconduction_tacite: tb.reconduction_tacite, preavis_mois: tb.preavis_mois, indexation_auto: tb.indexation_auto, indexation_mois: tb.indexation_mois, indexation_indice: tb.indexation_indice || 'ABEX', rappel_renouvellement_jours: tb.rappel_renouvellement_jours, rappel_indexation_jours: tb.rappel_indexation_jours, notes: tb.notes || '' }); setShowForm(true) }}>✏️</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(tb.id)}>🗑️</button>
                </div>
              </div>
              <div style={{ padding: '12px 22px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                <div><div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t(lang,'duree_mois')}</div><div style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>{tb.duree_mois} mois</div></div>
                <div><div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t(lang,'preavis')}</div><div style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>{tb.preavis_mois} mois</div></div>
                <div><div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t(lang,'index_auto')}</div><div style={{ fontSize: 14, fontWeight: 600, color: tb.indexation_auto ? '#1D9E75' : '#aaa' }}>{tb.indexation_auto ? `${t(lang,'tous_les')} ${tb.indexation_mois} ${t(lang,'mois_idx')} · ${tb.indexation_indice || t(lang,'configurable')}` : t(lang,'index_desactivee')}</div></div>
                <div><div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t(lang,'rappel_renouv')}</div><div style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>J-{tb.rappel_renouvellement_jours} / J-{tb.rappel_indexation_jours}</div></div>
              </div>
            </div>
          )
        })
      }

      {/* Section : assigner un type à un locataire */}
      <AssignBailSection userId={userId} locataires={locataires} typesBail={typesBail} onGenerated={onEcheancesUpdated} lang={lang} />
    </div>
  )
}

// ─── ASSIGNER TYPE DE BAIL À UN LOCATAIRE ─────────────────────
function AssignBailSection({ userId, locataires, typesBail, onGenerated, lang = 'fr' }) {
  const [selected, setSelected]   = useState('')
  const [typeBailId, setTypeBailId] = useState('')
  const [dateSignature, setDateSignature] = useState('')
  const [indiceRef, setIndiceRef] = useState('')
  const [indiceNom, setIndiceNom] = useState('')
  const [saving, setSaving]       = useState(false)
  const [saved, setSaved]         = useState(false)

  const locatairesActifs = locataires.filter(l => !l.date_sortie)

  const handleAssign = async () => {
    if (!selected || !typeBailId || !dateSignature) return
    setSaving(true)

    await supabase.from('locataires').update({
      type_bail_id:    typeBailId,
      date_signature:  dateSignature,
      indice_reference: indiceRef ? parseFloat(indiceRef) : null,
      indice_nom:      indiceNom || null,
    }).eq('id', selected)

    // Générer les échéances automatiquement
    const typeBail = typesBail.find(t => t.id === typeBailId)
    const locataire = locatairesActifs.find(l => l.id === selected)
    if (typeBail && locataire) {
      const lFull = { ...locataire, date_signature: dateSignature, indice_reference: indiceRef, indice_nom: indiceNom }
      await generateEcheancesForLocataire(lFull, typeBail, userId, lang)
    }

    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 3000)
    if (onGenerated) onGenerated()
  }

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">🔗 {t(lang,'assigner_bail')}</span></div>
      <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontSize: 13, color: '#888', background: '#f0faf5', border: '1px solid #9FE1CB', borderRadius: 8, padding: '10px 14px' }}>
          {t(lang,'assigner_info')}
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label>{t(lang,'locataire_req')}</label>
            <select value={selected} onChange={e => setSelected(e.target.value)}>
              <option value="">{t(lang,'selectionner')}</option>
              {locatairesActifs.map(l => <option key={l.id} value={l.id}>{l.prenom} {l.nom}{l.type_bail_id ? ' ✓' : ''}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t(lang,'type_bail')} *</label>
            <select value={typeBailId} onChange={e => { setTypeBailId(e.target.value); const t = typesBail.find(tb => tb.id === e.target.value); if (t) setIndiceNom(t.indexation_indice || '') }}>
              <option value="">{t(lang,'selectionner')}</option>
              {typesBail.map(tb => <option key={tb.id} value={tb.id}>{tb.nom}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t(lang,'date_sign')} *</label>
            <input type="date" value={dateSignature} onChange={e => setDateSignature(e.target.value)} />
          </div>
          <div className="form-group">
            <label>{t(lang,'indice_ref')}</label>
            <input type="number" placeholder={t(lang,'ph_indice_ref')} value={indiceRef} onChange={e => setIndiceRef(e.target.value)} />
          </div>
          <div className="form-group full">
            <label>{t(lang,'nom_indice')}</label>
            <input placeholder={t(lang,'ph_indice_nom')} value={indiceNom} onChange={e => setIndiceNom(e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
          {saved && <span style={{ fontSize: 13, color: '#1D9E75', fontWeight: 600 }}>✅ {t(lang,'bail_assigne')}</span>}
          <button className="btn btn-primary" onClick={handleAssign} disabled={saving || !selected || !typeBailId || !dateSignature}>
            {saving ? t(lang,'traitement_en_cours') : t(lang,'assigner_btn')}
          </button>
        </div>
      </div>
    </div>
  )
}

function formatAlerte(alerte, lang) {
  const d = alerte.data
  if (!d) return alerte.message // fallback pour anciennes alertes

  const localeMap = { fr: 'fr-FR', en: 'en-GB', nl: 'nl-BE' }
  const dateLabel = new Date(d.date_echeance).toLocaleDateString(
    localeMap[lang] || 'fr-FR',
    { day: 'numeric', month: 'long', year: 'numeric' }
  )

  if (lang === 'en') {
    return d.type === 'renouvellement'
      ? `The lease for ${d.loc_nom} (${d.adresse}) expires on ${dateLabel}. ${d.diff_days} days left to act.`
      : `The annual rent indexation for ${d.loc_nom} (${d.adresse}) is scheduled on ${dateLabel}. ${d.diff_days} days left.`
  }
  if (lang === 'nl') {
    return d.type === 'renouvellement'
      ? `Het huurcontract van ${d.loc_nom} (${d.adresse}) vervalt op ${dateLabel}. Nog ${d.diff_days} dagen om actie te ondernemen.`
      : `De jaarlijkse huurindexatie van ${d.loc_nom} (${d.adresse}) is gepland op ${dateLabel}. Nog ${d.diff_days} dagen.`
  }
  // fr par défaut
  return d.type === 'renouvellement'
    ? `Le bail de ${d.loc_nom} (${d.adresse}) arrive à échéance le ${dateLabel}. Il reste ${d.diff_days} jours pour agir.`
    : `L'indexation annuelle du loyer de ${d.loc_nom} (${d.adresse}) est prévue le ${dateLabel}. Il reste ${d.diff_days} jours.`
}

// ─── ÉCHÉANCES ────────────────────────────────────────────────
function EcheancesPage({ userId, locataires, lang = 'fr' }) {
  const [confirmTraitee, setConfirmTraitee] = useState(null)
  const [echeances, setEcheances]   = useState([])
  const [alertes, setAlertes]       = useState([])
  const [loading, setLoading]       = useState(true)
  const [showForm, setShowForm]     = useState(false)
  const [editEch, setEditEch]       = useState(null)
  const [form, setForm]             = useState({ locataire_id: '', type: 'renouvellement', date_echeance: '', rappel_jours: 30, note: '' })
  const [saving, setSaving]         = useState(false)
  const [filtre, setFiltre]         = useState('actives')
  const [moisCal, setMoisCal] = useState(new Date())
  const [calDetail, setCalDetail] = useState(null)

  const locatairesActifs = locataires.filter(l => !l.date_sortie)

  const loadData = useCallback(async () => {
    const [{ data: e }, { data: a }] = await Promise.all([
      supabase.from('echeances')
        .select('*, locataires(prenom, nom, bien_id, biens(adresse))')
        .eq('user_id', userId)
        .order('date_echeance', { ascending: true }),
      supabase.from('alertes')
        .select('*')
        .eq('user_id', userId)
        .eq('lu', false)
    ])
    setEcheances(e || [])
    setAlertes(a || [])
    setLoading(false)
  }, [userId])

  useEffect(() => { loadData() }, [loadData])

  const getDiffDays = (dateStr) => {
    const date = new Date(dateStr)
    const now  = new Date()
    return Math.ceil((date - now) / (1000 * 60 * 60 * 24))
  }

  const getDaysBadge = (diffDays, traitee) => {
    if (traitee) return { cls: 'past', label: t(lang,'traitee_lbl') }
    if (diffDays < 0)  return { cls: 'past',     label: t(lang,'depassee_lbl') }
    if (diffDays <= 7)  return { cls: 'critique', label: `J-${diffDays}` }
    if (diffDays <= 30) return { cls: 'urgent',   label: `J-${diffDays}` }
    if (diffDays <= 60) return { cls: 'soon',     label: `J-${diffDays}` }
    return { cls: 'ok', label: `J-${diffDays}` }
  }

  const getCardClass = (diffDays, traitee) => {
    if (traitee) return 'traitee'
    if (diffDays <= 7)  return 'critique'
    if (diffDays <= 30) return 'urgent'
    return ''
  }

  const handleSave = async () => {
    if (!form.locataire_id || !form.date_echeance) return
    setSaving(true)
    if (editEch) {
      await supabase.from('echeances').update({ type: form.type, date_echeance: form.date_echeance, rappel_jours: parseInt(form.rappel_jours), note: form.note || null }).eq('id', editEch.id)
    } else {
      await supabase.from('echeances').insert({ user_id: userId, locataire_id: form.locataire_id, type: form.type, date_echeance: form.date_echeance, rappel_jours: parseInt(form.rappel_jours), note: form.note || null })
    }
    setSaving(false)
    setShowForm(false)
    setEditEch(null)
    setForm({ locataire_id: '', type: 'renouvellement', date_echeance: '', rappel_jours: 30, note: '' })
    loadData()
  }

  const handleMarquerTraitee = async (ech) => {
  await supabase.from('echeances').update({ traitee: true }).eq('id', ech.id)
  await supabase.from('alertes').update({ lu: true }).eq('user_id', userId)
  setConfirmTraitee(ech)
  loadData()
}

const handleGenererSuivante = async (ech) => {
  setConfirmTraitee(null)

  // Étape 1 : récupérer le locataire
  const { data: loc } = await supabase
    .from('locataires')
    .select('type_bail_id')
    .eq('id', ech.locataire_id)
    .single()

 
  if (!loc?.type_bail_id) {
    alert(t(lang,'ech_no_bail'))
    loadData()
    return
  }

  // Étape 2 : récupérer le type de bail séparément
  const { data: typeBail } = await supabase
    .from('types_bail')
    .select('*')
    .eq('id', loc.type_bail_id)
    .single()

   if (!typeBail) {
    alert(t(lang,'ech_bail_introuvable'))
    loadData()
    return
  }

  const dateActuelle = new Date(ech.date_echeance)
  const prochaineDate = new Date(dateActuelle)
  if (ech.type === 'renouvellement') {
    prochaineDate.setMonth(prochaineDate.getMonth() + typeBail.duree_mois)
  } else {
    prochaineDate.setMonth(prochaineDate.getMonth() + typeBail.indexation_mois)
  }

  await supabase.from('echeances').insert({
    user_id:       userId,
    locataire_id:  ech.locataire_id,
    type:          ech.type,
    date_echeance: prochaineDate.toISOString().split('T')[0],
    rappel_jours:  ech.type === 'renouvellement'
      ? typeBail.rappel_renouvellement_jours
      : typeBail.rappel_indexation_jours,
    note:          ech.note,
    traitee:       false,
  })

  await loadData()
}

  const handleDelete = async (id) => {
    if (!window.confirm(t(lang,'suppr_ech_confirm'))) return
    await supabase.from('echeances').delete().eq('id', id)
    loadData()
  }

  const handleMarquerAlertesLues = async () => {
    await supabase.from('alertes').update({ lu: true }).eq('user_id', userId)
    loadData()
  }

  const echeancesFiltrees = echeances.filter(e => {
    if (filtre === 'actives') return !e.traitee
    if (filtre === 'traitees') return e.traitee
    return true
  })

  if (loading) return <div className="empty">{t(lang,'chargement')}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {confirmTraitee && (
      <div className="modal-overlay" onClick={() => setConfirmTraitee(null)}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div className="modal-title">✅ {t(lang,'traite_btn')}</div>
          <div className="modal-confirm-text">
            {t(lang,'generer_suivante_msg').replace('{type}', confirmTraitee.type)}
          </div>
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={() => setConfirmTraitee(null)}>{t(lang,'non')}</button>
            <button className="btn btn-primary" onClick={() => handleGenererSuivante(confirmTraitee)}>{t(lang,'oui_generer')}</button>
          </div>
        </div>
      </div>
       )}
      <div className="page-header-row">
        <div>
          <div className="page-title">
            {t(lang,'ech_titre')}
            {alertes.length > 0 && <span className="alerte-badge">{alertes.length}</span>}
          </div>
          <div className="page-sub">{t(lang,'ech_sub')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {alertes.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={handleMarquerAlertesLues}>
              {t(lang,'mark_lues')}
            </button>
          )}
          <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setEditEch(null); setForm({ locataire_id: '', type: 'renouvellement', date_echeance: '', rappel_jours: 30, note: '' }) }}>
            {showForm ? t(lang,'annuler') : t(lang,'ajouter_ech')}
          </button>
        </div>
      </div>

      {/* Alertes non lues */}
      {alertes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {alertes.map(a => (
            <div key={a.id} style={{ background: 'linear-gradient(135deg,#fff8e1,#fff3cd)', border: '1.5px solid #f5c842', borderRadius: 12, padding: '14px 18px', fontSize: 13, color: '#7a5c00', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <span style={{ flex: 1 }}>{formatAlerte(a, lang)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Formulaire ajout/modif */}
      {showForm && (
        <div className="form-card orange">
          <h3>📅 {editEch ? t(lang,'modifier_e') : t(lang,'ajouter_e')}</h3>
          <div className="form-grid">
            {!editEch && (
              <div className="form-group full">
                <label>{t(lang,'locataire_req')}</label>
                <select value={form.locataire_id} onChange={e => setForm({ ...form, locataire_id: e.target.value })}>
                  <option value="">{t(lang,'selectionner')}</option>
                  {locatairesActifs.map(l => <option key={l.id} value={l.id}>{l.prenom} {l.nom}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>{t(lang,'type_req')} *</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option value="renouvellement">🔄 {t(lang,'renouvellement')}</option>
                <option value="indexation">📈 {t(lang,'indexation')}</option>
              </select>
            </div>
            <div className="form-group">
              <label>{t(lang,'date_ech_req')}</label>
              <input type="date" value={form.date_echeance} onChange={e => setForm({ ...form, date_echeance: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t(lang,'rappel_av')}</label>
              <select value={form.rappel_jours} onChange={e => setForm({ ...form, rappel_jours: e.target.value })}>
                <option value={7}>{t(lang,'sept_j_avant')}</option>
                <option value={14}>{t(lang,'quatorze_j_avant')}</option>
                <option value={30}>{t(lang,'un_mois_avant')}</option>
                <option value={60}>{t(lang,'deux_mois_avant')}</option>
                <option value={90}>{t(lang,'trois_mois_avant')}</option>
              </select>
            </div>
            <div className="form-group full">
              <label>{t(lang,'note_opt')}</label>
              <input placeholder={t(lang,'ph_note_ech')} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" style={{ background: '#854F0B', borderColor: '#854F0B' }} onClick={handleSave} disabled={saving}>
              {saving ? t(lang,'enreg_en_cours') : t(lang,'enregistrer')}
            </button>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="tabs">
      <button className={`tab ${filtre === 'actives' ? 'active' : ''}`} onClick={() => setFiltre('actives')}>
        {t(lang,'actives')} ({echeances.filter(e => !e.traitee).length})
      </button>
      <button className={`tab ${filtre === 'traitees' ? 'active' : ''}`} onClick={() => setFiltre('traitees')}>
        {t(lang,'traitees')} ({echeances.filter(e => e.traitee).length})
      </button>
      <button className={`tab ${filtre === 'toutes' ? 'active' : ''}`} onClick={() => setFiltre('toutes')}>
        {t(lang,'toutes')} ({echeances.length})
      </button>
      <button className={`tab ${filtre === 'calendrier' ? 'active' : ''}`} onClick={() => setFiltre('calendrier')}>
        {t(lang,'dash_calendrier')}
      </button>
    </div>

      {/* Liste */}
      {filtre !== 'calendrier' && (
        echeancesFiltrees.length === 0
        ? <div className="card"><div className="empty">{t(lang,'aucune_ech')} {filtre === 'actives' ? t(lang,'active_lbl') : ''}.</div></div>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {echeancesFiltrees.map(e => {
              const diffDays  = getDiffDays(e.date_echeance)
              const badge     = getDaysBadge(diffDays, e.traitee)
              const cardClass = getCardClass(diffDays, e.traitee)
              const dateLabel = new Date(e.date_echeance).toLocaleDateString(_langLocale || 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
              return (
                <div key={e.id} className={`ech-card ${cardClass}`}>
                  <div className={`ech-icon ${e.type}`}>
                    {e.type === 'renouvellement' ? '🔄' : '📈'}
                  </div>
                  <div className="ech-info">
                    <div className="ech-title">
                      {e.type === 'renouvellement' ? t(lang,'renouvellement') : t(lang,'indexation')} — {e.locataires?.prenom} {e.locataires?.nom}
                    </div>
                    <div className="ech-meta">
                      <span>{e.locataires?.biens?.adresse?.split(',')[0]}</span>
                      <span>·</span>
                      <span>{dateLabel}</span>
                      {e.note && <><span>·</span><span style={{ fontStyle: 'italic' }}>{e.note}</span></>}
                    </div>
                  </div>
                  <span className={`ech-days ${badge.cls}`}>{badge.label}</span>
                  <div className="row-actions">
                    {!e.traitee && (
                      <>
                        <button className="btn btn-sm btn-ghost" onClick={() => { setEditEch(e); setForm({ locataire_id: e.locataire_id, type: e.type, date_echeance: e.date_echeance, rappel_jours: e.rappel_jours, note: e.note || '' }); setShowForm(true) }}>✏️</button>
                        <button className="btn btn-sm btn-primary" onClick={() => handleMarquerTraitee(e)}>{t(lang,'traite_btn')}</button>
                      </>
                    )}
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(e.id)}>🗑️</button>
                  </div>
                </div>
              )
            })}
          </div>
        )
      )}

        {calDetail && (
          <div className="modal-overlay" onClick={() => setCalDetail(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-title">
                {calDetail.type === 'renouvellement' ? `🔄 ${t(lang,'renouvellement')}` : `📈 ${t(lang,'indexation')}`}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14 }}>
                <div style={{ background: '#f0faf5', borderRadius: 10, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div><strong>t(lang,'locataire_lbl')</strong> {calDetail.locataires?.prenom} {calDetail.locataires?.nom}</div>
                  <div><strong>t(lang,'bien_lbl')</strong> {calDetail.locataires?.biens?.adresse?.split(',')[0] || '—'}</div>
                  <div><strong>t(lang,'date_lbl')</strong> {fmtDate(calDetail.date_echeance, { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                  {calDetail.note && <div><strong>t(lang,'note_lbl')</strong> {calDetail.note}</div>}
                  <div><strong>t(lang,'statut') :</strong> {calDetail.traitee ? `✓ ${t(lang,'traitee_lbl')}` : `⏳ ${t(lang,'att_tab')}`}</div>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={() => setCalDetail(null)}>Fermer</button>
                {!calDetail.traitee && (
                  <button className="btn btn-primary" onClick={async () => {
                    await handleMarquerTraitee(calDetail)
                    setCalDetail(null)
                  }}>
                    ✓ Marquer traitée
                  </button>
                )}
                <button className="btn btn-ghost" onClick={() => {
                  setCalDetail(null)
                  setEditEch(calDetail)
                  setForm({ locataire_id: calDetail.locataire_id, type: calDetail.type, date_echeance: calDetail.date_echeance, rappel_jours: calDetail.rappel_jours, note: calDetail.note || '' })
                  setShowForm(true)
                  setFiltre('actives')
                }}>
                  ✏️ Modifier
                </button>
              </div>
            </div>
          </div>
        )}
      {filtre === 'calendrier' && (
        <div className="card">
          {/* Navigation mois */}
          <div className="card-header">
            <button className="btn btn-sm btn-ghost" onClick={() => setMoisCal(m => { const d = new Date(m); d.setMonth(d.getMonth()-1); return d })}>←</button>
            <span className="card-title" style={{ textTransform: 'capitalize' }}>
              {moisCal.toLocaleDateString(_langLocale, { month: 'long', year: 'numeric' })}
            </span>
            <button className="btn btn-sm btn-ghost" onClick={() => setMoisCal(m => { const d = new Date(m); d.setMonth(d.getMonth()+1); return d })}>→</button>
          </div>

          {/* Grille calendrier */}
          <div style={{ padding: 16 }}>
            {/* Entêtes jours */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 8 }}>
              {[t(lang,'lun'),t(lang,'mar'),t(lang,'mer'),t(lang,'jeu'),t(lang,'ven'),t(lang,'sam'),t(lang,'dim')].map(j => (
                <div key={j} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#aaa', padding: '4px 0' }}>{j}</div>
              ))}
            </div>

            {/* Cases du mois */}
            {(() => {
              const annee   = moisCal.getFullYear()
              const mois    = moisCal.getMonth()
              const premier = new Date(annee, mois, 1)
              const dernier = new Date(annee, mois + 1, 0)
              const offsetDebut = (premier.getDay() + 6) % 7
              const cases = []

              // Cases vides début
              for (let i = 0; i < offsetDebut; i++) {
                cases.push(<div key={`e-${i}`} />)
              }

              // Jours du mois
              for (let jour = 1; jour <= dernier.getDate(); jour++) {
                const dateStr    = `${annee}-${String(mois+1).padStart(2,'0')}-${String(jour).padStart(2,'0')}`
                const eventsJour = echeances.filter(e => e.date_echeance === dateStr)
                const isToday    = dateStr === new Date().toISOString().split('T')[0]

                cases.push(
                  <div key={jour} style={{
                    minHeight: 70, border: '1px solid #f0f0f0', borderRadius: 8, padding: '4px 6px',
                    background:  isToday ? '#f0faf5' : '#fff',
                    borderColor: isToday ? '#9FE1CB' : '#f0f0f0',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 400, color: isToday ? '#1D9E75' : '#aaa', marginBottom: 4 }}>
                      {jour}
                    </div>
                    {eventsJour.map(e => (
                      <div key={e.id}
                      onClick={() => setCalDetail(e)}
                      style={{ fontSize: 10, fontWeight: 600, padding: '2px 4px', borderRadius: 4, marginBottom: 2,
                        background: e.traitee ? '#f0f0f0' : e.type === 'renouvellement' ? '#eef5fc' : '#e1f5ee',
                        color: e.traitee ? '#aaa' : e.type === 'renouvellement' ? '#185FA5' : '#0F6E56',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        cursor: 'pointer' }}>
                      {e.type === 'renouvellement' ? '🔄' : '📈'} {e.locataires?.prenom} {e.locataires?.nom?.charAt(0)}.
                    </div>
                    ))}
                  </div>
                )
              }

              return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>{cases}</div>
            })()}
          </div>

          {/* Légende */}
          <div style={{ padding: '0 16px 16px', display: 'flex', gap: 16, fontSize: 12, color: '#888' }}>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#eef5fc', marginRight: 4 }}/>{t(lang,'renouvellement')}</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#e1f5ee', marginRight: 4 }}/>{t(lang,'indexation')}</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#f0f0f0', marginRight: 4 }}/>{t(lang,'traitee_lbl')}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── NOTIFICATIONS PAGE ───────────────────────────────────────

  const DEFAULT_TEMPLATES = {
    fr: {
      generation_sujet: 'Votre loyer de {mois} est disponible',
      generation_corps: 'Bonjour {prenom},\n\nVotre loyer de {montant} € est dû {echeance} pour le bien situé au {adresse}.\n\nCordialement,\nVotre propriétaire',
      rappel_sujet:     'Rappel : votre loyer est dû dans {jours} jours',
      rappel_corps:     'Bonjour {prenom},\n\nVotre loyer de {montant} € pour le bien situé au {adresse} est dû {echeance}.\n\nMerci de procéder au paiement.\n\nCordialement,\nVotre propriétaire',
      relance_sujet:    'Loyer impayé - {mois}',
      relance_corps:    'Bonjour {prenom},\n\nNous constatons que votre loyer de {montant} € pour {mois} n\'a pas encore été reçu (échéance {echeance}).\n\nMerci de régulariser rapidement.\n\nCordialement,\nVotre propriétaire',
    },
    en: {
      generation_sujet: 'Your rent for {month} is available',
      generation_corps: 'Hello {firstname},\n\nYour rent of {amount} € is due on {duedate} for the property at {address}.\n\nBest regards,\nYour landlord',
      rappel_sujet:     'Reminder: your rent is due in {days} days',
      rappel_corps:     'Hello {firstname},\n\nYour rent of {amount} € for the property at {address} is due on {duedate}.\n\nPlease proceed with payment.\n\nBest regards,\nYour landlord',
      relance_sujet:    'Unpaid rent - {month}',
      relance_corps:    'Hello {firstname},\n\nWe notice that your rent of {amount} € for {month} has not yet been received (due {duedate}).\n\nPlease regularise as soon as possible.\n\nBest regards,\nYour landlord',
      },
    nl: {
        generation_sujet: 'Uw huur voor {maand} is beschikbaar',
        generation_corps: 'Beste {voornaam},\n\nUw huur van {bedrag} € is verschuldigd op {vervaldatum} voor het pand gelegen te {adres}.\n\nMet vriendelijke groeten,\nUw verhuurder',
        rappel_sujet:     'Herinnering: uw huur vervalt over {dagen} dagen',
        rappel_corps:     'Beste {voornaam},\n\nUw huur van {bedrag} € voor het pand gelegen te {adres} vervalt op {vervaldatum}.\n\nGelieve de betaling uit te voeren.\n\nMet vriendelijke groeten,\nUw verhuurder',
        relance_sujet:    'Onbetaalde huur - {maand}',
        relance_corps:    'Beste {voornaam},\n\nWij stellen vast dat uw huur van {bedrag} € voor {maand} nog niet ontvangen werd (vervaldatum {vervaldatum}).\n\nGelieve zo snel mogelijk te regulariseren.\n\nMet vriendelijke groeten,\nUw verhuurder',
     },
     }
function NotificationsPage({ userId, lang = 'fr' }) {
  const [settings, setSettings] = useState(null)
  const [loading, setLoading]   = useState(true)
  const [saving, setSaving]     = useState(false)
  const [saved, setSaved]       = useState(false)
  const [editLang, setEditLang] = useState('fr')
  const [onglet, setOnglet]               = useState('templates')
  const [loyersLate, setLoyersLate]       = useState([])
  const [relancesLog, setRelancesLog]     = useState([])
  const [relancing, setRelancing]         = useState(null)
  const [loadingRelances, setLoadingRelances] = useState(false)
  const [filtreLog, setFiltreLog] = useState('tous')

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (data) {
        // Fusionner les templates existants avec les défauts
        const templates = data.templates || {}
        const merged = {}
        for (const l of ['fr', 'en', 'nl']) {
          merged[l] = { ...DEFAULT_TEMPLATES[l], ...(templates[l] || {}) }
        }
        // Compat: si les anciens champs FR existent, les utiliser comme base FR
        if (!templates.fr) {
          merged.fr = {
            generation_sujet: data.generation_sujet || DEFAULT_TEMPLATES.fr.generation_sujet,
            generation_corps: data.generation_corps || DEFAULT_TEMPLATES.fr.generation_corps,
            rappel_sujet:     data.rappel_sujet     || DEFAULT_TEMPLATES.fr.rappel_sujet,
            rappel_corps:     data.rappel_corps     || DEFAULT_TEMPLATES.fr.rappel_corps,
            relance_sujet:    data.relance_sujet    || DEFAULT_TEMPLATES.fr.relance_sujet,
            relance_corps:    data.relance_corps    || DEFAULT_TEMPLATES.fr.relance_corps,
          }
        }
        setSettings({ ...data, templates: merged })
      } else {
        const defaults = {
          user_id:             userId,
          notif_generation:    true,
          notif_rappel:        true,
          notif_relance:       true,
          rappel_jours_avant:  3,
          relance_jours_apres: 3,
          templates:           DEFAULT_TEMPLATES,
        }
        const { data: created } = await supabase
          .from('notification_settings')
          .insert(defaults)
          .select()
          .single()
        setSettings(created || defaults)
      }
      setLoading(false)
    }
    load()
  }, [userId])


  const loadRelances = useCallback(async () => {
  setLoadingRelances(true)
  const [{ data: late }, { data: log }] = await Promise.all([
    supabase.from('paiements')
      .select('*, locataires(prenom, nom, email), biens(adresse)')
      .eq('statut', 'late')
      .order('mois', { ascending: false }),
    supabase.from('notifications_log')
      .select('*')
      .eq('user_id', userId)
      .order('sent_at', { ascending: false })
      .limit(200),
  ])
  setLoyersLate(late || [])
  setRelancesLog(log || [])
  setLoadingRelances(false)
}, [userId])

  useEffect(() => {
    if (onglet === 'relances') loadRelances()
  }, [onglet, loadRelances])

  const handleRelance = async (paiementId) => {
    setRelancing(paiementId)
    try {
      const token = await getToken()
      const res = await fetch('/api/router', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'send-relance', paiementId }),
      })
      if (res.ok) await loadRelances()
      else { const err = await res.json(); alert(t(lang,'erreur_generique') + (err.error || '')) }
    } catch(e) { alert(t(lang,'erreur_reseau')) }
    setRelancing(null)
  }
  const update = (field, value) => setSettings(s => ({ ...s, [field]: value }))
  const updateTemplate = (field, value) => setSettings(s => ({
    ...s,
    templates: {
      ...s.templates,
      [editLang]: { ...s.templates[editLang], [field]: value }
    }
  }))

  const tpl = settings?.templates?.[editLang] || DEFAULT_TEMPLATES[editLang]

  const handleSave = async () => {
    setSaving(true)
    await supabase
      .from('notification_settings')
      .upsert({ ...settings, user_id: userId })
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  if (loading) return <div className="empty">{t(lang,'chargement')}</div>

  const LANG_FLAGS = { fr: '🇫🇷', en: '🇬🇧', nl: '🇧🇪' }
  const LANG_LABELS = { fr: 'Français', en: 'English', nl: 'Nederlands' }
  const vars = VARS_BY_LANG[lang] || VARS_BY_LANG.fr
  const VARS_DISPLAY = Object.values(vars)

  return (
    <div className="notif-page">
      <div>
        <div className="page-title">{t(lang,'notif_titre')}</div>
        <div className="page-sub">{t(lang,'notif_sub')}</div>
      </div>

      {/* ── Onglets ── */}
      <div className="tabs">
        <button className={`tab ${onglet === 'templates' ? 'active' : ''}`} onClick={() => setOnglet('templates')}>
          {t(lang,'templates_titre')}
        </button>
        <button className={`tab ${onglet === 'relances' ? 'active' : ''}`} onClick={() => setOnglet('relances')}>
          {t(lang,'filtre_relances')} 
          {loyersLate.length > 0 && <span className="alerte-badge" style={{ marginLeft: 6 }}>{loyersLate.length}</span>}
        </button>
      </div>

      {/* ── ONGLET TEMPLATES ── */}
      {onglet === 'templates' && (
        <>
          {/* Variables disponibles */}
          <div style={{ background: '#f0faf5', border: '1.5px solid #9FE1CB', borderRadius: 12, padding: '14px 18px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#0F6E56', marginBottom: 8 }}>{t(lang,'vars_dispo')}</div>
            <div className="vars-hint">
              {VARS_DISPLAY.map(v => <span key={v} className="var-chip">{v}</span>)}
            </div>
          </div>

          {/* Toggles globaux */}
          <div className="notif-section">
            <div className="notif-section-header">
              <div className="notif-section-title">⚙️ {t(lang,'notif_activation')}</div>
            </div>
            <div className="notif-section-body" style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 24 }}>
              {[
                ['notif_generation', `📬 ${t(lang,'email_gen')}`],
                ['notif_rappel',     `⏰ ${t(lang,'rappel_ech')}`],
                ['notif_relance',    `🔴 ${t(lang,'relance_imp')}`],
              ].map(([field, label]) => (
                <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button className={`toggle ${settings[field] ? 'on' : ''}`} onClick={() => update(field, !settings[field])} />
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#333' }}>{label}</span>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <div className="notif-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <label style={{ marginBottom: 0 }}>{t(lang,'jours_avant')}</label>
                  <input type="number" min="1" max="14" value={settings.rappel_jours_avant} onChange={e => update('rappel_jours_avant', parseInt(e.target.value))} style={{ width: 60 }} />
                </div>
                <div className="notif-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <label style={{ marginBottom: 0 }}>{t(lang,'jours_apres')}</label>
                  <input type="number" min="1" max="30" value={settings.relance_jours_apres} onChange={e => update('relance_jours_apres', parseInt(e.target.value))} style={{ width: 60 }} />
                </div>
              </div>
            </div>
          </div>

          {/* Sélecteur langue templates */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0d2b1f' }}>✉️ {t(lang, 'notif_templates_titre')}</div>
            <div style={{ fontSize: 12, color: '#888', background: '#f0faf5', border: '1px solid #9FE1CB', borderRadius: 8, padding: '8px 12px' }}>
              ℹ️ {t(lang, 'notif_templates_info')}
            </div>
            <div className="tabs">
              {['fr', 'en', 'nl'].map(l => (
                <button key={l} className={`tab ${editLang === l ? 'active' : ''}`} onClick={() => setEditLang(l)}>
                  {LANG_FLAGS[l]} {LANG_LABELS[l]}
                </button>
              ))}
            </div>
          </div>

          {/* GÉNÉRATION */}
          <div className="notif-section">
            <div className="notif-section-header">
              <div className="notif-section-title">📬 {t(lang,'email_gen')} <span style={{ fontSize: 12, color: '#aaa', fontWeight: 400 }}>— {t(lang,'le_1er_mois')}</span></div>
            </div>
            <div className={`notif-section-body ${!settings.notif_generation ? 'disabled' : ''}`}>
              <div className="notif-row">
                <label>{t(lang,'sujet_loc')}</label>
                <input type="text" value={tpl.generation_sujet} onChange={e => updateTemplate('generation_sujet', e.target.value)} />
              </div>
              <div className="notif-row">
                <label>{t(lang,'corps_loc')}</label>
                <textarea value={tpl.generation_corps} onChange={e => updateTemplate('generation_corps', e.target.value)} />
              </div>
            </div>
          </div>

          {/* RAPPEL */}
          <div className="notif-section">
            <div className="notif-section-header">
              <div className="notif-section-title">⏰ {t(lang,'rappel_ech')}</div>
            </div>
            <div className={`notif-section-body ${!settings.notif_rappel ? 'disabled' : ''}`}>
              <div className="notif-row">
                <label>{t(lang,'sujet_loc')}</label>
                <input type="text" value={tpl.rappel_sujet} onChange={e => updateTemplate('rappel_sujet', e.target.value)} />
              </div>
              <div className="notif-row">
                <label>{t(lang,'corps_loc')}</label>
                <textarea value={tpl.rappel_corps} onChange={e => updateTemplate('rappel_corps', e.target.value)} />
              </div>
            </div>
          </div>

          {/* RELANCE */}
          <div className="notif-section">
            <div className="notif-section-header">
              <div className="notif-section-title">🔴 {t(lang,'relance_imp')}</div>
            </div>
            <div className={`notif-section-body ${!settings.notif_relance ? 'disabled' : ''}`}>
              <div className="notif-row">
                <label>{t(lang,'sujet_loc')}</label>
                <input type="text" value={tpl.relance_sujet} onChange={e => updateTemplate('relance_sujet', e.target.value)} />
              </div>
              <div className="notif-row">
                <label>{t(lang,'corps_loc')}</label>
                <textarea value={tpl.relance_corps} onChange={e => updateTemplate('relance_corps', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="notif-save-bar">
            {saved && <span style={{ fontSize: 13, color: '#1D9E75', fontWeight: 600 }}>✅ {t(lang,'enreg_ok')}</span>}
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? t(lang,'enreg_en_cours') : t(lang,'enreg_notif')}
            </button>
          </div>
        </>
      )}

      {/* ── ONGLET RELANCES ── */}
      {onglet === 'relances' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">{t(lang,'loyers_retard')}</div>
              <div className="stat-value red">{loyersLate.length}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">{t(lang,'relances_envoyees')}</div>
              <div className="stat-value">{relancesLog.length}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">{t(lang,'jamais_relances')}</div>
              <div className="stat-value orange">
                {loyersLate.filter(p => !relancesLog.some(r => r.locataire_id === p.locataire_id)).length}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">🔴 {t(lang,'loyers_retard')}</span>
              <button className="btn btn-sm btn-ghost" onClick={loadRelances}>{t(lang,'actualiser')}</button>
            </div>
            {loadingRelances
              ? <div className="empty">{t(lang,'chargement')}</div>
              : loyersLate.length === 0
                ? <div className="empty">{t(lang,'aucun_retard')}</div>
                : loyersLate.map(p => {
                    const derniereRelance = relancesLog.find(r => r.locataire_id === p.locataire_id)
                    const jamaisRelance   = !derniereRelance
                    const joursDepuis     = derniereRelance
                      ? Math.floor((Date.now() - new Date(derniereRelance.sent_at)) / (1000*60*60*24))
                      : null
                    return (
                      <div className="row" key={p.id}>
                        <div className="row-main">
                          <div className="row-name">
                            {p.locataires?.prenom} {p.locataires?.nom}
                            {jamaisRelance && (
                              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, background: '#faece7', color: '#993C1D', padding: '2px 8px', borderRadius: 20 }}>
                                {t(lang,'jamais_relance_badge')}
                              </span>
                            )}
                          </div>
                          <div className="row-sub">
                            {p.biens?.adresse?.split(',')[0]} · {fmtDate(p.mois, { month: 'long', year: 'numeric' })} · {fmt(p.montant)}
                          </div>
                          {derniereRelance && (
                            <div style={{ fontSize: 11, color: '#aaa', marginTop: 3 }}>
                              {t(lang,'derniere_relance')} {joursDepuis} {t(lang,'jour_s')}{joursDepuis > 1 ? 's' : ''} · {p.locataires?.email}
                            </div>
                          )}
                        </div>
                        <div className="row-actions">
                          {p.locataires?.email ? (
                            <button
                              className="btn btn-sm btn-ghost"
                              style={{ color: '#D85A30', borderColor: '#D85A30' }}
                              disabled={relancing === p.id}
                              onClick={() => handleRelance(p.id)}
                            >
                              {relancing === p.id ? '⏳' : jamaisRelance ? t(lang,'relancer') : t(lang,'relancer_nouveau')}
                            </button>
                          ) : (
                            <span style={{ fontSize: 11, color: '#aaa' }}>{t(lang,'pas_email')}</span>
                          )}
                        </div>
                      </div>
                    )
                  })
            }
          </div>

          {relancesLog.length > 0 && (
  <div className="card">
    <div className="card-header">
      <span className="card-title">📬 {t(lang,'historique')} ({relancesLog.length})</span>
      <div style={{ display: 'flex', gap: 6 }}>
        {['tous', 'relance', 'quittance', 'generation', 'invitation', 'intervention'].map(f => {
          const count = f === 'tous' ? relancesLog.length : relancesLog.filter(r => r.type === f).length
          if (count === 0 && f !== 'tous') return null
          const labels = {
            tous:         t(lang,'filtre_tous'),
            relance:      t(lang,'filtre_relances'),
            quittance:    t(lang,'filtre_quittances'),
            generation:   t(lang,'filtre_generation'),
            invitation:   t(lang,'filtre_invitations'),
            intervention: t(lang,'filtre_interventions'),
          }
          return (
            <button key={f}
              className={`btn btn-sm ${filtreLog === f ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFiltreLog(f)}
            >
              {labels[f]} ({count})
            </button>
          )
        })}
      </div>
    </div>
            {relancesLog.filter(r => filtreLog === 'tous' || r.type === filtreLog).map(r => {
              const icons  = { relance: '🔴', quittance: '📄', generation: '📬', invitation: '📨', intervention: '🔧' }
              return (
                <div className="row" key={r.id}>
                  <div style={{ fontSize: 22, flexShrink: 0 }}>{icons[r.type] || '📧'}</div>
                  <div className="row-main">
                    <div className="row-name">{r.recipient}</div>
                    <div className="row-sub">{r.sujet}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#aaa', flexShrink: 0 }}>
                    {new Date(r.sent_at).toLocaleDateString(_langLocale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
        </div>
      )}
    </div>
  )
}
// ─── AUTH ─────────────────────────────────────────────────────
function AuthPage({ onAuth }) {
  const [mode, setMode]       = useState('login')
  const [email, setEmail]     = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const urlLang = new URLSearchParams(window.location.search).get('lang')
  if (urlLang && ['fr','en','nl'].includes(urlLang)) {
    localStorage.setItem('domavix_lang', urlLang)
  }
  const savedLang = localStorage.getItem('domavix_lang')
  const [lang, setLang] = useState(
    (savedLang && ['fr','en','nl'].includes(savedLang)) ? savedLang : 'fr'
  )
  const [cguAccepted, setCguAccepted] = useState(false)

  const handleSubmit = async () => {
    if (!email || !password) { setMessage({ type: 'error', text: t(lang, 'auth_err_fields') }); return }
    if (password.length < 6) { setMessage({ type: 'error', text: t(lang, 'auth_err_pwd_min') }); return }
    if (mode === 'signup' && !cguAccepted) {
      setMessage({ type: 'error', text: t(lang, 'auth_err_cgu') })
      return
    }
    setLoading(true); setMessage(null)
    if (mode === 'login') {
      const res = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          data: { 
            lang: lang,
            cgu_accepted_at: new Date().toISOString()
          }
        }
      })
      if (res.error) setMessage({ type: 'error', text: res.error.message }); else onAuth(res.data.user)
    } else {
      const res = await supabase.auth.signUp({
        email,
        password,
        options: { data: { lang: lang } }
      })
      if (res.error) {
        setMessage({ type: 'error', text: res.error.message })
      } else {
        await new Promise(resolve => setTimeout(resolve, 1000))
        await supabase.rpc('set_cgu_accepted', {
          user_email: email,
          cgu_ts: new Date().toISOString()
        })
        setMessage({ type: 'success', text: t(lang, 'auth_ok_signup') })
      }
    }
    setLoading(false)
  }

  const handleForgotPassword = async () => {
    if (!email) { setMessage({ type: 'error', text: t(lang, 'auth_err_fields') }); return }
    setLoading(true); setMessage(null)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://domavix.com/app',
    })
    setLoading(false)
    if (error) setMessage({ type: 'error', text: error.message })
    else setMessage({ type: 'success', text: t(lang, 'auth_ok_reset') })
  }

  return (
    <div className="auth-page">
      <div className="auth-left">
      <div>
        <div className="auth-brand-icon">🏠</div>
        <div className="auth-brand-name">Dom<em>avix</em></div>
        <div className="auth-brand-tagline">{t(lang, 'auth_tagline')}</div>
      </div>
      <div className="auth-features">
        <div className="auth-feature">
          <div className="auth-feature-icon">📊</div>
          <div className="auth-feature-text">
            <strong>{t(lang, 'auth_feat1_title')}</strong>
            {t(lang, 'auth_feat1_desc')}
          </div>
        </div>
        <div className="auth-feature">
          <div className="auth-feature-icon">💶</div>
          <div className="auth-feature-text">
            <strong>{t(lang, 'auth_feat2_title')}</strong>
            {t(lang, 'auth_feat2_desc')}
          </div>
        </div>
        <div className="auth-feature">
          <div className="auth-feature-icon">📄</div>
          <div className="auth-feature-text">
            <strong>{t(lang, 'auth_feat3_title')}</strong>
            {t(lang, 'auth_feat3_desc')}
          </div>
        </div>
      </div>
      <div className="auth-footer">© 2026 Domavix</div>
    </div>
      <div className="auth-right">
        <div className="auth-form-wrap">
          {/* Sélecteur de langue */}
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            {LANGS.map(l => (
          <button key={l.code}
            onClick={() => setLang(l.code)}
            style={{
              fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
              cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
              textTransform: 'uppercase', letterSpacing: '0.06em',
              border: `1.5px solid ${lang === l.code ? '#0d2b1f' : '#e0e0e0'}`,
              background: lang === l.code ? '#0d2b1f' : '#fff',
              color: lang === l.code ? '#fff' : '#aaa',
            }}
          >
            {l.code.toUpperCase()}
          </button>
          ))}
          </div>

          <div>
            <div className="auth-form-title">
              {mode === 'login' ? t(lang, 'auth_titre_login') : mode === 'signup' ? t(lang, 'auth_titre_signup') : '🔑 Reset'}
            </div>
            <div className="auth-form-sub">
              {mode === 'login' ? t(lang, 'auth_sub_login') : mode === 'signup' ? t(lang, 'auth_sub_signup') : ''}
            </div>
          </div>

          {message && <div className={`auth-msg ${message.type}`}>{message.text}</div>}

          <div className="auth-fields">
            <div className="auth-field">
              <label>{t(lang, 'auth_email')}</label>
              <input type="email" placeholder="ton@email.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
            </div>
            {mode !== 'forgot' && (
              <div className="auth-field">
                <label>{t(lang, 'auth_pwd')}</label>
                <input type="password" placeholder={t(lang, 'auth_pwd_min')} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
              </div>
            )}
            {mode === 'login'  && <button className="auth-submit" onClick={handleSubmit} disabled={loading}>{loading ? '...' : t(lang, 'auth_login_btn')}</button>}
            {mode === 'signup' && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '4px 0 12px' }}>
                  <input
                    type="checkbox"
                    id="cgu-check"
                    checked={cguAccepted}
                    onChange={e => setCguAccepted(e.target.checked)}
                    style={{ marginTop: 3, cursor: 'pointer', accentColor: '#1D9E75', flexShrink: 0 }}
                  />
                  <label htmlFor="cgu-check" style={{ fontSize: 12, color: '#888', lineHeight: 1.6, cursor: 'pointer' }}>
                    {t(lang, 'auth_cgu_label')}{' '}
                    <a href="/legal.html#cgu" target="_blank" rel="noreferrer" style={{ color: '#1D9E75' }}>
                      {t(lang, 'auth_cgu_link')}
                    </a>
                    {' '}{t(lang, 'auth_cgu_et')}{' '}
                    <a href="/legal.html#rgpd" target="_blank" rel="noreferrer" style={{ color: '#1D9E75' }}>
                      {t(lang, 'auth_cgu_rgpd_link')}
                    </a>
                  </label>
                </div>
              )}
            {mode === 'signup' && <button className="auth-submit" onClick={handleSubmit} disabled={loading || !cguAccepted}>{loading ? '...' : t(lang, 'auth_signup_btn')}</button>}
            {mode === 'forgot' && <button className="auth-submit" onClick={handleForgotPassword} disabled={loading}>{loading ? '...' : t(lang, 'auth_reset_btn')}</button>}
          </div>

          <div className="auth-switch">
            {mode === 'login' && <>
              <button onClick={() => { setMode('forgot'); setMessage(null) }}>{t(lang, 'auth_forgot')}</button>
              {' · '}
              {t(lang, 'auth_no_account')} <button onClick={() => { setMode('signup'); setMessage(null) }}>{t(lang, 'auth_create')}</button>
            </>}
            {mode === 'signup' && <>
              {t(lang, 'auth_has_account')} <button onClick={() => { setMode('login'); setMessage(null) }}>{t(lang, 'auth_signin')}</button>
            </>}
            {mode === 'forgot' && <>
              <button onClick={() => { setMode('login'); setMessage(null) }}>{t(lang, 'auth_back_login')}</button>
            </>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── PRICING ─────────────────────────────────────────────────
function PricingPage({ userEmail, userId, currentPlan, lang = 'fr', proprio }) {
  const isMobile = useIsMobile()
  const plans = [
  {
    key: 'free',
    name: t(lang, 'plan_free_name'),
    amount: '0',
    period: t(lang, 'plan_free_period'),
    desc: t(lang, 'plan_free_desc'),
    ctaClass: 'free',
    ctaAction: null,
    features: [
      { label: t(lang, 'feat_1_bien'),      ok: true  },
      { label: t(lang, 'feat_1_loc'),       ok: true  },
      { label: t(lang, 'feat_suivi'),       ok: true  },
      { label: t(lang, 'feat_infos'),       ok: true  },
      { label: t(lang, 'feat_quittances'),  ok: false },
      { label: t(lang, 'feat_docs_lock'),   ok: false },
      { label: t(lang, 'feat_notif'),       ok: false },
      { label: t(lang, 'feat_interventions'), ok: false },
      { label: t(lang, 'feat_ponto'),       ok: false },
      { label: t(lang, 'feat_indexation'),  ok: false },
      { label: t(lang, 'feat_calendrier'),  ok: false },
      { label: t(lang, 'feat_taches_rec'),  ok: false },
      { label: t(lang, 'feat_charges'),     ok: false },
    ],
  },
  {
    key: 'starter',
    name: 'Starter',
    amount: '9,99',
    period: `/${t(lang, 'par_mois')}`,
    desc: t(lang, 'plan_starter_desc'),
    ctaClass: 'starter',
    ctaAction: () => window.open(stripeUrl(STRIPE_STARTER_URL, userId, userEmail), '_blank'),
    features: [
      { label: t(lang, 'feat_4_biens'),       ok: true  },
      { label: t(lang, 'feat_loc_illim'),     ok: true  },
      { label: t(lang, 'feat_suivi'),         ok: true  },
      { label: t(lang, 'feat_infos'),         ok: true  },
      { label: t(lang, 'feat_quittances'),    ok: true  },
      { label: t(lang, 'feat_5_docs'),        ok: true  },
      { label: t(lang, 'feat_notif'),         ok: true  },
      { label: t(lang, 'feat_interventions'), ok: true  },
      { label: t(lang, 'feat_export_xl'),     ok: true  },
      { label: t(lang, 'feat_ponto'),         ok: false },
      { label: t(lang, 'feat_messagerie'),    ok: false },
      { label: t(lang, 'feat_indexation'),  ok: true  },
      { label: t(lang, 'feat_calendrier'),  ok: true  },
      { label: t(lang, 'feat_taches_rec'),  ok: true  },
      { label: t(lang, 'feat_charges'),     ok: false },
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    amount: '19,99',
    period: `/${t(lang, 'par_mois')}`,
    desc: t(lang, 'plan_pro_desc'),
    ctaClass: 'pro',
    popular: true,
    ctaAction: () => window.open(stripeUrl(STRIPE_PRO_URL, userId, userEmail), '_blank'),
    features: [
      { label: t(lang, 'feat_10_biens'),      ok: true },
      { label: t(lang, 'feat_loc_illim'),     ok: true },
      { label: t(lang, 'feat_suivi'),         ok: true },
      { label: t(lang, 'feat_infos'),         ok: true },
      { label: t(lang, 'feat_quittances'),    ok: true },
      { label: t(lang, 'feat_20_docs'),    ok: true },
      { label: t(lang, 'feat_notif'),         ok: true },
      { label: t(lang, 'feat_interventions'), ok: true },
      { label: t(lang, 'feat_export_xl'),     ok: true },
      { label: t(lang, 'feat_ponto'),         ok: true },
      { label: t(lang, 'feat_messagerie'),    ok: true },
      { label: t(lang, 'feat_echeances'),     ok: true },
      { label: t(lang, 'feat_support'),       ok: true },
      { label: t(lang, 'feat_indexation'),  ok: true },
      { label: t(lang, 'feat_calendrier'),  ok: true },
      { label: t(lang, 'feat_taches_rec'),  ok: true },
      { label: t(lang, 'feat_charges'),     ok: true },
    ],
  },
]
  return (
    <div className="pricing-page">
      <div className="pricing-hero">
        <div className="pricing-badge">{t(lang,'prix_badge')}</div>
        <div className="pricing-hero-title">{t(lang,'prix_titre')}</div>
        <div className="pricing-hero-sub">{t(lang,'prix_sub')}</div>
      </div>
      <div className="pricing-grid">
        {plans.map(plan => {
          const isCurrent = plan.key === currentPlan
          return (
            <div key={plan.key} className={`pricing-card ${plan.popular && !isCurrent ? 'popular' : ''} ${isCurrent ? 'current' : ''}`}>
              {plan.popular && !isCurrent && <div className="popular-tag">{t(lang,'populaire')}</div>}
              {isCurrent && <div className="current-tag">✓ {t(lang,'plan_act')}</div>}
              <div>
                <div className="pricing-plan">{plan.name}</div>
                <div className="pricing-price"><span className="pricing-currency">€</span><span className="pricing-amount">{plan.amount}</span><span className="pricing-period">{plan.period}</span></div>
                <div className="pricing-desc">{plan.desc}</div>
              </div>
              <div className="pricing-features">{plan.features.map(f => (<div key={f.label} className="pricing-feature"><div className={`pricing-feature-icon ${f.ok ? '' : 'no'}`}>{f.ok ? '✓' : '✕'}</div><span style={{ color: f.ok ? '#333' : '#bbb' }}>{f.label}</span></div>))}</div>
              {isCurrent
                ? <div className="pricing-cta current-plan">{`✓ ${t(lang,'plan_act')}`}</div>
                : plan.ctaAction
                ? <button className={`pricing-cta ${plan.ctaClass}`} onClick={plan.ctaAction}>{t(lang,'choisir',{plan:plan.name})}</button>
                : <div className="pricing-cta free">{t(lang,'plan_free_name')}</div>
              }
            </div>
          )
        })}
      </div>
      {/* Annulation programmée */}
      {proprio?.plan_cancel_at && (currentPlan === 'starter' || currentPlan === 'pro') && (
        <div style={{ background: '#fff8e1', border: '1.5px solid #f5c842', borderRadius: 12, padding: '14px 20px', fontSize: 13, color: '#7a5c00', textAlign: 'center' }}>
          ⚠️ {t(lang, 'cancel_warning_1')} <strong>{currentPlan}</strong> {t(lang, 'cancel_warning_2')}{' '}
          <strong>...</strong>.
          {t(lang, 'cancel_warning_3')}
        </div>
        )}
      {/* Enterprise */}
      <div style={{ marginTop: 20, background: 'linear-gradient(135deg,#fefcf5,#fff)', border: '1.5px solid #e8d5a3', borderRadius: 20, padding: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9a7700', marginBottom: 8 }}>Enterprise</div>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: '#0d2b1f', marginBottom: 8 }}>{t(lang, 'plan_enterprise_title')}</div>
          <div style={{ fontSize: 14, color: '#888', lineHeight: 1.6 }}>{t(lang, 'plan_enterprise_desc')}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 24, flexWrap: 'wrap' }}>
            {[t(lang,'feat_biens_illim'), t(lang,'feat_20_docs'), t(lang,'feat_export_xl'), t(lang,'feat_support')].map(f => (
            <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#555' }}>
              <span style={{ color: '#c9a84c', fontWeight: 700 }}>✓</span> {f}
            </div>
          ))}
        </div>
        
        <a href={lang === 'en' ? 'https://tally.so/r/Bz00G5' : lang === 'nl' ? 'https://tally.so/r/Bz00G5' : 'https://tally.so/r/Bz00G5'}
        target="_blank"
        rel="noreferrer"
          style={{ background: '#c9a84c', color: '#fff', padding: '14px 28px', borderRadius: 10, fontSize: 14, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', width: isMobile ? '100%' : 'auto',  textAlign: 'center', display: 'block', flexShrink: 0 }}>
          {t(lang, 'plan_enterprise_cta')}
        </a>
      </div>
      <div className="pricing-guarantee">{t(lang,'securise')}</div>
      {(currentPlan === 'starter' || currentPlan === 'pro') && (
      <div style={{ textAlign: 'center' }}>
        
      <button className="btn btn-ghost" onClick={async () => {
        const token = await getToken()
        const res = await fetch('/api/router', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ action: 'create-portal-session', userId }),
        })
        const { url } = await res.json()
        window.open(url, '_blank')
      }}>
        ⚙️ {t(lang, 'gerer_abonnement')} →
      </button>
        <div style={{ fontSize: 12, color: '#aaa', marginTop: 6 }}>{t(lang,'gerer_abonnement_sub')}</div>
      </div>
      )}
       <div className="pricing-faq">
        <div className="pricing-faq-title">{t(lang, 'faq_titre')}</div>
        <div className="faq-item">
          <div className="faq-q">{t(lang, 'faq_q1')}</div>
          <div className="faq-a">{t(lang, 'faq_a1')}</div>
        </div>
        <div className="faq-item">
          <div className="faq-q">{t(lang, 'faq_q2')}</div>
          <div className="faq-a">{t(lang, 'faq_a2')}</div>
        </div>
        <div className="faq-item">
          <div className="faq-q">{t(lang, 'faq_q3')}</div>
          <div className="faq-a">{t(lang, 'faq_a3')}</div>
        </div>
        <div className="faq-item">
          <div className="faq-q">{t(lang, 'faq_q4')}</div>
          <div className="faq-a">{t(lang, 'faq_a4')}</div>
        </div>
      </div>
    </div>
  )
  
}

// ═══════════════════════════════════════════════════════════════
//  IMMEUBLES — gestion des immeubles et répartition des lots
//  Onglet dans la page Biens. Pro+ (décompte charges = Pro)
//  À insérer dans App.js, avant function Biens
// ═══════════════════════════════════════════════════════════════

// Calcule la quote-part normalisée d'un lot selon la clé de répartition.
// Retourne un objet { [bienId]: fraction } où la somme des fractions = 1.
function calculerQuotesParts(lotsImmeuble, cleRepartition) {
  const n = lotsImmeuble.length
  if (n === 0) return {}

  if (cleRepartition === 'egale') {
    const part = 1 / n
    return Object.fromEntries(lotsImmeuble.map(b => [b.id, part]))
  }

  const champ = cleRepartition === 'surface' ? 'surface'
              : cleRepartition === 'habitants' ? 'nb_habitants'
              : 'quotite'
  const total = lotsImmeuble.reduce((s, b) => s + (Number(b[champ]) || 0), 0)
  // Normalisation : chaque part = valeur du lot / total réel (même si total ≠ 1000)
  if (total <= 0) {
    // Pas de valeurs → répartition égale par défaut
    const part = 1 / n
    return Object.fromEntries(lotsImmeuble.map(b => [b.id, part]))
  }
  return Object.fromEntries(lotsImmeuble.map(b => [b.id, (Number(b[champ]) || 0) / total]))
}

// ── Sous-composant : saisie des charges communes d'un immeuble ──
function ChargesCommunes({ immeubleId, userId, cleDefaut = 'quotite', lang = 'fr' }) {
  const anneeActuelle = new Date().getFullYear()
  const [annee, setAnnee]       = useState(anneeActuelle - 1)
  const [factures, setFactures] = useState([])
  const [loading, setLoading]   = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId]     = useState(null)
  const [form, setForm] = useState({ categorie: 'communs', libelle: '', montant: '', date_facture: '', recuperable: true, cle_repartition: cleDefaut })

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('charges_factures')
      .select('*').eq('immeuble_id', immeubleId).eq('annee', annee).order('date_facture', { ascending: true })
    setFactures(data || [])
    setLoading(false)
  }, [immeubleId, annee])

  useEffect(() => { load() }, [load])

  const resetForm = () => {
    setForm({ categorie: 'communs', libelle: '', montant: '', date_facture: '', recuperable: true, cle_repartition: cleDefaut })
    setEditId(null); setShowForm(false)
  }

  const handleSave = async () => {
    if (!form.montant || !form.date_facture) return
    const payload = {
      categorie: form.categorie, libelle: form.libelle || null,
      montant: parseFloat(form.montant), date_facture: form.date_facture,
      annee: new Date(form.date_facture).getFullYear(), recuperable: form.recuperable,
      cle_repartition: form.cle_repartition || null,
    }
    if (editId) {
      await supabase.from('charges_factures').update(payload).eq('id', editId)
    } else {
      await supabase.from('charges_factures').insert({
        user_id: userId, immeuble_id: immeubleId, bien_id: null, location_id: null, ...payload,
      })
    }
    resetForm(); load()
  }

  const handleEdit = (f) => {
    setEditId(f.id)
    setForm({
      categorie: f.categorie, libelle: f.libelle || '', montant: String(f.montant),
      date_facture: f.date_facture, recuperable: f.recuperable,
      cle_repartition: f.cle_repartition || cleDefaut,
    })
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    await supabase.from('charges_factures').delete().eq('id', id)
    load()
  }

  const fmtE = (n) => Number(n).toFixed(2).replace('.', ',')
  const total = factures.filter(f => f.recuperable).reduce((s, f) => s + Number(f.montant), 0)
  const cleLabel = (cle) => t(lang, cle === 'surface' ? 'imm_cle_surface' : cle === 'habitants' ? 'imm_cle_habitants' : cle === 'egale' ? 'imm_cle_egale' : 'imm_cle_quotite')

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed #ddd' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#0d2b1f' }}>🧾 {t(lang,'cc_titre')}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select value={annee} onChange={e => setAnnee(parseInt(e.target.value))}
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}>
            {[anneeActuelle, anneeActuelle-1, anneeActuelle-2, anneeActuelle-3].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {!showForm && <button className="btn btn-sm btn-primary" onClick={() => setShowForm(true)} style={{ fontSize: 12 }}>+ {t(lang,'cc_ajouter')}</button>}
        </div>
      </div>

      {showForm && (
        <div style={{ background: '#fafafa', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <select value={form.categorie} onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, flex: '1 1 140px' }}>
              {CHARGES_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {categorieLabel(c.key, lang)}</option>)}
            </select>
            <input type="number" value={form.montant} onChange={e => setForm(f => ({ ...f, montant: e.target.value }))}
              placeholder={t(lang,'ch_montant')} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, width: 100 }} />
            <input type="date" value={form.date_facture} onChange={e => setForm(f => ({ ...f, date_facture: e.target.value }))}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: '#888' }}>{t(lang,'cc_repartir')}</span>
            <select value={form.cle_repartition} onChange={e => setForm(f => ({ ...f, cle_repartition: e.target.value }))}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, flex: 1 }}>
              <option value="quotite">{t(lang,'imm_cle_quotite')}</option>
              <option value="surface">{t(lang,'imm_cle_surface')}</option>
              <option value="habitants">{t(lang,'imm_cle_habitants')}</option>
              <option value="egale">{t(lang,'imm_cle_egale')}</option>
            </select>
          </div>
          <input value={form.libelle} onChange={e => setForm(f => ({ ...f, libelle: e.target.value }))}
            placeholder={t(lang,'ch_libelle')} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, width: '100%', marginBottom: 8 }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.recuperable} onChange={e => setForm(f => ({ ...f, recuperable: e.target.checked }))} />
            {t(lang,'ch_recuperable')}
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-sm btn-ghost" onClick={resetForm}>{t(lang,'annuler_btn')}</button>
            <button className="btn btn-sm btn-primary" onClick={handleSave}>{editId ? t(lang,'enregistrer') : t(lang,'ajouter')}</button>
          </div>
        </div>
      )}

      {loading ? null : factures.length === 0 ? (
        <div style={{ fontSize: 12, color: '#aaa', padding: '4px 0' }}>{t(lang,'cc_aucune')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {factures.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fafafa', borderRadius: 6, fontSize: 12 }}>
              <span>{categorieIcon(f.categorie)}</span>
              <span style={{ flex: 1, color: '#0d2b1f', minWidth: 0 }}>
                {categorieLabel(f.categorie, lang)}{f.libelle ? ` · ${f.libelle}` : ''}{!f.recuperable && ` · ${t(lang,'ch_non_recup')}`}
                <span style={{ display: 'inline-block', marginLeft: 6, padding: '1px 7px', borderRadius: 10, background: '#eef5fc', color: '#185FA5', fontSize: 10, fontWeight: 600 }}>
                  {cleLabel(f.cle_repartition || cleDefaut)}
                </span>
              </span>
              <span style={{ color: '#888' }}>{new Date(f.date_facture).toLocaleDateString(_langLocale)}</span>
              <span style={{ fontWeight: 600, color: '#0d2b1f' }}>{fmtE(f.montant)} €</span>
              <button onClick={() => handleEdit(f)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#185FA5', fontSize: 12 }}>✏️</button>
              <button onClick={() => handleDelete(f.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#993C1D', fontSize: 12 }}>✕</button>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px 0', fontSize: 13, fontWeight: 700, color: '#0d2b1f' }}>
            <span>{t(lang,'cc_total')}</span>
            <span>{fmtE(total)} €</span>
          </div>
        </div>
      )}
    </div>
  )
}

function ImmeublesView({ userId, biens, onRefresh, lang = 'fr' }) {
  const [immeubles, setImmeubles] = useState([])
  const [loading, setLoading]     = useState(true)
  const [showForm, setShowForm]   = useState(false)
  const [editImm, setEditImm]     = useState(null)
  const [expandedId, setExpandedId] = useState(null)
  const [savedFeedback, setSavedFeedback] = useState(false)
  const [form, setForm] = useState({ nom: '', adresse: '', cle_repartition: 'quotite', format_quotite: 'millieme' })

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('immeubles')
      .select('*').eq('user_id', userId).order('nom', { ascending: true })
    setImmeubles(data || [])
    setLoading(false)
  }, [userId])

  useEffect(() => { load() }, [load])

  // Biens rattachés à un immeuble donné
  const lotsDe = (immeubleId) => biens.filter(b => b.immeuble_id === immeubleId)
  // Biens sans immeuble (disponibles à rattacher)
  const biensLibres = biens.filter(b => !b.immeuble_id)

  const resetForm = () => {
    setForm({ nom: '', adresse: '', cle_repartition: 'quotite', format_quotite: 'millieme' })
    setEditImm(null); setShowForm(false)
  }

  const handleSave = async () => {
    if (!form.nom) return
    const payload = {
      user_id: userId, nom: form.nom, adresse: form.adresse || null,
      cle_repartition: form.cle_repartition, format_quotite: form.format_quotite,
    }
    if (editImm) await supabase.from('immeubles').update(payload).eq('id', editImm.id)
    else         await supabase.from('immeubles').insert(payload)
    resetForm(); load()
  }

  const handleEdit = (imm) => {
    setEditImm(imm)
    setForm({ nom: imm.nom, adresse: imm.adresse || '', cle_repartition: imm.cle_repartition || 'quotite', format_quotite: imm.format_quotite || 'millieme' })
    setShowForm(true)
  }

  const handleDelete = async (imm) => {
    if (!window.confirm(`${imm.nom} — ${t(lang,'supprimer_btn')} ?`)) return
    // Détacher les lots avant de supprimer l'immeuble
    await supabase.from('biens').update({ immeuble_id: null }).eq('immeuble_id', imm.id)
    await supabase.from('immeubles').delete().eq('id', imm.id)
    load(); onRefresh()
  }

  // Rattacher / détacher un bien
  const attacherBien = async (bienId, immeubleId) => {
    await supabase.from('biens').update({ immeuble_id: immeubleId }).eq('id', bienId)
    onRefresh()
  }
  const detacherBien = async (bienId) => {
    await supabase.from('biens').update({ immeuble_id: null, quotite: null }).eq('id', bienId)
    onRefresh()
  }

  // Mise à jour quotité / surface d'un lot
  const updateLot = async (bienId, champ, valeur) => {
    await supabase.from('biens').update({ [champ]: valeur === '' ? null : parseFloat(valeur) }).eq('id', bienId)
    onRefresh()
  }

  if (loading) return <div className="empty" style={{ padding: 40 }}>{t(lang,'chargement')}…</div>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#888' }}>{t(lang,'imm_sous_titre')}</div>
        {!showForm && (
          <button className="btn btn-sm btn-primary" onClick={() => setShowForm(true)}>+ {t(lang,'imm_ajouter')}</button>
        )}
      </div>

      {/* Formulaire immeuble */}
      {showForm && (
        <div className="card" style={{ marginBottom: 16, padding: 20 }}>
          <div className="form-grid">
            <div className="form-group full">
              <label>{t(lang,'imm_nom')} *</label>
              <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder={t(lang,'imm_nom_ph')} />
            </div>
            <div className="form-group full">
              <label>{t(lang,'imm_adresse')}</label>
              <input value={form.adresse} onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t(lang,'imm_cle')}</label>
              <select value={form.cle_repartition} onChange={e => setForm(f => ({ ...f, cle_repartition: e.target.value }))}>
                <option value="quotite">{t(lang,'imm_cle_quotite')}</option>
                <option value="surface">{t(lang,'imm_cle_surface')}</option>
                <option value="egale">{t(lang,'imm_cle_egale')}</option>
              </select>
            </div>
            {form.cle_repartition === 'quotite' && (
              <div className="form-group">
                <label>{t(lang,'imm_format')}</label>
                <select value={form.format_quotite} onChange={e => setForm(f => ({ ...f, format_quotite: e.target.value }))}>
                  <option value="millieme">{t(lang,'imm_millieme')}</option>
                  <option value="pourcent">{t(lang,'imm_pourcent')}</option>
                </select>
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={resetForm}>{t(lang,'annuler_btn')}</button>
            <button className="btn btn-primary" onClick={handleSave}>{t(lang,'enregistrer')}</button>
          </div>
        </div>
      )}

      {/* État vide engageant */}
      {immeubles.length === 0 && !showForm ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🏢</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#0d2b1f', marginBottom: 8 }}>{t(lang,'imm_vide_titre')}</div>
          <div style={{ fontSize: 14, color: '#888', maxWidth: 380, margin: '0 auto 20px' }}>{t(lang,'imm_vide_desc')}</div>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ {t(lang,'imm_ajouter')}</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {immeubles.map(imm => {
            const lots = lotsDe(imm.id)
            const champ = imm.cle_repartition === 'surface' ? 'surface' : 'quotite'
            const totalQuotites = lots.reduce((s, b) => s + (Number(b[champ]) || 0), 0)
            const cibleTotal = imm.format_quotite === 'pourcent' ? 100 : 1000
            const estEgale = imm.cle_repartition === 'egale'
            const totalIncoherent = !estEgale && imm.cle_repartition === 'quotite' && lots.length > 0 && Math.abs(totalQuotites - cibleTotal) > 0.5
            const isOpen = expandedId === imm.id

            return (
              <div key={imm.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* En-tête immeuble */}
                <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', borderBottom: isOpen ? '1px solid #eee' : 'none' }}
                  onClick={() => setExpandedId(isOpen ? null : imm.id)}>
                  <div style={{ fontSize: 22 }}>🏢</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#0d2b1f' }}>{imm.nom}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>
                      {lots.length} {lots.length > 1 ? t(lang,'imm_lots') : t(lang,'imm_lot')}
                      {imm.adresse ? ` · ${imm.adresse}` : ''}
                      {' · '}{t(lang, imm.cle_repartition === 'surface' ? 'imm_cle_surface' : imm.cle_repartition === 'egale' ? 'imm_cle_egale' : 'imm_cle_quotite')}
                    </div>
                  </div>
                  <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); handleEdit(imm) }}>{t(lang,'modifier')}</button>
                  <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); handleDelete(imm) }} style={{ color: '#993C1D' }}>✕</button>
                  <span style={{ fontSize: 14, color: '#aaa' }}>{isOpen ? '▲' : '▼'}</span>
                </div>

                {/* Détail déplié : lots */}
                {isOpen && (
                  <div style={{ padding: '14px 18px' }}>
                    {/* Alerte total incohérent */}
                    {totalIncoherent && (
                      <div style={{ background: '#faeeda', border: '1px solid #F5D08A', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#854F0B', marginBottom: 12 }}>
                        ⚠️ {t(lang,'imm_total_alerte').replace('{total}', totalQuotites).replace('{cible}', cibleTotal)} {t(lang,'imm_normalise')}
                      </div>
                    )}

                    {/* Lots rattachés */}
                    {lots.length === 0 ? (
                      <div style={{ fontSize: 13, color: '#aaa', padding: '8px 0' }}>{t(lang,'imm_aucun_lot')}</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                        {/* En-tête colonnes */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 2px', fontSize: 10, fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          <span style={{ flex: '1 1 120px' }}>{t(lang,'imm_col_bien')}</span>
                          <span style={{ width: 72, textAlign: 'right' }}>‰</span>
                          <span style={{ width: 68, textAlign: 'right' }}>m²</span>
                          <span style={{ width: 54, textAlign: 'right' }}>👤</span>
                          <span style={{ width: 48, textAlign: 'right' }}>{t(lang,'imm_col_part')}</span>
                          <span style={{ width: 13 }}></span>
                        </div>
                        {lots.map(b => {
                          const qp = calculerQuotesParts(lots, imm.cle_repartition)[b.id] || 0
                          return (
                            <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fafafa', borderRadius: 8, flexWrap: 'wrap' }}>
                              <span style={{ flex: '1 1 120px', fontSize: 13, color: '#0d2b1f', minWidth: 0 }}>{b.adresse.split(',')[0]}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <input type="number" defaultValue={b.quotite ?? ''} onBlur={e => updateLot(b.id, 'quotite', e.target.value)} title={t(lang,'imm_cle_quotite')}
                                  style={{ width: 56, padding: '4px 6px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, textAlign: 'right' }} />
                                <span style={{ fontSize: 11, color: '#aaa', width: 16 }}>‰</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <input type="number" defaultValue={b.surface ?? ''} onBlur={e => updateLot(b.id, 'surface', e.target.value)} title={t(lang,'imm_cle_surface')}
                                  style={{ width: 50, padding: '4px 6px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, textAlign: 'right' }} />
                                <span style={{ fontSize: 11, color: '#aaa', width: 18 }}>m²</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <input type="number" defaultValue={b.nb_habitants ?? ''} onBlur={e => updateLot(b.id, 'nb_habitants', e.target.value)} title={t(lang,'imm_cle_habitants')}
                                  style={{ width: 40, padding: '4px 6px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, textAlign: 'right' }} />
                                <span style={{ fontSize: 11, color: '#aaa', width: 14 }}>👤</span>
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#0F6E56', width: 48, textAlign: 'right' }}>{(qp * 100).toFixed(1)}%</span>
                              <button onClick={() => detacherBien(b.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#993C1D', fontSize: 13 }}>✕</button>
                            </div>
                          )
                        })}
                        {/* Bouton Enregistrer rassurant */}
                        {!estEgale && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                            <button className="btn btn-sm" onClick={() => { onRefresh(); setSavedFeedback(true); setTimeout(() => setSavedFeedback(false), 2000) }}
                              style={{ fontSize: 12, background: savedFeedback ? '#e1f5ee' : '#f0f0f0', color: savedFeedback ? '#0F6E56' : '#555' }}>
                              {savedFeedback ? `✓ ${t(lang,'imm_enregistre')}` : `💾 ${t(lang,'imm_enregistrer_quotites')}`}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Rattacher un bien */}
                    {biensLibres.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <select id={`attach-${imm.id}`} defaultValue="" style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
                          <option value="">{t(lang,'imm_rattacher')}…</option>
                          {biensLibres.map(b => <option key={b.id} value={b.id}>{b.adresse.split(',')[0]}</option>)}
                        </select>
                        <button className="btn btn-sm btn-primary" onClick={() => {
                          const sel = document.getElementById(`attach-${imm.id}`)
                          if (sel.value) { attacherBien(sel.value, imm.id); sel.value = '' }
                        }}>{t(lang,'imm_ajouter_lot')}</button>
                      </div>
                    )}
                    {/* Charges communes de l'immeuble */}
                    <ChargesCommunes immeubleId={imm.id} userId={userId} cleDefaut={imm.cle_repartition || 'quotite'} lang={lang} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── BIENS ────────────────────────────────────────────────────
function Biens({ biens, onRefresh, userId, plan, planCustomBiens = null, userEmail, onGoToPricing, lang = 'fr' }) {
  const [showForm, setShowForm]   = useState(false)
  const [editBien, setEditBien]   = useState(null)
  const [deleteBien, setDeleteBien] = useState(null)
  const [form, setForm]   = useState({ adresse: '', type_bien: 'Appartement', loyer: '', charges: '', valeur: '', jour_echeance: 1, pays: 'BE', region: '', peb: '', dpe: '', departement: '' })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [vueImm, setVueImm] = useState('biens')  // 'biens' | 'immeubles'
  const limite = planCustomBiens ?? PLAN_LIMITS[plan]?.biens ?? 1
  const limitAtteinte = biens.length >= limite
  const enDepassement = biens.length > limite

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })
  const handleAdd = async () => {
    if (!form.adresse || !form.loyer) { setMessage({ type: 'error', text: t(lang,'adresse_loyer_req') }) }
    setLoading(true); setMessage(null)
    const { error } = await supabase.from('biens').insert({ adresse: form.adresse, type_bien: form.type_bien, loyer: parseFloat(form.loyer), charges: parseFloat(form.charges) || 0, valeur: form.valeur ? parseFloat(form.valeur) : null, jour_echeance: parseInt(form.jour_echeance) || 1, pays: form.pays, region: form.pays === 'BE' ? (form.region || null) : null, peb: form.pays === 'BE' ? (form.peb || null) : null, dpe: form.pays === 'FR' ? (form.dpe || null) : null, departement: form.pays === 'FR' ? (form.departement || null) : null, user_id: userId })
    setLoading(false)
    if (error) setMessage({ type: 'error', text: 'Erreur : ' + error.message })
    else { setForm({ adresse: '', type_bien: 'Appartement', loyer: '', charges: '', valeur: '', jour_echeance: 1, pays: 'BE', region: '', peb: '', dpe: '', departement: '' }); setShowForm(false); onRefresh() }
  }
  const handleDelete = async () => {
  await supabase.from('biens').update({ deleted_at: new Date().toISOString() }).eq('id', deleteBien.id)
  setDeleteBien(null); onRefresh()
  }

  return (
    <>
      {editBien   && <EditBienModal bien={editBien} userId={userId} onSave={() => { setEditBien(null); onRefresh() }} onCancel={() => setEditBien(null)} lang={lang} />}
      {deleteBien && <ConfirmModal danger message={`${t(lang,'supprimer_btn')} "${deleteBien.adresse.split(',')[0]}" ?`} onConfirm={handleDelete} onCancel={() => setDeleteBien(null)} lang={lang} />}
      <div className="page-header-row">
        <div>
          <div className="page-title">{t(lang,'mes_biens')}</div>
          <div className="page-sub" style={{ marginTop: 4 }}>{limite === Infinity ? t(lang,'illimites') : t(lang,'biens_utilises').replace('{n}', biens.length).replace('{max}', limite)}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ExportButton biens={biens} locataires={[]} paiements={[]} plan={plan} small lang={lang} />
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)} disabled={limitAtteinte && !showForm} style={limitAtteinte && !showForm ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
            {showForm ? t(lang,'annuler') : t(lang,'ajouter_bien')}
          </button>
        </div>
      </div>
      {/* Toggle Biens / Immeubles */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #eee', marginBottom: 16 }}>
        <button className={`dossier-tab ${vueImm === 'biens' ? 'active' : ''}`} onClick={() => setVueImm('biens')}>
          🏠 {t(lang,'mes_biens')}
        </button>
        <button className={`dossier-tab ${vueImm === 'immeubles' ? 'active' : ''}`} onClick={() => setVueImm('immeubles')}>
          🏢 {t(lang,'imm_onglet')}
        </button>
      </div>
      {vueImm === 'immeubles' && (
        <ImmeublesView userId={userId} biens={biens} onRefresh={onRefresh} lang={lang} />
      )}
      {vueImm === 'biens' && (<>
      {enDepassement && (
        <div style={{ background: 'linear-gradient(135deg,#faece7,#fff5f5)', border: '1.5px solid #e57373', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#993C1D', marginBottom: 4 }}>{t(lang,'depass_biens').replace('{n}', biens.length).replace('{max}', limite === 1 ? '1 bien max' : `${limite} biens max`)}</div>
            <div style={{ fontSize: 13, color: '#c0392b', lineHeight: 1.5 }}>{t(lang,'depass_biens_desc')}</div>
          </div>
          <button className="btn btn-sm" style={{ background: '#D85A30', color: '#fff', borderColor: '#D85A30', flexShrink: 0 }} onClick={onGoToPricing}>{t(lang,'voir_plans_btn')}</button>
        </div>
      )}
      {!enDepassement && limitAtteinte && !showForm && <UpgradeBanner plan={plan} resource="biens" userEmail={userEmail} userId={userId} onGoToPricing={onGoToPricing} />}
      {showForm && !limitAtteinte && (
        <div className="form-card green">
          <h3>{t(lang,'ajouter_bien_titre')}</h3>
          {message && <div className={message.type === 'error' ? 'error-msg' : 'success-msg'}>{message.text}</div>}
          <div className="form-grid">
            <div className="form-group full"><label>{t(lang,'adresse_lbl')}</label><input name="adresse" placeholder={t(lang,'ph_adresse')} value={form.adresse} onChange={handleChange} /></div>
            <div className="form-group"><label>{t(lang,'type_lbl')}</label><select name="type_bien" value={form.type_bien} onChange={handleChange}><option>Appartement</option><option>Studio</option><option>Maison</option><option>Kot</option><option>Commerce</option></select></div>
            <div className="form-group"><label>{t(lang,'loyer_eur_lbl')}</label><input name="loyer" type="number" placeholder={t(lang,'ph_loyer')} value={form.loyer} onChange={handleChange} /></div>
            <div className="form-group"><label>{t(lang,'charges_eur_lbl')}</label><input name="charges" type="number" placeholder={t(lang,'ph_charges')} value={form.charges} onChange={handleChange} /></div>
            <div className="form-group"><label>{t(lang,'valeur_eur_lbl')}</label><input name="valeur" type="number" placeholder={t(lang,'ph_valeur')} value={form.valeur} onChange={handleChange} /></div>
            <div className="form-group">
              <label>{t(lang,'jour_ech')}</label>
              <input
              name="jour_echeance"
              type="number"
              min="1"
              max="28"
              value={form.jour_echeance}
              onChange={handleChange}
              placeholder="1"
              />
            </div>
            <div className="form-group">
              <label>{t(lang,'pays_lbl')}</label>
              <select name="pays" value={form.pays} onChange={handleChange}>
                <option value="BE">Belgique</option>
                <option value="FR">France</option>
                <option value="LU">Luxembourg</option>
                <option value="OTHER">{t(lang,'autre_pays')}</option>
              </select>
            </div>
            {form.pays === 'BE' && (
              <>
                <div className="form-group">
                  <label>{t(lang,'region_lbl')}</label>
                  <select name="region" value={form.region} onChange={handleChange}>
                    <option value="">—</option>
                    <option value="wallonie">{t(lang,'region_wallonie')}</option>
                    <option value="bruxelles">{t(lang,'region_bruxelles')}</option>
                    <option value="flandre">{t(lang,'region_flandre')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t(lang,'peb_lbl')}</label>
                  <select name="peb" value={form.peb} onChange={handleChange}>
                    <option value="">—</option>
                    <option value="A+">A+</option><option value="A">A</option>
                    <option value="B">B</option><option value="C">C</option>
                    <option value="D">D</option><option value="E">E</option>
                    <option value="F">F</option><option value="G">G</option>
                  </select>
                </div>
              </>
            )}
            {form.pays === 'FR' && (
              <>
                <div className="form-group">
                  <label>{t(lang,'dpe_lbl')}</label>
                  <select name="dpe" value={form.dpe} onChange={handleChange}>
                    <option value="">—</option>
                    <option value="A">A</option><option value="B">B</option>
                    <option value="C">C</option><option value="D">D</option>
                    <option value="E">E</option><option value="F">F</option>
                    <option value="G">G</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t(lang,'departement_lbl')}</label>
                  <input name="departement" value={form.departement} onChange={handleChange} placeholder={t(lang,'departement_ph')} maxLength={3} />
                </div>
              </>
            )}
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleAdd} disabled={loading}>{loading ? t(lang,'enreg_en_cours') : t(lang,'enregistrer')}</button></div>
        </div>
      )}
      <div className="card">
        <div className="card-header"><span className="card-title">{biens.length} {t(lang,'nb_biens')}</span></div>
        {biens.length === 0 ? <div className="empty">{t(lang,'aucun_bien')}</div> : biens.map(b => (
          <div className="row" key={b.id}>
            <div className="icon-wrap">🏠</div>
            <div className="row-main">
              <div className="row-name">{b.adresse}</div>
              <div className="row-sub">{b.type_bien} · {fmt(b.loyer)}/{t(lang,'par_mois')} <span className="echeance-chip">📅 {t(lang,'le_jour')} {b.jour_echeance || 1}</span></div>
            </div>
            {b.valeur && <div style={{ textAlign: 'right', marginRight: 8 }}><div className="row-sub" style={{ color: '#1D9E75' }}>{t(lang,'rdt')} : {((b.loyer * 12 / b.valeur) * 100).toFixed(1)}%</div></div>}
            <div className="row-actions">
              <button className="btn btn-sm btn-ghost" onClick={() => setEditBien(b)}>{t(lang,'modifier')}</button>
              <button className="btn btn-sm btn-danger" onClick={() => setDeleteBien(b)}>🗑️</button>
            </div>
          </div>
        ))}
      </div>
      </>)}
    </>
  )
}

// ─── LOCATAIRES ───────────────────────────────────────────────
function Locataires({ locataires, biens, onRefresh, plan, planCustomBiens = null, userEmail, userId, proprio, onGoToPricing, lang = 'fr' }) {
  const [onglet, setOnglet]           = useState('actifs')
  const [showForm, setShowForm]       = useState(false)
  const [editLoc, setEditLoc]         = useState(null)
  const [confirmAction, setConfirmAction] = useState(null)
  const [dossierLoc, setDossierLoc]   = useState(null)
  const [typesBail, setTypesBail]     = useState([])
  const [form, setForm]     = useState({ prenom: '', nom: '', email: '', telephone: '', bien_id: '', date_entree: '' })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    supabase.from('types_bail').select('id, nom').eq('user_id', userId).then(({ data }) => setTypesBail(data || []))
  }, [userId])

  const actifs   = useMemo(() => locataires.filter(l => !l.date_sortie), [locataires])
  const inactifs = useMemo(() => locataires.filter(l =>  l.date_sortie), [locataires])
  const liste    = onglet === 'actifs' ? actifs : inactifs

  const limite        = PLAN_LIMITS[plan]?.locataires ?? 1
  const limitAtteinte = actifs.length >= limite
  const enDepassement = actifs.length > limite

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })
  const handleAdd = async () => {
    if (!form.prenom || !form.nom || !form.bien_id || !form.date_entree) { setMessage({ type: 'error', text: t(lang,'champs_obligatoires') }); return }
    setLoading(true); setMessage(null)
    const { error } = await supabase.from('locataires').insert({ prenom: form.prenom, nom: form.nom, email: form.email || null, telephone: form.telephone || null, bien_id: form.bien_id, date_entree: form.date_entree })
    setLoading(false)
    if (error) setMessage({ type: 'error', text: 'Erreur : ' + error.message })
    else { setForm({ prenom: '', nom: '', email: '', telephone: '', bien_id: '', date_entree: '' }); setShowForm(false); onRefresh() }
  }
  const handleConfirm = async () => {
    const { type, loc } = confirmAction
    if (type === 'delete') await supabase.from('locataires').update({ deleted_at: new Date().toISOString() }).eq('id', loc.id)
    else if (type === 'deactivate') await supabase.from('locataires').update({ date_sortie: new Date().toISOString().split('T')[0] }).eq('id', loc.id)
    else if (type === 'reactivate') await supabase.from('locataires').update({ date_sortie: null }).eq('id', loc.id)
    setConfirmAction(null); onRefresh()
  }
  const confirmCopy = {
  delete:     { msg: l => t(lang,'suppr_loc_confirm').replace('{prenom}', l.prenom).replace('{nom}', l.nom), label: t(lang,'supprimer_btn'), danger: true },
  deactivate: { msg: l => t(lang,'desact_loc_confirm').replace('{prenom}', l.prenom).replace('{nom}', l.nom), label: t(lang,'desactiver'), danger: false },
  reactivate: { msg: l => t(lang,'react_loc_confirm').replace('{prenom}', l.prenom).replace('{nom}', l.nom), label: t(lang,'reactiver'), danger: false },
  }

  return (
    <>
      {editLoc && <EditLocataireModal locataire={editLoc} biens={biens} onSave={() => { setEditLoc(null); onRefresh() }} onCancel={() => setEditLoc(null)} lang={lang} />}
      {confirmAction && <ConfirmModal message={confirmCopy[confirmAction.type].msg(confirmAction.loc)} confirmLabel={confirmCopy[confirmAction.type].label} danger={confirmCopy[confirmAction.type].danger} onConfirm={handleConfirm} onCancel={() => setConfirmAction(null)} lang={lang} />}
      {dossierLoc && <DossierPanel locataire={dossierLoc} biens={biens} userId={userId} plan={plan} planCustomBiens={planCustomBiens} proprio={proprio} onClose={() => setDossierLoc(null)} onRefresh={onRefresh} lang={lang} />}
      <div className="page-header-row">
        <div>
          <div className="page-title">{t(lang,'loc_titre')}</div>
          <div className="page-sub" style={{ marginTop: 4 }}>{actifs.length} {t(lang,'actifs')} · {inactifs.length} {t(lang,'inactifs')}{limite !== Infinity && ` · ${t(lang,'limite')} : ${limite}`}</div>
        </div>
        {onglet === 'actifs' && (
          <button className="btn btn-blue" onClick={() => setShowForm(!showForm)} disabled={limitAtteinte && !showForm} style={limitAtteinte && !showForm ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
            {showForm ? t(lang,'annuler') : t(lang,'ajouter_loc')}
          </button>
        )}
      </div>
      <div className="tabs">
        <button className={`tab ${onglet === 'actifs'   ? 'active' : ''}`} onClick={() => { setOnglet('actifs');   setShowForm(false) }}>{t(lang,'actifs_tab')} ({actifs.length})</button>
        <button className={`tab ${onglet === 'inactifs' ? 'active' : ''}`} onClick={() => { setOnglet('inactifs'); setShowForm(false) }}>{t(lang,'inactifs_tab')} ({inactifs.length})</button>
      </div>
      {onglet === 'actifs' && enDepassement && (
        <div style={{ background: 'linear-gradient(135deg,#faece7,#fff5f5)', border: '1.5px solid #e57373', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#993C1D', marginBottom: 4 }}>{t(lang,'depass_locs').replace('{n}', actifs.length).replace('{max}', limite === 1 ? '1 locataire max' : `${limite} max`)}</div>
            <div style={{ fontSize: 13, color: '#c0392b', lineHeight: 1.5 }}>{t(lang,'depass_locs_desc')}</div>
          </div>
          <button className="btn btn-sm" style={{ background: '#D85A30', color: '#fff', borderColor: '#D85A30', flexShrink: 0 }} onClick={onGoToPricing}>{t(lang,'voir_plans_btn')}</button>
        </div>
      )}
      {onglet === 'actifs' && !enDepassement && limitAtteinte && !showForm && <UpgradeBanner plan={plan} resource="locataires" userEmail={userEmail} userId={userId} onGoToPricing={onGoToPricing} />}
      {showForm && onglet === 'actifs' && !limitAtteinte && (
        biens.length === 0 ? <div className="error-msg">{t(lang,'ajouter_bien_first')}</div>
        : <div className="form-card blue">
          <h3>{t(lang,'ajouter_loc_titre')}</h3>
          {message && <div className={message.type === 'error' ? 'error-msg' : 'success-msg'}>{message.text}</div>}
          <div className="form-grid">
            <div className="form-group"><label>{t(lang,'prenom')} *</label><input name="prenom" placeholder={t(lang,'ph_prenom')} value={form.prenom} onChange={handleChange} /></div>
            <div className="form-group"><label>{t(lang,'nom')} *</label><input name="nom" placeholder={t(lang,'ph_nom_loc')} value={form.nom} onChange={handleChange} /></div>
            <div className="form-group"><label>{t(lang,'email')}</label><input name="email" type="email" placeholder={t(lang,'ph_email')} value={form.email} onChange={handleChange} /></div>
            <div className="form-group"><label>{t(lang,'telephone')}</label><input name="telephone" placeholder={t(lang,'ph_telephone')} value={form.telephone} onChange={handleChange} /></div>
            <div className="form-group full"><label>{t(lang,'bien_loue')} *</label><select name="bien_id" value={form.bien_id} onChange={handleChange}><option value="">{t(lang,'selectionner')}</option>{biens.map(b => <option key={b.id} value={b.id}>{b.adresse.split(',')[0]}</option>)}</select></div>
            <div className="form-group"><label>{t(lang,'date_entree')} *</label><input name="date_entree" type="date" value={form.date_entree} onChange={handleChange} /></div>
          </div>
          <div className="form-actions"><button className="btn btn-blue" onClick={handleAdd} disabled={loading}>{loading ? t(lang,'enreg_en_cours') : t(lang,'enregistrer')}</button></div>
        </div>
      )}
      <div className="card">
        <div className="card-header"><span className="card-title">{liste.length} {t(lang,'nb_loc')} {onglet === 'actifs' ? t(lang,'actifs') : t(lang,'inactifs')}</span></div>
        {liste.length === 0 ? <div className="empty">{onglet === 'actifs' ? t(lang,'aucun_loc_act') : t(lang,'aucun_loc_in')}</div>
          : liste.map(l => (
            <div className="row" key={l.id}>
              <div className={`avatar ${onglet === 'inactifs' ? 'inactive' : ''}`}>{l.prenom?.[0]}{l.nom?.[0]}</div>
              <div className="row-main">
                <div className="row-name">{l.prenom} {l.nom}</div>
                <div className="row-sub">
                  {l.email}{l.telephone ? ` · ${l.telephone}` : ''}
                  {onglet === 'inactifs' && l.date_sortie && ` · ${t(lang,'sorti_le')} ${fmtDate(l.date_sortie)}`}
                  {l.type_bail_id && typesBail.find(t => t.id === l.type_bail_id) && (
                    <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: '#eef5fc', color: '#185FA5', display: 'inline-block', marginTop: 2 }}>
                      📋 {typesBail.find(t => t.id === l.type_bail_id)?.nom}
                    </span>
                  )}
                </div>
              </div>
              {onglet === 'actifs' ? <span className="badge badge-paid">{t(lang,'actifs_tab')}</span> : <span className="badge badge-inactive">{t(lang,'inactifs_tab')}</span>}
              <div className="row-actions">
                {onglet === 'actifs' && <button className="btn btn-sm btn-ghost" onClick={() => setEditLoc(l)}>✏️</button>}
                {onglet === 'actifs' && <button className="btn btn-sm btn-ghost" style={{ color: '#854F0B', borderColor: '#F5D08A' }} onClick={() => setConfirmAction({ type: 'deactivate', loc: l })}>{t(lang,'desactiver')}</button>}
                {onglet === 'inactifs' && <button className="btn btn-sm btn-primary" onClick={() => setConfirmAction({ type: 'reactivate', loc: l })}>{t(lang,'reactiver')}</button>}
                <button className="btn btn-sm btn-ghost" style={{ color: '#185FA5', borderColor: '#B5D4F4' }} onClick={() => setDossierLoc(l)}>{t(lang,'dossier')}</button>
                {l.telephone && (
                  <a
                    className="btn btn-sm btn-ghost"
                    style={{ color: '#25D366', borderColor: '#25D366', textDecoration: 'none' }}
                    href={`https://wa.me/${l.telephone.replace(/[\s+\-().]/g, '')}?text=${encodeURIComponent(`Bonjour ${l.prenom}, `)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    WhatsApp
                  </a>
                )}
                <button className="btn btn-sm btn-danger" onClick={() => setConfirmAction({ type: 'delete', loc: l })}>🗑️</button>
              </div>
            </div>
          ))
        }
      </div>
    </>
  )
}

// ─── ONBOARDING ───────────────────────────────────────────────
function OnboardingWelcome({ setPage, plan, lang = 'fr' }) {
  const steps = [
    { icon: '🏠', title: t(lang,'onb_step1_titre'), desc: t(lang,'onb_step1_desc'), page: 'biens', cta: t(lang,'ajouter_bien') },
    { icon: '👤', title: t(lang,'onb_step2_titre'), desc: t(lang,'onb_step2_desc'), page: 'locataires', cta: t(lang,'ajouter_loc') },
    { icon: '💶', title: t(lang,'onb_step3_titre'), desc: t(lang,'onb_step3_desc'), page: 'paiements', cta: t(lang,'voir_loyers') },
  ]
  const isMobile = useIsMobile()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #0d2b1f 0%, #1a4a35 100%)', borderRadius: 20, padding: '36px 32px', color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'rgba(29,158,117,0.15)', top: -80, right: -80, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>👋</div>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, marginBottom: 8 }}>{t(lang,'onb_titre')}</div>
          <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, maxWidth: 480 }}>
            {t(lang,'onb_sub')}
          </div>
          {plan === 'free' && (
            <div style={{ marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(29,158,117,0.2)', border: '1px solid rgba(29,158,117,0.4)', borderRadius: 10, padding: '8px 14px', fontSize: 13, color: '#4de8b4' }}>
              {t(lang,'onb_plan_free')}
            </div>
          )}
        </div>
      </div>

      {/* Étapes */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16 }}>
        {steps.map((s, i) => (
          <div key={i} style={{ background: '#fff', border: '1.5px solid #eee', borderRadius: 16, padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 12, transition: 'box-shadow 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#e8f5ef', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{s.icon}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#1D9E75', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t(lang,'onb_etape').replace('{n}', i + 1)}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, color: '#0d2b1f', marginBottom: 6 }}>{s.title}</div>
              <div style={{ fontSize: 13, color: '#888', lineHeight: 1.5 }}>{s.desc}</div>
            </div>
            <button className="btn btn-primary btn-sm" style={{ marginTop: 'auto', alignSelf: 'flex-start' }} onClick={() => setPage(s.page)}>
              {s.cta}
            </button>
          </div>
        ))}
      </div>

      {/* Tips */}
      <div style={{ background: '#f0faf5', border: '1.5px solid #9FE1CB', borderRadius: 14, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0F6E56' }}>{t(lang,'saviez_vous')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
          {[t(lang,'tip1'), t(lang,'tip2'), t(lang,'tip3'), t(lang,'tip4')].map((tip, i) => (
            <div key={i} style={{ fontSize: 13, color: '#555', background: '#fff', borderRadius: 10, padding: '10px 14px', lineHeight: 1.5 }}>{tip}</div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── DASHBOARD ────────────────────────────────────────────────
function Dashboard({ biens, paiements, locataires, setPage, plan, lang = 'fr', userId }) {
  const [onglet, setOnglet]           = useState('mois')
  const [annee, setAnnee]             = useState(new Date().getFullYear())
  const [interventions, setInterventions] = useState([])
  const [messagesNonLus, setMessagesNonLus] = useState([])
  const [moisCal, setMoisCal] = useState(new Date())
  const [echeancesDash, setEcheancesDash] = useState([])

  useEffect(() => {
    if (!userId) return
    supabase.from('interventions')
      .select('*, locataires(prenom, nom), biens(adresse)')
      .eq('user_id', userId)
      .in('statut', ['nouvelle', 'en_cours'])
      .order('created_at', { ascending: false })
      .limit(5)
      .then(({ data }) => setInterventions(data || []))
    supabase.from('messages')
      .select('*, locataires(prenom, nom)')
      .eq('proprio_user_id', userId)
      .eq('expediteur', 'locataire')
      .eq('lu', false)
      .order('created_at', { ascending: false })
      .limit(5)
      .then(({ data }) => setMessagesNonLus(data || []))
    supabase.from('echeances')
      .select('*, locataires(prenom, nom, bien_id, biens(adresse))')
      .eq('user_id', userId)
      .order('date_echeance', { ascending: true })
      .then(({ data }) => setEcheancesDash(data || []))
  }, [userId])

  // Afficher l'onboarding si aucun bien encore créé
  if (biens.length === 0 && locataires.length === 0) {
    return <OnboardingWelcome setPage={setPage} plan={plan} lang={lang} />
  }

  const now       = new Date()
  const moisLabel = fmtDate(now.toISOString(), { month: 'long', year: 'numeric' })
  const moisKey   = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  const moisPaie  = paiements.filter(p => p.mois?.startsWith(moisKey))
  const actifs    = locataires.filter(l => !l.date_sortie)

  const totalAttendu = moisPaie.reduce((s, p) => s + Number(p.montant), 0)
  // Encaissé = loyers payés + montants partiels reçus
  const totalEncaisse = paiements
    .filter(p => p.mois?.startsWith(moisKey))
    .reduce((s, p) => {
      if (p.statut === 'paid')    return s + Number(p.montant)
      if (p.statut === 'partial') return s + Number(p.montant_paye || 0)
      return s
    }, 0)
  const retards = paiements.filter(p => p.statut === 'late').length

  const MOIS_LABELS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']
  const paieAnnee = paiements.filter(p => p.mois?.startsWith(String(annee)))
  const mensuel = MOIS_LABELS.map((label, i) => {
    const key = `${annee}-${String(i+1).padStart(2,'0')}`
    const mPaie = paiements.filter(p => p.mois?.startsWith(key))
    const encaisse = mPaie.reduce((s,p) => p.statut==='paid' ? s+Number(p.montant) : p.statut==='partial' ? s+Number(p.montant_paye||0) : s, 0)
    const attendu  = mPaie.reduce((s,p) => s+Number(p.montant), 0)
    return { label, encaisse, attendu }
  })
  const totalAnnuel        = mensuel.reduce((s,m) => s+m.encaisse, 0)
  const totalAttenduAnnuel = mensuel.reduce((s,m) => s+m.attendu, 0)
  const tauxAnnuel         = totalAttenduAnnuel ? Math.round(totalAnnuel/totalAttenduAnnuel*100) : 0
  const maxMensuel         = Math.max(...mensuel.map(m => m.encaisse), 1)
  const meilleurMois       = mensuel.reduce((b,m) => m.encaisse > b.encaisse ? m : b, mensuel[0])
  const revenusParBien = biens.map(b => ({
  adresse: b.adresse,
  total: paieAnnee
    .filter(p => String(p.bien_id) === String(b.id))
    .reduce((s, p) => {
      if (p.statut === 'paid')    return s + Number(p.montant)
      if (p.statut === 'partial') return s + Number(p.montant_paye || 0)
      return s
    }, 0)
})).sort((a, b) => b.total - a.total)

  const anneesDispos = [...new Set(paiements.map(p => p.mois?.slice(0,4)).filter(Boolean))].sort().reverse()
  if (!anneesDispos.includes(String(annee))) anneesDispos.unshift(String(annee))

  return (
    <>
      <div className="page-header-row">
        <div><div className="page-title">{t(lang,'tdb_titre')}</div><div className="page-sub">{moisLabel.charAt(0).toUpperCase() + moisLabel.slice(1)}</div></div>
        <ExportButton biens={biens} locataires={locataires} paiements={paiements} plan={plan} lang={lang} />
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <div className="tabs">
          <button className={`tab ${onglet==='mois'?'active':''}`} onClick={() => setOnglet('mois')}>{t(lang,'ce_mois')}</button>
          <button className={`tab ${onglet==='annee'?'active':''}`} onClick={() => setOnglet('annee')}>{t(lang,'annee')}</button>
        </div>
        {onglet==='annee' && (
          <select value={annee} onChange={e => setAnnee(Number(e.target.value))} style={{ padding:'6px 12px', border:'1.5px solid #e0e0e0', borderRadius:8, fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:'none', background:'#fff' }}>
            {anneesDispos.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
      </div>

      {onglet==='mois' && <><div className="stats-grid">
        <div className="stat-card"><div className="stat-label">{t(lang,'loyers_att')}</div><div className="stat-value">{fmt(totalAttendu)}</div><div className="stat-sub">{t(lang,'ce_mois')}</div></div>
        <div className="stat-card"><div className="stat-label">{t(lang,'encaisses')}</div><div className="stat-value green">{fmt(totalEncaisse)}</div><div className="stat-sub">{totalAttendu ? Math.round(totalEncaisse / totalAttendu * 100) : 0}%</div></div>
        <div className="stat-card"><div className="stat-label">{t(lang,'biens_loc')}</div><div className="stat-value">{biens.length} / {actifs.length}</div></div>
        <div className="stat-card"><div className="stat-label">{t(lang,'retards')}</div><div className="stat-value red">{retards}</div><div className="stat-sub">{t(lang,'a_relancer')}</div></div>
      </div>
      <div className="two-col">
        <div className="card">
          <div className="card-header"><span className="card-title">{t(lang,'loyers_mois')}</span><button className="btn btn-sm btn-ghost" onClick={() => setPage('paiements')}>{t(lang,'voir_tout')}</button></div>
          {moisPaie.length === 0 ? <div className="empty">{t(lang,'aucun_paie')}</div>
            : moisPaie.map(p => (
              <div className="row" key={p.id}>
                <div className="row-main"><div className="row-name">{p.locataires?.prenom} {p.locataires?.nom}</div><div className="row-sub">{p.biens?.adresse?.split(',')[0]}</div></div>
                <div className="row-amount">{fmt(p.montant)}</div>
                <Badge statut={p.statut} montant={p.montant} montantPaye={p.montant_paye} lang={lang} />
              </div>
            ))
          }
        </div>
        <div className="card">
          <div className="card-header"><span className="card-title">{t(lang,'loc_actifs')}</span><button className="btn btn-sm btn-ghost" onClick={() => setPage('locataires')}>{t(lang,'voir_tout')}</button></div>
          {actifs.length === 0 ? <div className="empty">{t(lang,'aucun_loc')}</div>
            : actifs.slice(0, 4).map(l => (
              <div className="row" key={l.id}>
                <div className="avatar">{l.prenom?.[0]}{l.nom?.[0]}</div>
                <div className="row-main"><div className="row-name">{l.prenom} {l.nom}</div><div className="row-sub">{l.email || t(lang,'pas_email')}</div></div>
                <span className="badge badge-paid">{t(lang,'actifs_tab')}</span>
              </div>
            ))
          }
        </div>
      </div>

      {/* ── 4 nouveaux widgets ── */}
      <div className="two-col">

        {/* Répartition des statuts — donut SVG */}
        <div className="card">
          <div className="card-header"><span className="card-title">{t(lang,'dash_repartition')}</span></div>
          <div style={{ padding: '20px 22px', display: 'flex', alignItems: 'center', gap: 24 }}>
            {(() => {
              const paid    = paiements.filter(p => p.statut === 'paid').length
              const pending = paiements.filter(p => p.statut === 'pending').length
              const late    = paiements.filter(p => p.statut === 'late').length
              const partial = paiements.filter(p => p.statut === 'partial').length
              const total   = paid + pending + late + partial || 1
              const segments = [
                { label: t(lang,'payes'),     value: paid,    color: '#1D9E75' },
                { label: t(lang,'att_tab'),   value: pending, color: '#F5D08A' },
                { label: t(lang,'retard_tab'),value: late,    color: '#D85A30' },
                { label: t(lang,'partiels'),  value: partial, color: '#6B4FBB' },
              ].filter(s => s.value > 0)

              // SVG donut
              const r = 40, cx = 50, cy = 50, stroke = 18
              let cumul = 0
              const circumference = 2 * Math.PI * r

              return (
                <>
                  <svg width={100} height={100} viewBox="0 0 100 100">
                    <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0f0ee" strokeWidth={stroke} />
                    {segments.map((seg, i) => {
                      const pct    = seg.value / total
                      const dash   = pct * circumference
                      const offset = circumference * (1 - cumul)
                      cumul += pct
                      return (
                        <circle key={i} cx={cx} cy={cy} r={r} fill="none"
                          stroke={seg.color} strokeWidth={stroke}
                          strokeDasharray={`${dash} ${circumference - dash}`}
                          strokeDashoffset={offset}
                          style={{ transform: 'rotate(-90deg)', transformOrigin: '50px 50px' }}
                        />
                      )
                    })}
                    <text x={cx} y={cy+2} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 14, fontWeight: 700, fontFamily: 'DM Serif Display, serif', fill: '#0d2b1f' }}>
                      {total}
                    </text>
                  </svg>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                    {segments.map((seg, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: seg.color, flexShrink: 0, display: 'inline-block' }} />
                        <span style={{ flex: 1, color: '#555' }}>{seg.label}</span>
                        <span style={{ fontWeight: 700, color: '#0d2b1f' }}>{seg.value}</span>
                        <span style={{ color: '#aaa', fontSize: 11 }}>{Math.round(seg.value/total*100)}%</span>
                      </div>
                    ))}
                  </div>
                </>
              )
            })()}
          </div>
        </div>

        {/* Interventions en cours */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">{t(lang,'dash_interventions')}</span>
            <button className="btn btn-sm btn-ghost" onClick={() => setPage('interventions')}>{t(lang,'voir_tout')}</button>
          </div>
          {interventions.length === 0
            ? <div className="empty" style={{ padding: 24 }}>{t(lang,'dash_aucune_interv')}</div>
            : interventions.map(i => (
                <div className="row" key={i.id} style={{ cursor: 'pointer' }} onClick={() => setPage('interventions')}>
                  <div style={{ fontSize: 20 }}>🔧</div>
                  <div className="row-main">
                    <div className="row-name">{i.locataires?.prenom} {i.locataires?.nom}</div>
                    <div className="row-sub">{i.biens?.adresse?.split(',')[0]}</div>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, flexShrink: 0,
                    background: i.statut === 'nouvelle' ? '#faece7' : '#faeeda',
                    color:      i.statut === 'nouvelle' ? '#993C1D' : '#854F0B',
                  }}>
                    {i.statut === 'nouvelle' ? t(lang,'interv_nouvelle') : t(lang,'interv_en_cours')}
                    {i.urgence === 'urgente' && ' 🔴'}
                  </span>
                </div>
              ))
          }
        </div>
      </div>

      <div className="two-col">

        {/* Messages non lus */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">
              {t(lang,'dash_messages_nonlus')}
              {messagesNonLus.length > 0 && <span className="alerte-badge" style={{ marginLeft: 8 }}>{messagesNonLus.length}</span>}
            </span>
            <button className="btn btn-sm btn-ghost" onClick={() => setPage('messagerie')}>{t(lang,'voir_tout')}</button>
          </div>
          {messagesNonLus.length === 0
            ? <div className="empty" style={{ padding: 24 }}>{t(lang,'dash_aucun_msg_nonlu')}</div>
            : messagesNonLus.map(m => (
                <div className="row" key={m.id} style={{ cursor: 'pointer' }} onClick={() => setPage('messagerie')}>
                  <div className="avatar">{m.locataires?.prenom?.[0]}{m.locataires?.nom?.[0]}</div>
                  <div className="row-main">
                    <div className="row-name">{m.locataires?.prenom} {m.locataires?.nom}</div>
                    <div className="row-sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.contenu}</div>
                  </div>
                  <div style={{ fontSize: 11, color: '#aaa', flexShrink: 0 }}>
                    {new Date(m.created_at).toLocaleDateString(_langLocale, { day: 'numeric', month: 'short' })}
                  </div>
                </div>
              ))
          }
        </div>

        {/* Calendrier 30 jours */}
        <div className="card">
          <div className="card-header"><span className="card-title">{t(lang,'dash_loyers_30j')}</span></div>
          <div style={{ padding: '12px 22px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(() => {
              const today = new Date()
              const in30  = new Date(today); in30.setDate(today.getDate() + 30)
              // Grouper par jour d'échéance
              const parJour = {}
              paiements
                .filter(p => p.statut === 'pending' || p.statut === 'late')
                .forEach(p => {
                  const jour = p.biens?.jour_echeance || 1
                  const moisP = new Date(p.mois)
                  const dateEch = new Date(moisP.getFullYear(), moisP.getMonth(), jour)
                  if (dateEch >= today && dateEch <= in30) {
                    const key = dateEch.toISOString().split('T')[0]
                    if (!parJour[key]) parJour[key] = []
                    parJour[key].push(p)
                  }
                })
              const jours = Object.keys(parJour).sort()
              if (jours.length === 0) return <div className="empty" style={{ padding: 16 }}>{t(lang,'dash_aucun_loyer_30j')}</div>
              return jours.map(dateStr => {
                const ps      = parJour[dateStr]
                const total   = ps.reduce((s, p) => s + Number(p.montant), 0)
                const diff    = Math.ceil((new Date(dateStr) - today) / (1000*60*60*24))
                return (
                  <div key={dateStr} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f8f8f8' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: diff <= 3 ? '#faece7' : '#f0faf5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: diff <= 3 ? '#D85A30' : '#1D9E75', flexShrink: 0 }}>
                      {new Date(dateStr).getDate()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: '#aaa' }}>
                        {new Date(dateStr).toLocaleDateString(_langLocale, { weekday: 'short', day: 'numeric', month: 'short' })}
                        {' · '}{ps.length} loyer{ps.length > 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ps.map(p => `${p.locataires?.prenom} ${p.locataires?.nom}`).join(', ')}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0d2b1f', flexShrink: 0 }}>{fmt(total)}</div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: diff <= 3 ? '#faece7' : '#f0faf5', color: diff <= 3 ? '#D85A30' : '#1D9E75', flexShrink: 0 }}>
                      J-{diff}
                    </span>
                  </div>
                )
              })
            })()}
          </div>
        </div>

      </div>
      </>}

      {onglet==='annee' && <>
        <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">{t(lang,'enc_annuel')} {annee}</div>
          <div className="stat-value green">{fmt(totalAnnuel)}</div>
          <div className="stat-sub">{t(lang,'annee_compl')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t(lang,'taux_recouv')}</div>
          <div className={`stat-value ${tauxAnnuel>=90?'green':tauxAnnuel>=70?'orange':'red'}`}>{tauxAnnuel}%</div>
          <div className="stat-sub">{fmt(totalAttenduAnnuel)} {t(lang,'attendus')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t(lang,'meilleur_mois')}</div>
          <div className="stat-value">{meilleurMois.encaisse>0?meilleurMois.label:'—'}</div>
          <div className="stat-sub">{meilleurMois.encaisse>0?fmt(meilleurMois.encaisse):t(lang,'aucun_enc')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t(lang,'moy_mens')}</div>
          <div className="stat-value">{fmt(Math.round(totalAnnuel/12))}</div>
          <div className="stat-sub">{t(lang,'sur_12_mois')}</div>
        </div>
      </div>
        <div className="card">
          <div className="card-header"><span className="card-title">📊 {t(lang,'enc_graph')} {annee}</span></div>
          <div style={{ padding:'24px 22px' }}>
            <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:160, minHeight:160 }}>
              {mensuel.map((m,i) => {
                const h = Math.round((m.encaisse/maxMensuel)*120) || 3
                const isCur = String(annee)===String(now.getFullYear()) && i===now.getMonth()
                return (
                  <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    {m.encaisse>0 && <div style={{ fontSize:9, color:'#888' }}>{(m.encaisse/1000).toFixed(1)}k</div>}
                    <div title={`${m.label} : ${fmt(m.encaisse)}`} style={{ width:'100%', height:h, background:isCur?'#1D9E75':m.encaisse>0?'#9FE1CB':'#f0f0ee', borderRadius:'4px 4px 0 0', minHeight:3 }} />
                  </div>
                )
              })}
            </div>
            <div style={{ display:'flex', gap:6, marginTop:8 }}>
              {mensuel.map((m,i) => {
                const isCur = String(annee)===String(now.getFullYear()) && i===now.getMonth()
                return <div key={i} style={{ flex:1, textAlign:'center', fontSize:10, color:isCur?'#1D9E75':'#aaa', fontWeight:isCur?700:400 }}>{m.label}</div>
              })}
            </div>
            <div style={{ display:'flex', gap:16, marginTop:14, fontSize:12, color:'#888' }}>
            {[['#1D9E75',t(lang,'mois_en_cours')],['#9FE1CB',t(lang,'encaisse_lbl')],['#f0f0ee',t(lang,'vide')]].map(([bg,lbl]) => (
              <span key={lbl}><span style={{ display:'inline-block', width:12, height:12, borderRadius:3, background:bg, marginRight:6, verticalAlign:'middle' }}/>{lbl}</span>
            ))}
           </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><span className="card-title">🏠 {t(lang,'rev_par_bien')} — {annee}</span></div>
          {revenusParBien.every(b=>b.total===0)
            ? <div className="empty">{t(lang,'aucun_enc_ann')} {annee}.</div>
            : revenusParBien.map((b,i) => {
              const pct = Math.round(b.total/(revenusParBien[0].total||1)*100)
              return (
                <div key={i} style={{ padding:'14px 22px', borderBottom:i<revenusParBien.length-1?'1px solid #f8f8f8':'none' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, gap: 8 }}>
                  <div style={{ fontSize:14, fontWeight:500, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{b.adresse.split(',')[0]}</div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#1D9E75', flexShrink:0 }}>{fmt(b.total)}</div>
                </div>
                  <div style={{ height:6, background:'#f0f0ee', borderRadius:3, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${pct}%`, background:'#1D9E75', borderRadius:3 }} />
                  </div>
                </div>
              )
            })
          }
        </div>
        </>}
        {onglet==='calendrier' && (
        <>
          <div className="card">
            <div className="card-header">
              <button className="btn btn-sm btn-ghost" onClick={() => setMoisCal(m => { const d = new Date(m); d.setMonth(d.getMonth()-1); return d })}>←</button>
              <span className="card-title" style={{ textTransform: 'capitalize' }}>
                {moisCal.toLocaleDateString(_langLocale, { month: 'long', year: 'numeric' })}
              </span>
              <button className="btn btn-sm btn-ghost" onClick={() => setMoisCal(m => { const d = new Date(m); d.setMonth(d.getMonth()+1); return d })}>→</button>
            </div>
            <div style={{ padding: 16 }}>
              {/* Entêtes */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 8 }}>
                {[t(lang,'lun'),t(lang,'mar'),t(lang,'mer'),t(lang,'jeu'),t(lang,'ven'),t(lang,'sam'),t(lang,'dim')].map(j => (
                  <div key={j} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#aaa', padding: '4px 0' }}>{j}</div>
                ))}
              </div>
              {/* Grille */}
              {(() => {
                const annee   = moisCal.getFullYear()
                const mois    = moisCal.getMonth()
                const premier = new Date(annee, mois, 1)
                const dernier = new Date(annee, mois + 1, 0)
                const offset  = (premier.getDay() + 6) % 7
                const cases   = []
                for (let i = 0; i < offset; i++) cases.push(<div key={`e-${i}`} />)

                for (let jour = 1; jour <= dernier.getDate(); jour++) {
                  const dateStr = `${annee}-${String(mois+1).padStart(2,'0')}-${String(jour).padStart(2,'0')}`
                  const isToday = dateStr === new Date().toISOString().split('T')[0]

                  // Loyers attendus ce jour
                  const loyersJour = paiements.filter(p => {
                    if (p.statut !== 'pending' && p.statut !== 'late') return false
                    const jourEch = p.biens?.jour_echeance || 1
                    const moisP   = new Date(p.mois)
                    return moisP.getFullYear() === annee && moisP.getMonth() === mois && jourEch === jour
                  })
                  // Échéances ce jour
                  const echJour = echeancesDash.filter(e => e.date_echeance === dateStr)

                  cases.push(
                    <div key={jour} style={{
                      minHeight: 80, border: '1px solid #f0f0f0', borderRadius: 8, padding: '4px 6px',
                      background:  isToday ? '#f0faf5' : '#fff',
                      borderColor: isToday ? '#9FE1CB' : '#f0f0f0',
                    }}>
                      <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 400, color: isToday ? '#1D9E75' : '#aaa', marginBottom: 4 }}>
                        {jour}
                      </div>
                      {loyersJour.map(p => (
                        <div key={p.id}
                          onClick={() => setPage('paiements')}
                          style={{
                            fontSize: 10, fontWeight: 600, padding: '2px 4px', borderRadius: 4, marginBottom: 2,
                            background: p.statut === 'late' ? '#faece7' : '#faeeda',
                            color:      p.statut === 'late' ? '#993C1D' : '#854F0B',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            cursor: 'pointer',
                          }}>
                          💶 {p.locataires?.prenom} {p.locataires?.nom?.charAt(0)}. · {fmt(p.montant)}
                        </div>
                      ))}
                      {echJour.map(e => (
                        <div key={e.id}
                          onClick={() => setPage('echeances')}
                          style={{
                            fontSize: 10, fontWeight: 600, padding: '2px 4px', borderRadius: 4, marginBottom: 2,
                            background: e.traitee ? '#f0f0f0' : e.type === 'renouvellement' ? '#eef5fc' : '#e1f5ee',
                            color:      e.traitee ? '#aaa'    : e.type === 'renouvellement' ? '#185FA5' : '#0F6E56',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            cursor: 'pointer',
                          }}>
                          {e.type === 'renouvellement' ? '🔄' : '📈'} {e.locataires?.prenom} {e.locataires?.nom?.charAt(0)}.
                        </div>
                      ))}
                    </div>
                  )
                }
                return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>{cases}</div>
              })()}
            </div>
            {/* Légende */}
            <div style={{ padding: '0 16px 16px', display: 'flex', gap: 16, fontSize: 12, color: '#888' }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#faeeda', marginRight: 4 }}/>{t(lang,'att_tab')}</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#faece7', marginRight: 4 }}/>{t(lang,'retard_tab')}</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#eef5fc', marginRight: 4 }}/>{t(lang,'renouvellement')}</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#e1f5ee', marginRight: 4 }}/>{t(lang,'indexation')}</span>
            </div>
          </div>

          {/* Résumé du mois sélectionné */}
          <div className="two-col">
            <div className="card">
              <div className="card-header"><span className="card-title">{t(lang,'dash_loyers_att_mois')}</span></div>
              <div style={{ padding: '12px 22px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {paiements.filter(p => {
                  const moisP = new Date(p.mois)
                  return (p.statut === 'pending' || p.statut === 'late') &&
                    moisP.getFullYear() === moisCal.getFullYear() &&
                    moisP.getMonth() === moisCal.getMonth()
                }).length === 0
                  ? <div className="empty" style={{ padding: 12 }}>{t(lang,'dash_tout_encaisse')}</div>
                  : paiements.filter(p => {
                      const moisP = new Date(p.mois)
                      return (p.statut === 'pending' || p.statut === 'late') &&
                        moisP.getFullYear() === moisCal.getFullYear() &&
                        moisP.getMonth() === moisCal.getMonth()
                    }).map(p => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #f8f8f8', fontSize: 13 }}>
                        <div className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                          {p.locataires?.prenom?.[0]}{p.locataires?.nom?.[0]}
                        </div>
                        <div className="row-main">
                          <div className="row-name" style={{ fontSize: 13 }}>{p.locataires?.prenom} {p.locataires?.nom}</div>
                          <div className="row-sub">{p.biens?.adresse?.split(',')[0]}</div>
                        </div>
                        <div style={{ fontWeight: 700, color: '#0d2b1f' }}>{fmt(p.montant)}</div>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: p.statut === 'late' ? '#faece7' : '#faeeda', color: p.statut === 'late' ? '#993C1D' : '#854F0B' }}>
                          {p.statut === 'late' ? t(lang,'retard_tab') : t(lang,'att_tab')}
                        </span>
                      </div>
                    ))
                }
              </div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">{t(lang,'dash_encaisses_mois')}</span></div>
              <div style={{ padding: '12px 22px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {paiements.filter(p => {
                  const moisP = new Date(p.mois)
                  return p.statut === 'paid' &&
                    moisP.getFullYear() === moisCal.getFullYear() &&
                    moisP.getMonth() === moisCal.getMonth()
                }).length === 0
                  ? <div className="empty" style={{ padding: 12 }}>{t(lang,'dash_aucun_encaisse')}</div>
                  : paiements.filter(p => {
                      const moisP = new Date(p.mois)
                      return p.statut === 'paid' &&
                        moisP.getFullYear() === moisCal.getFullYear() &&
                        moisP.getMonth() === moisCal.getMonth()
                    }).map(p => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #f8f8f8' }}>
                        <div className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                          {p.locataires?.prenom?.[0]}{p.locataires?.nom?.[0]}
                        </div>
                        <div className="row-main">
                          <div className="row-name" style={{ fontSize: 13 }}>{p.locataires?.prenom} {p.locataires?.nom}</div>
                          <div className="row-sub">{p.biens?.adresse?.split(',')[0]}</div>
                        </div>
                        <div style={{ fontWeight: 700, color: '#1D9E75' }}>{fmt(p.montant)}</div>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: '#e1f5ee', color: '#0F6E56' }}>✓ {t(lang,'payes')}</span>
                      </div>
                    ))
                }
              </div>
            </div>
          </div>
        </>
      )}
      
    </>
  )
}

// ─── MODAL QUITTANCE ──────────────────────────────────────────
function QuittanceModal({ paiement, locataire, bien, proprio, userEmail, onClose, lang = 'fr', getToken }) {
  const [sending, setSending]   = useState(false)
  const [sent, setSent]         = useState(false)
  const [error, setError]       = useState(null)
  const [autoSend, setAutoSend] = useState(false)

  const moisLabel  = fmtDate(paiement.mois, { month: 'long', year: 'numeric' })
  const datePayStr = paiement.date_paiement ? fmtDate(paiement.date_paiement) : fmtDate()
  const propOk     = !!proprio?.proprio_nom

  const handleDownload = async () => {
    // APRÈS
  const doc = await generateQuittancePDF({ paiement, locataire, bien, proprio: { nom: proprio?.proprio_nom, adresse: proprio?.proprio_adresse, ville: proprio?.proprio_ville, telephone: proprio?.proprio_telephone }, lang: locataire.lang || lang })
    doc.save(`quittance-${locataire.nom}-${moisLabel.replace(' ', '-')}.pdf`)
    if (autoSend && locataire.email) await handleSendEmail(doc)
  }

  const handleSendEmail = async (docArg) => {
    setSending(true); setError(null)
    try {
      const doc = docArg || await generateQuittancePDF({ paiement, locataire, bien, proprio: { nom: proprio?.proprio_nom, adresse: proprio?.proprio_adresse, ville: proprio?.proprio_ville, telephone: proprio?.proprio_telephone},  lang: locataire.lang || lang  })
      const pdfBase64 = doc.output('datauristring').split(',')[1]
      const token = await getToken()
      const res = await fetch('/api/router', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json','Authorization': `Bearer ${token}`, },
        body: JSON.stringify({
          action:           'send-quittance',
          locataireEmail:   locataire.email,
          locataireNom:     `${locataire.prenom} ${locataire.nom}`,
          locataireId:      locataire.id,
           paiementId:       paiement.id,  
          userId:           proprio?.id || null,
          proprioNom:       proprio?.proprio_nom || '',
          proprioAdresse:   proprio?.proprio_adresse || '',
          proprioVille:     proprio?.proprio_ville || '',
          proprioTelephone: proprio?.proprio_telephone || '',
          bienAdresse:      bien.adresse,
          montant:          Number(paiement.montant).toLocaleString(_locale),
          moisLabel,
          datePaiement:     datePayStr,
          pdfBase64,
        }),
      })
      if (!res.ok) throw new Error('Erreur envoi')
      setSent(true)
    } catch (e) {
      setError(t(lang, 'envoi_erreur'))
    }
    setSending(false)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{t(lang,'quittance')}</div>

        {/* Aperçu infos */}
        <div style={{ background: '#f7f7f5', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          <div><strong>{t(lang,'locataire_lbl')} :</strong> {locataire.prenom} {locataire.nom}</div>
          <div><strong>{t(lang,'bien_lbl')} :</strong> {bien.adresse}</div>
          <div><strong>{t(lang,'periode')} :</strong> {moisLabel}</div>
          <div><strong>{t(lang,'montant_lbl')} :</strong> {fmt(paiement.montant)} · {t(lang,'paye_le')} {datePayStr}</div>
        </div>

        {!propOk && (
          <div className="error-msg">{t(lang,'prop_incomplet')}</div>
        )}

        {error && <div className="error-msg">{error}</div>}
        {sent  && <div className="success-msg">{t(lang,'envoi_ok',{email:locataire.email})}</div>}

        {/* Option envoi auto avec téléchargement */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#555' }}>
          <button className={`toggle ${autoSend ? 'on' : ''}`} onClick={() => setAutoSend(!autoSend)} />
          <span>{t(lang,'envoi_aussi')}</span>
        </div>
        {autoSend && !locataire.email && (
          <div className="error-msg">{t(lang,'no_email_loc')}</div>
        )}

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>{t(lang,'fermer')}</button>
          {locataire.email && !sent && (
            <button className="btn btn-blue" onClick={() => handleSendEmail(null)} disabled={sending || !propOk}>
              {sending ? t(lang,'envoi_en_cours') : t(lang,'envoyer_email')}
            </button>
          )}
          <button className="btn btn-primary" onClick={handleDownload} disabled={!propOk}>
            {t(lang,'dl_pdf')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── PAIEMENTS ────────────────────────────────────────────────
function Paiements({ paiements, biens, locataires, onPaiementAjoute, onStatutChange, onPartialChange, proprio, plan, lang = 'fr' }) {
  const [showForm, setShowForm]         = useState(false)
  const [filtre, setFiltre]             = useState('tous')
  const [partialModal, setPartialModal] = useState(null)
  const [quittanceModal, setQuittanceModal] = useState(null)
  const [form, setForm]     = useState({ bien_id: '', locataire_id: '', mois: '', montant: '', charges: '', statut: 'pending' })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [locatairesFiltres, setLocatairesFiltres] = useState([])
  const [relancing, setRelancing] = useState(null)
  const locatairesActifs = useMemo(() => locataires.filter(l => !l.date_sortie), [locataires])
  const paiementsFiltres = filtre === 'tous' ? paiements : paiements.filter(p => p.statut === filtre)

  const totalEncaisse = paiements.reduce((s, p) => {
    if (p.statut === 'paid')    return s + Number(p.montant)
    if (p.statut === 'partial') return s + Number(p.montant_paye || 0)
    return s
  }, 0)
  const totalAttendu = paiements.filter(p => p.statut === 'pending').reduce((s, p) => s + Number(p.montant), 0)
  const totalRetard  = paiements.filter(p => p.statut === 'late').reduce((s, p) => s + Number(p.montant), 0)
  const totalPartiel = paiements.filter(p => p.statut === 'partial').reduce((s, p) => s + (Number(p.montant) - Number(p.montant_paye || 0)), 0)

  const handleChange = (e) => {
    const updated = { ...form, [e.target.name]: e.target.value }
    if (e.target.name === 'bien_id') {
      const f = locatairesActifs.filter(l => l.bien_id === e.target.value)
      setLocatairesFiltres(f); updated.locataire_id = f[0]?.id || ''
      const b = biens.find(b => b.id === e.target.value)
      if (b) {
        const loyerNu = Number(b.loyer || 0)
        const chargesM = Number(b.charges || 0)
        updated.montant = loyerNu + chargesM
        updated.charges = chargesM
      }
    }
    setForm(updated)
  }
  const handleAdd = async () => {
    if (!form.bien_id || !form.locataire_id || !form.mois || !form.montant) { setMessage({ type: 'error', text: t(lang,'champs_paie_req') }); return }
    setLoading(true); setMessage(null)
    const { error } = await supabase.from('paiements').insert({ bien_id: form.bien_id, locataire_id: form.locataire_id, mois: form.mois + '-01', montant: parseFloat(form.montant), charges: parseFloat(form.charges) || 0, montant_paye: null, statut: form.statut, date_paiement: form.statut === 'paid' ? new Date().toISOString().split('T')[0] : null })
    setLoading(false)
    if (error) setMessage({ type: 'error', text: error.message })
    else { setForm({ bien_id: '', locataire_id: '', mois: '', montant: '', charges: '', statut: 'pending' }); setLocatairesFiltres([]); setShowForm(false); onPaiementAjoute() }
  }

  // Ouvre la modal quittance avec toutes les données
  const openQuittance = (p) => {
    const loc  = locataires.find(l => l.id === p.locataire_id)
    const bien = biens.find(b => b.id === p.bien_id)
    setQuittanceModal({ paiement: p, locataire: loc, bien })
  }

  const handleRelance = async (paiementId) => {
  setRelancing(paiementId)
  try {
    const token = await getToken()
    const res = await fetch('/api/router', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'send-relance', paiementId }),
    })
    if (res.ok) alert(t(lang, 'relance_ok'))
    else {
      const err = await res.json()
      alert(t(lang,'erreur_generique') + (err.error || ''))
    }
  } catch(e) { alert(t(lang,'erreur_reseau')) }
  setRelancing(null)
  }

  return (
    <>
      {partialModal && (
        <PartialModal
          paiement={partialModal}
          onConfirm={(montantPaye) => { onPartialChange(partialModal.id, montantPaye); setPartialModal(null) }}
          onCancel={() => setPartialModal(null)}
          lang={lang}
        />
      )}
      {quittanceModal && (
        <QuittanceModal
          paiement={quittanceModal.paiement}
          locataire={quittanceModal.locataire}
          bien={quittanceModal.bien}
          proprio={proprio}
          onClose={() => setQuittanceModal(null)}
          lang={lang}
          getToken={getToken}
        />
      )}

        <div className="page-header-row">
        <div className="page-title">{t(lang,'suivi_loyers')}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ExportButton biens={biens} locataires={locataires} paiements={paiements} plan={plan} small lang={lang} />
          <PontoSyncButton userId={proprio?.id} plan={plan} onSynced={onPaiementAjoute} lang={lang} />
          <button className="btn btn-primary" style={{ background: '#854F0B', borderColor: '#854F0B' }} onClick={() => setShowForm(!showForm)}>
            {showForm ? t(lang,'annuler') : t(lang,'enreg_manuel')}
          </button>
        </div>
      </div>

      {showForm && (
        biens.length === 0 ? <div className="error-msg">{t(lang,'ajouter_bien_first')}</div>
        : <div className="form-card orange">
          <h3>💶 {t(lang,'enreg_paie')}</h3>
          {message && <div className="error-msg">{message.text}</div>}
          <div className="form-grid">
            <div className="form-group full"><label>{t(lang,'bien_loue')} *</label><select name="bien_id" value={form.bien_id} onChange={handleChange}><option value="">{t(lang,'selectionner')}</option>{biens.map(b => <option key={b.id} value={b.id}>{b.adresse.split(',')[0]} ({b.loyer} €/{t(lang,'par_mois')})</option>)}</select></div>
            <div className="form-group full"><label>{t(lang,'locataire_req')}</label><select name="locataire_id" value={form.locataire_id} onChange={handleChange} disabled={!form.bien_id}><option value="">{t(lang,'selectionner')}</option>{locatairesFiltres.map(l => <option key={l.id} value={l.id}>{l.prenom} {l.nom}</option>)}</select></div>
            <div className="form-group"><label>{t(lang,'mois_req')}</label><input name="mois" type="month" value={form.mois} onChange={handleChange} /></div>
            <div className="form-group"><label>{t(lang,'montant_req')}</label><input name="montant" type="number" value={form.montant} onChange={handleChange} /></div>
            <div className="form-group"><label>{t(lang,'dont_charges') || 'Dont charges (€)'}</label><input name="charges" type="number" value={form.charges} onChange={handleChange} /></div>
            <div className="form-group full"><label>{t(lang,'statut')}</label><select name="statut" value={form.statut} onChange={handleChange}><option value="paid">✅ {t(lang,'payes')}</option><option value="pending">⏳ {t(lang,'att_tab')}</option><option value="late">🔴 {t(lang,'retard_tab')}</option></select></div>
          </div>
          <div className="form-actions"><button className="btn btn-primary" style={{ background: '#854F0B', borderColor: '#854F0B' }} onClick={handleAdd} disabled={loading}>{loading ? '...' : t(lang,'enregistrer')}</button></div>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">{t(lang,'enc_total')}</div><div className="stat-value green">{fmt(totalEncaisse)}</div></div>
        <div className="stat-card"><div className="stat-label">{t(lang,'en_attente')}</div><div className="stat-value orange">{fmt(totalAttendu)}</div></div>
        <div className="stat-card"><div className="stat-label">{t(lang,'soldes_part')}</div><div className="stat-value purple">{fmt(totalPartiel)}</div></div>
        <div className="stat-card"><div className="stat-label">{t(lang,'en_retard')}</div><div className="stat-value red">{fmt(totalRetard)}</div></div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">{paiementsFiltres.length} {t(lang,'nb_paie')}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {['tous','paid','partial','pending','late'].map(f => (
              <button key={f} className={`btn btn-sm ${filtre === f ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFiltre(f)}>
                {f === 'tous' ? t(lang,'tous') : f === 'paid' ? t(lang,'payes') : f === 'partial' ? t(lang,'partiels') : f === 'pending' ? t(lang,'att_tab') : t(lang,'retard_tab')}
              </button>
            ))}
          </div>
        </div>

        {paiementsFiltres.length === 0 ? <div className="empty">{t(lang,'aucun_paie2')}</div>
          : paiementsFiltres.map(p => {
            const solde = p.statut === 'partial' ? Number(p.montant) - Number(p.montant_paye || 0) : null
            return (
              <div className="row" key={p.id}>
                <div className="row-main">
                  <div className="row-name">{p.locataires?.prenom} {p.locataires?.nom}</div>
                  <div className="row-sub">
                    {p.biens?.adresse?.split(',')[0]} · {fmtDate(p.mois, { month: 'long', year: 'numeric' })}
                    <span className="echeance-chip">📅 {t(lang,'le_jour')} {p.biens?.jour_echeance || 1}</span>
                  </div>
                </div>
                <div className="row-amount">
                  {p.statut === 'partial' ? (
                    <>
                      <div>{fmt(p.montant_paye)} <span className="row-amount-sub">{t(lang,'recu')}</span></div>
                      <div style={{ color: '#6B4FBB' }}>{fmt(solde)} <span className="row-amount-sub">{t(lang,'du')}</span></div>
                    </>
                  ) : (
                    <div>{fmt(p.montant)}</div>
                  )}
                </div>
                <Badge statut={p.statut} montant={p.montant} montantPaye={p.montant_paye} lang={lang} />
                <div className="statut-btns">
                  {p.statut !== 'paid' && (
                    <button className="btn btn-sm btn-primary" onClick={() => onStatutChange(p.id, 'paid')}>{t(lang,'paye_btn')}</button>
                  )}
                  {(p.statut === 'pending' || p.statut === 'late' || p.statut === 'partial') && (
                    <button className="btn btn-sm btn-purple" onClick={() => setPartialModal(p)}>{t(lang,'partiel_btn')}</button>
                  )}
                  {p.statut === 'pending' && (
                    <button className="btn btn-sm btn-danger" onClick={() => onStatutChange(p.id, 'late')}>{t(lang,'en_retard')}</button>
                  )}
                  {(p.statut === 'late' || p.statut === 'partial') && (
                    <button
                      className="btn btn-sm btn-ghost"
                      style={{ color: '#D85A30', borderColor: '#D85A30' }}
                      disabled={relancing === p.id}
                      onClick={() => handleRelance(p.id)}
                    >
                      {relancing === p.id ? '⏳' : `📧 ${t(lang, 'relancer_btn')}`}
                    </button>
                  )}
                  {p.statut === 'paid' && (
                    <>
                      <button className="btn btn-sm btn-ghost" style={{ color: '#1D9E75', borderColor: '#1D9E75' }} onClick={() => openQuittance(p)}>{t(lang,'quittance_btn')}</button>
                      <button className="btn btn-sm btn-ghost" onClick={() => onStatutChange(p.id, 'pending')}>{t(lang,'rouvrir_btn')}</button>
                    </>
                  )}
                </div>
              </div>
            )
          })
        }
      </div>
    </>
  )
}
// --- Interventions -------------------------------------------
function InterventionsPage({ userId, locataires, biens, lang = 'fr' }) {
  const [interventions, setInterventions] = useState([])
  const [loading, setLoading]             = useState(true)
  const [selected, setSelected]           = useState(null)
  const [reponse, setReponse]             = useState('')
  const [note, setNote]                   = useState('')
  const [sending, setSending]             = useState(false)
  const [filtre, setFiltre]               = useState('actives')

  const CATEGORIES = {
    cat_elec:  t(lang,'interv_cat_elec'),
    cat_plomb: t(lang,'interv_cat_plomb'),
    cat_chauf: t(lang,'interv_cat_chauf'),
    cat_elec2: t(lang,'interv_cat_elec2'),
    cat_autre: t(lang,'interv_cat_autre'),
  }
  const CAT_ICONS = {
    cat_elec: '🍳', cat_plomb: '🚿', cat_chauf: '🔥', cat_elec2: '⚡', cat_autre: '🔧'
  }

  const loadData = useCallback(async () => {
    const { data } = await supabase
      .from('interventions')
      .select('*, locataires(prenom, nom, email), biens(adresse)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    setInterventions(data || [])
    setLoading(false)
  }, [userId])

  useEffect(() => { loadData() }, [loadData])

  const handleStatut = async (id, statut) => {
    await supabase.from('interventions').update({ statut, updated_at: new Date().toISOString() }).eq('id', id)
    loadData()
    if (selected?.id === id) setSelected(s => ({ ...s, statut }))
  }

  const handleRepondre = async () => {
    if (!reponse.trim() || !selected) return
    setSending(true)
    // Envoyer email au locataire
    const token = await getToken()
    await fetch('/api/router', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, },
      body: JSON.stringify({
        action:         'send-intervention',
        locataireEmail: selected.locataires?.email,
        locataireNom:   `${selected.locataires?.prenom} ${selected.locataires?.nom}`,
        categorie:      CATEGORIES[selected.categorie] || selected.categorie,
        reponse:        reponse.trim(),
        userId,
        interventionId: selected.id,
        locataireId:    selected.locataire_id,
      }),
    })
    // Sauvegarder la note interne si remplie
    if (note.trim()) {
      await supabase.from('interventions').update({ note_interne: note.trim(), statut: 'en_cours', updated_at: new Date().toISOString() }).eq('id', selected.id)
    } else {
      await supabase.from('interventions').update({ statut: 'en_cours', updated_at: new Date().toISOString() }).eq('id', selected.id)
    }
    setReponse('')
    setNote('')
    setSending(false)
    loadData()
  }

  const STATUT_COLORS = {
    nouvelle:  { bg: '#faece7', color: '#993C1D', label: t(lang,'interv_nouvelle') },
    en_cours:  { bg: '#faeeda', color: '#854F0B', label: t(lang,'interv_en_cours') },
    resolue:   { bg: '#e1f5ee', color: '#0F6E56', label: t(lang,'interv_resolue') },
  }

  const filtrees = interventions.filter(i => {
    if (filtre === 'actives') return i.statut !== 'resolue'
    if (filtre === 'resolues') return i.statut === 'resolue'
    return true
  })

  const nbNouvelles = interventions.filter(i => i.statut === 'nouvelle').length

  if (loading) return <div className="empty">{t(lang,'chargement')}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="page-header-row">
        <div>
          <div className="page-title">
            🔧 {t(lang,'interv_titre')}
            {nbNouvelles > 0 && <span className="alerte-badge">{nbNouvelles}</span>}
          </div>
          <div className="page-sub">{t(lang,'interv_sub')}</div>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${filtre==='actives'?'active':''}`} onClick={() => setFiltre('actives')}>
          {t(lang,'actives')} ({interventions.filter(i => i.statut !== 'resolue').length})
        </button>
        <button className={`tab ${filtre==='resolues'?'active':''}`} onClick={() => setFiltre('resolues')}>
          {t(lang,'interv_resolues')} ({interventions.filter(i => i.statut === 'resolue').length})
        </button>
        <button className={`tab ${filtre==='toutes'?'active':''}`} onClick={() => setFiltre('toutes')}>
          {t(lang,'toutes')} ({interventions.length})
        </button>
      </div>

<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

  {/* Liste */}
  <div className="card">
    <div className="card-header"><span className="card-title">{filtrees.length} {t(lang,'demandes')}</span></div>
    {filtrees.length === 0
      ? <div className="empty">{t(lang,'interv_aucune')}</div>
      : filtrees.map(i => {
          const s = STATUT_COLORS[i.statut] || STATUT_COLORS.nouvelle
          const isSelected = selected?.id === i.id
          return (
            <div key={i.id} className="row" onClick={() => { setSelected(isSelected ? null : i); setReponse(''); setNote('') }}
              style={{ cursor: 'pointer', background: isSelected ? '#f0faf5' : 'transparent', borderLeft: isSelected ? '3px solid #1D9E75' : '3px solid transparent' }}>
              <div style={{ fontSize: 28, flexShrink: 0 }}>{CAT_ICONS[i.categorie] || '🔧'}</div>
              <div className="row-main">
                <div className="row-name">{i.locataires?.prenom} {i.locataires?.nom}</div>
                <div className="row-sub">{CATEGORIES[i.categorie] || i.categorie} · {i.biens?.adresse?.split(',')[0]}</div>
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                  {new Date(i.created_at).toLocaleDateString(_langLocale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  {i.urgence === 'urgente' && <span style={{ marginLeft: 8, color: '#D85A30', fontWeight: 700 }}>🔴 {t(lang,'urgent')}</span>}
                </div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: s.bg, color: s.color, flexShrink: 0 }}>
                {s.label}
              </span>
            </div>
          )
        })
    }
  </div>

  {/* Détail — s'affiche en dessous sur mobile, en dessous aussi sur desktop */}
  {selected && (
    <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="card-header">
        <span className="card-title">{CAT_ICONS[selected.categorie]} {CATEGORIES[selected.categorie]}</span>
        <button className="btn btn-sm btn-ghost" onClick={() => setSelected(null)}>✕ {t(lang,'fermer')}</button>
      </div>
      <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>

        <div style={{ background: '#f7f7f5', borderRadius: 10, padding: '14px 16px', fontSize: 13 }}>
          <div style={{ fontWeight: 600, color: '#0d2b1f', marginBottom: 8 }}>
            {selected.locataires?.prenom} {selected.locataires?.nom}
            {selected.urgence === 'urgente' && <span style={{ marginLeft: 8, color: '#D85A30' }}>🔴 {t(lang,'urgent')}</span>}
          </div>
          <div style={{ color: '#555', lineHeight: 1.6 }}>{selected.description}</div>
          {selected.photo_url && (
            <a href={selected.photo_url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 10, color: '#1D9E75', fontSize: 12 }}>
              📷 {t(lang,'voir_photo')}
            </a>
          )}
        </div>

        {selected.note_interne && (
          <div style={{ background: '#fef9f0', border: '1px solid #F5D08A', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#854F0B' }}>
            📝 {t(lang,'interv_note')} : {selected.note_interne}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {selected.statut === 'nouvelle' && (
            <button className="btn btn-sm btn-primary" onClick={() => handleStatut(selected.id, 'en_cours')}>
              {t(lang,'interv_prendre')}
            </button>
          )}
          {selected.statut !== 'resolue' && (
            <button className="btn btn-sm" style={{ background: '#1D9E75', color: '#fff', borderColor: '#1D9E75' }} onClick={() => handleStatut(selected.id, 'resolue')}>
              ✓ {t(lang,'interv_resoudre')}
            </button>
          )}
          {selected.statut === 'resolue' && (
            <button className="btn btn-sm btn-ghost" onClick={() => handleStatut(selected.id, 'en_cours')}>{t(lang,'rouvrir')}</button>
          )}
        </div>

        {selected.statut !== 'resolue' && selected.locataires?.email && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid #f0f0ee', paddingTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t(lang,'interv_repondre')} {t(lang,'par_email')}
            </div>
            <textarea
              style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 14, outline: 'none', resize: 'vertical', minHeight: 80 }}
              placeholder={t(lang,'repondre_placeholder').replace('{prenom}', selected.locataires?.prenom)}
              value={reponse}
              onChange={e => setReponse(e.target.value)}
            />
            <div style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t(lang,'interv_note')} ({t(lang,'interne')})
            </div>
            <input
              style={{ padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 14, outline: 'none' }}
              placeholder={t(lang,'ph_note_interne')}
              value={note}
              onChange={e => setNote(e.target.value)}
            />
            <button className="btn btn-primary" onClick={handleRepondre} disabled={sending || !reponse.trim()}>
              {sending ? '...' : `✉️ ${t(lang,'interv_repondre')}`}
            </button>
          </div>
        )}
      </div>
    </div>
  )}
</div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
//  PAGE INDEXATION DES LOYERS — à insérer dans App.js
// ═══════════════════════════════════════════════════════════════

function IndexationsPage({ userId, locataires, biens, proprio, lang = 'fr', getToken, onRefresh }) {
  const idxStat      = { background: '#f7f7f5', border: '1px solid #eee', borderRadius: 10, padding: '12px 14px' }
  const idxStatLabel = { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }
  const idxStatVal   = { fontSize: 18, fontWeight: 700, color: '#0d2b1f' }

  const [indices, setIndices]       = useState([])
  const [indicesIrl, setIndicesIrl] = useState([])
  const [locations, setLocations]   = useState([])
  const [historique, setHistorique] = useState([])
  const [loading, setLoading]       = useState(true)
  const [selected, setSelected]     = useState(null)   // { location, bien, loc, dateAnniv, joursRestants }
  const [calcul, setCalcul]         = useState(null)
  const [applying, setApplying]     = useState(false)
  const [sending, setSending]       = useState(false)
  const [sentId, setSentId]         = useState(null)

  const loadData = useCallback(async () => {
    const [{ data: idx }, { data: irl }, { data: locs }, { data: hist }] = await Promise.all([
      supabase.from('indices_sante').select('*').order('annee', { ascending: false }).order('mois', { ascending: false }),
      supabase.from('indices_irl').select('*').order('annee', { ascending: false }).order('trimestre', { ascending: false }),
      supabase.from('locations').select('*, biens(adresse, region, peb, pays, dpe, departement), locataires(prenom, nom, email, lang)').eq('user_id', userId).is('deleted_at', null),
      supabase.from('loyer_historique').select('*').eq('user_id', userId).order('date_application', { ascending: false }).limit(50),
    ])
    setIndices(idx || [])
    setIndicesIrl(irl || [])
    setLocations(locs || [])
    setHistorique(hist || [])
    setLoading(false)
  }, [userId])

  useEffect(() => { loadData() }, [loadData])

  // Locations actives uniquement (pas de date_fin passée, statut non terminé)
  const locationsActives = locations.filter(l => {
    if (l.statut === 'terminee' || l.statut === 'terminated') return false
    if (l.date_fin && new Date(l.date_fin) < new Date()) return false
    return true
  })

  // Construit l'objet bail attendu par le moteur, à partir d'une location
  const buildBail = (location) => {
    const bien = location.biens || {}
    return {
      loyer_initial:    location.loyer,
      date_signature:   location.date_signature || location.date_debut,
      date_entree:      location.date_debut,
      region:           bien.region || 'wallonie',
      peb:              bien.peb || '',
      indice_reference: location.indice_reference || null,
    }
  }

  // Liste des locations DUES pour indexation (BE + FR)
  const bauxDus = locationsActives
    .map(location => {
      const bien = location.biens || {}
      const pays = bien.pays || 'BE'
      if (!PAYS_INDEXATION.includes(pays)) return null
      // La logique "dû" (fenêtre autour de la date anniversaire) est commune BE/FR
      const { du, dateAnniv, joursRestants } = estDuPourIndexation(
        { date_entree: location.date_debut, date_indexation: location.date_indexation },
        new Date(), 45
      )
      if (!du) return null
      return { location, bien, loc: location.locataires || {}, dateAnniv, joursRestants, pays }
    })
    .filter(Boolean)
    .sort((a, b) => (a.joursRestants ?? 999) - (b.joursRestants ?? 999))

  const handleCalculer = (item) => {
    const pays = item.pays || item.bien?.pays || 'BE'
    let res
    if (pays === 'FR') {
      // Moteur français (IRL)
      const bail = {
        loyer_actuel:       item.location.loyer,
        date_signature:     item.location.date_signature || item.location.date_debut,
        date_entree:        item.location.date_debut,
        dpe:                item.bien?.dpe || '',
        departement:        item.bien?.departement || '',
        irl_trimestre_ref:  item.location.irl_trimestre_ref || null,
        irl_annee_ref:      item.location.irl_annee_ref || null,
        zone_tendue:        item.location.zone_tendue || false,
      }
      res = calculerIndexationFrance({ bail, indices: indicesIrl, dateRevision: item.dateAnniv })
      res._pays = 'FR'
    } else {
      // Moteur belge (indice santé)
      const bail = buildBail(item.location)
      res = calculerIndexation({ bail, indices, dateAnniv: item.dateAnniv, baseCol: 'indice_2013' })
      res._pays = 'BE'
    }
    setSelected(item)
    setCalcul(res)
  }

  const handleAppliquer = async () => {
    if (!calcul?.ok || !selected) return
    setApplying(true)
    const { location, bien } = selected

    // 1) Met à jour le loyer de la LOCATION (source de vérité)
    await supabase.from('locations').update({
      loyer: calcul.nouveau_loyer,
      date_indexation: new Date().toISOString().slice(0,10),
    }).eq('id', location.id)

    // 2) Met aussi à jour le bien pour cohérence d'affichage
    if (bien?.id || location.bien_id) {
      await supabase.from('biens').update({ loyer: calcul.nouveau_loyer }).eq('id', location.bien_id)
    }

    // 3) Historise (champs adaptés selon le moteur BE/FR)
    const estFR = calcul._pays === 'FR'
    await supabase.from('loyer_historique').insert({
      user_id: userId,
      locataire_id: location.locataire_id,
      bien_id: location.bien_id,
      location_id: location.id,
      loyer_avant: estFR ? calcul.loyer_actuel : calcul.loyer_initial,
      loyer_apres: calcul.nouveau_loyer,
      indice_depart: estFR ? calcul.irl_depart : calcul.indice_depart,
      indice_nouveau: estFR ? calcul.irl_nouveau : calcul.indice_nouveau,
      pourcentage: calcul.pourcentage,
      source: 'indexation',
    })

    setApplying(false)
    setSelected(null)
    setCalcul(null)
    loadData()
    if (onRefresh) onRefresh()
  }

  const lettreLocataire = (item, res) => {
    const { loc, bien } = item
    const L = loc.lang || lang
    const moisAnniv = item.dateAnniv.toLocaleDateString(
      L === 'nl' ? 'nl-BE' : L === 'en' ? 'en-GB' : 'fr-BE',
      { month: 'long', year: 'numeric' }
    )
    const fmtE = (n) => Number(n).toFixed(2).replace('.', ',')
    const adresse = bien.adresse || '—'
    const estFR = res._pays === 'FR'

    // Loyer/indices selon le pays (champs différents entre les 2 moteurs)
    const loyerBase   = estFR ? res.loyer_actuel : res.loyer_initial
    const indiceDep   = estFR ? res.irl_depart    : res.indice_depart
    const indiceNouv  = estFR ? res.irl_nouveau   : res.indice_nouveau
    const labelIndiceFR = { fr: 'IRL', nl: 'IRL', en: 'IRL' }
    const labelIndiceBE = { fr: 'indice santé', nl: 'gezondheidsindex', en: 'health index' }
    const labelIndice = (estFR ? labelIndiceFR : labelIndiceBE)[L] || labelIndiceBE.fr

    // Cas France : loyer gelé (passoire thermique)
    if (estFR && res.gele) {
      const T = {
        nl: `Beste ${loc.prenom} ${loc.nom},\n\nOvereenkomstig de wettelijke bepalingen kan de huurprijs van het pand gelegen te ${adresse} dit jaar niet worden geïndexeerd.\n\n${res.avertissements[0] || ''}\n\nDe huurprijs blijft ongewijzigd op ${fmtE(loyerBase)} €.\n\nMet vriendelijke groeten,\n${proprio?.proprio_nom || ''}`,
        en: `Dear ${loc.prenom} ${loc.nom},\n\nIn accordance with legal provisions, the rent for the property located at ${adresse} cannot be revised this year.\n\n${res.avertissements[0] || ''}\n\nThe rent remains unchanged at ${fmtE(loyerBase)} €.\n\nKind regards,\n${proprio?.proprio_nom || ''}`,
        fr: `Cher/Chère ${loc.prenom} ${loc.nom},\n\nConformément aux dispositions légales, le loyer du bien situé ${adresse} ne peut pas être révisé cette année.\n\n${res.avertissements[0] || ''}\n\nLe loyer reste inchangé à ${fmtE(loyerBase)} €.\n\nBien à vous,\n${proprio?.proprio_nom || ''}`,
      }
      return T[L] || T.fr
    }

    // Avertissements complémentaires (zone tendue, travaux, PEB...) à inclure sous le calcul
    const avertTxt = (res.avertissements || []).length
      ? '\n' + res.avertissements.map(a => `⚠ ${a}`).join('\n') + '\n'
      : ''

    if (L === 'nl') {
      return `Beste ${loc.prenom} ${loc.nom},

Overeenkomstig de huurovereenkomst en de wettelijke bepalingen, wordt de huurprijs van het pand gelegen te ${adresse} geïndexeerd op de verjaardag van het contract (${moisAnniv}).

Berekening:
- Aanvangshuur: ${fmtE(loyerBase)} €
- Aanvangs-${labelIndice}: ${indiceDep}
- Nieuwe ${labelIndice}: ${indiceNouv}
- Nieuwe huurprijs: ${fmtE(res.nouveau_loyer)} € (${res.pourcentage > 0 ? '+' : ''}${res.pourcentage}%)
${avertTxt}
De nieuwe huurprijs van ${fmtE(res.nouveau_loyer)} € is van toepassing vanaf de volgende vervaldag.

Met vriendelijke groeten,
${proprio?.proprio_nom || ''}`
    }
    if (L === 'en') {
      return `Dear ${loc.prenom} ${loc.nom},

In accordance with the lease agreement and legal provisions, the rent for the property located at ${adresse} is revised on the contract anniversary (${moisAnniv}).

Calculation:
- Initial rent: ${fmtE(loyerBase)} €
- Starting ${labelIndice}: ${indiceDep}
- New ${labelIndice}: ${indiceNouv}
- New rent: ${fmtE(res.nouveau_loyer)} € (${res.pourcentage > 0 ? '+' : ''}${res.pourcentage}%)
${avertTxt}
The new rent of ${fmtE(res.nouveau_loyer)} € applies from the next due date.

Kind regards,
${proprio?.proprio_nom || ''}`
    }
    return `Cher/Chère ${loc.prenom} ${loc.nom},

Conformément au contrat de bail et aux dispositions légales, le loyer du bien situé ${adresse} est ${estFR ? 'révisé' : 'indexé'} à la date anniversaire du contrat (${moisAnniv}).

Calcul de ${estFR ? 'la révision' : "l'indexation"} :
- Loyer de base : ${fmtE(loyerBase)} €
- ${labelIndice.charAt(0).toUpperCase() + labelIndice.slice(1)} de départ : ${indiceDep}
- Nouvel ${labelIndice} : ${indiceNouv}
- Nouveau loyer : ${fmtE(res.nouveau_loyer)} € (${res.pourcentage > 0 ? '+' : ''}${res.pourcentage}%)
${avertTxt}
Le nouveau loyer de ${fmtE(res.nouveau_loyer)} € s'applique à partir de la prochaine échéance.

Bien à vous,
${proprio?.proprio_nom || ''}`
  }

  const handleEnvoyer = async (item, res) => {
    if (!item.loc.email) return
    setSending(true)
    try {
      const token = await getToken()
      await fetch('/api/router', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          action: 'send-indexation',
          locataireEmail: item.loc.email,
          locataireNom: `${item.loc.prenom} ${item.loc.nom}`,
          locataireId: item.location.locataire_id,
          objet: t(lang, 'idx_mail_objet'),
          corps: lettreLocataire(item, res),
        }),
      })
      await supabase.from('loyer_historique')
        .update({ notifie_locataire: true })
        .eq('location_id', item.location.id)
        .eq('loyer_apres', res.nouveau_loyer)
      setSentId(item.location.id)
    } catch (e) { /* silencieux */ }
    setSending(false)
  }

  const fmtPct = (p) => `${p > 0 ? '+' : ''}${p}%`

  if (loading) return <div className="empty" style={{ padding: 40 }}>{t(lang,'chargement')}…</div>

  return (
    <div>
      <div className="page-header">
        <h1>📈 {t(lang,'idx_titre')}</h1>
        <p className="page-subtitle">{t(lang,'idx_soustitre')}</p>
      </div>

      {bauxDus.length === 0 ? (
        <div className="card">
          <div className="empty" style={{ padding: 40, textAlign: 'center' }}>
            ✅ {t(lang,'idx_aucun')}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {bauxDus.map(item => {
            const isOpen = selected?.location.id === item.location.id
            return (
              <div key={item.location.id} className="card" style={{ borderLeft: '3px solid #1D9E75' }}>
                <div style={{ padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#0d2b1f' }}>
                      {item.loc.prenom} {item.loc.nom}
                    </div>
                    <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                      {item.bien.adresse} · {(item.bien.region || '—')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                      background: item.joursRestants < 0 ? '#faece7' : '#faeeda',
                      color: item.joursRestants < 0 ? '#993C1D' : '#854F0B' }}>
                      {item.joursRestants < 0 ? t(lang,'idx_depassee') : `J-${item.joursRestants}`}
                    </span>
                    {!isOpen && (
                      <button className="btn btn-primary" style={{ fontSize: 13, padding: '8px 18px' }}
                        onClick={() => handleCalculer(item)}>
                        {t(lang,'idx_calculer')}
                      </button>
                    )}
                  </div>
                </div>

                {isOpen && calcul && (
                  <div style={{ borderTop: '1px solid #eee', padding: '20px 22px' }}>
                    {calcul.erreur ? (
                      <div style={{ background: '#faece7', color: '#993C1D', padding: 14, borderRadius: 8, fontSize: 14 }}>
                        ⚠️ {calcul.erreur}
                        <div style={{ fontSize: 12, marginTop: 6, color: '#666' }}>
                          {t(lang,'idx_verif_indices')}
                        </div>
                      </div>
                    ) : calcul.gele ? (
                      <>
                        {/* Cas France : loyer gelé (passoire thermique F/G) */}
                        <div style={{ background: '#faece7', border: '1px solid #e8b8a8', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#993C1D', marginBottom: 8 }}>
                            🚫 {t(lang,'idx_gele_titre')}
                          </div>
                          <div style={{ fontSize: 13, color: '#7a4030', lineHeight: 1.6 }}>
                            {calcul.avertissements[0]}
                          </div>
                          <div style={{ marginTop: 12, fontSize: 13, color: '#555' }}>
                            {t(lang,'idx_loyer_actuel')} : <strong>{fmt(calcul.loyer_actuel)}</strong> — {t(lang,'idx_gele_inchange')}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 18 }}>
                          <div style={idxStat}>
                            <div style={idxStatLabel}>{t(lang,'idx_loyer_actuel')}</div>
                            <div style={idxStatVal}>{fmt(calcul._pays === 'FR' ? calcul.loyer_actuel : calcul.loyer_initial)}</div>
                          </div>
                          <div style={idxStat}>
                            <div style={idxStatLabel}>{calcul._pays === 'FR' ? t(lang,'idx_irl_depart') : t(lang,'idx_indice_depart')}</div>
                            <div style={idxStatVal}>{calcul._pays === 'FR' ? calcul.irl_depart : calcul.indice_depart}</div>
                          </div>
                          <div style={idxStat}>
                            <div style={idxStatLabel}>{calcul._pays === 'FR' ? t(lang,'idx_irl_nouveau') : t(lang,'idx_nouvel_indice')}</div>
                            <div style={idxStatVal}>{calcul._pays === 'FR' ? calcul.irl_nouveau : calcul.indice_nouveau}</div>
                          </div>
                          <div style={{ ...idxStat, background: '#f0faf6', borderColor: '#a7f0d4' }}>
                            <div style={idxStatLabel}>{t(lang,'idx_nouveau_loyer')}</div>
                            <div style={{ ...idxStatVal, color: '#0F6E56' }}>
                              {fmt(calcul.nouveau_loyer)} <span style={{ fontSize: 12 }}>({fmtPct(calcul.pourcentage)})</span>
                            </div>
                          </div>
                        </div>

                        {/* Détail correction PEB Flandre (transparence) */}
                        {calcul.correction_appliquee && (
                          <div style={{ background: '#eef5fc', border: '1px solid #b6d4f0', borderRadius: 8, padding: 12, marginBottom: 10, fontSize: 13 }}>
                            <div style={{ fontWeight: 600, color: '#185FA5', marginBottom: 6 }}>🏴 {t(lang,'idx_correction_titre')}</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#555' }}>
                              <span>{t(lang,'idx_loyer_plein')}</span>
                              <span>{fmt(calcul.loyer_plein)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#555' }}>
                              <span>{t(lang,'idx_facteur')} ({calcul.correction_label})</span>
                              <span>× {calcul.correction_facteur.toFixed(4)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, color: '#0F6E56', borderTop: '1px solid #b6d4f0', marginTop: 6, paddingTop: 6 }}>
                              <span>{t(lang,'idx_loyer_corrige')}</span>
                              <span>{fmt(calcul.nouveau_loyer)}</span>
                            </div>
                          </div>
                        )}

                        {calcul.avertissements.map((a, i) => (
                          <div key={i} style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: 12, borderRadius: 8, fontSize: 13, marginBottom: 10 }}>
                            ⚠️ {a}
                          </div>
                        ))}

                        <details style={{ marginBottom: 16 }}>
                          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0d2b1f' }}>
                            ✉️ {t(lang,'idx_voir_lettre')}
                          </summary>
                          <textarea readOnly value={lettreLocataire(item, calcul)}
                            style={{ width: '100%', minHeight: 200, marginTop: 10, padding: 14, fontSize: 13, fontFamily: 'inherit', borderRadius: 8, border: '1px solid #ddd', lineHeight: 1.6, resize: 'vertical' }} />
                        </details>

                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <button className="btn btn-primary" disabled={applying}
                            onClick={handleAppliquer} style={{ fontSize: 14 }}>
                            {applying ? `${t(lang,'idx_application')}…` : `✅ ${t(lang,'idx_appliquer')}`}
                          </button>
                          {item.loc.email && (
                            <button className="btn" disabled={sending || sentId === item.location.id}
                              onClick={() => handleEnvoyer(item, calcul)}
                              style={{ fontSize: 14, background: sentId === item.location.id ? '#e1f5ee' : '#f0f0f0', color: sentId === item.location.id ? '#0F6E56' : '#333' }}>
                              {sentId === item.location.id ? `✓ ${t(lang,'idx_envoye')}` : `✉️ ${t(lang,'idx_envoyer')}`}
                            </button>
                          )}
                          <button className="btn" onClick={() => { setSelected(null); setCalcul(null) }}
                            style={{ fontSize: 14, background: 'transparent', color: '#888' }}>
                            {t(lang,'annuler')}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {historique.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 16, color: '#0d2b1f', marginBottom: 14 }}>{t(lang,'idx_historique')}</h2>
          <div className="card">
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>{t(lang,'idx_locataire')}</th>
                  <th>{t(lang,'idx_date')}</th>
                  <th>{t(lang,'idx_avant')}</th>
                  <th>{t(lang,'idx_apres')}</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {historique.map(h => (
                  <tr key={h.id}>
                    <td>{(() => { const l = locataires.find(x => x.id === h.locataire_id); return l ? `${l.prenom} ${l.nom}` : '—' })()}</td>
                    <td>{new Date(h.date_application).toLocaleDateString(_langLocale)}</td>
                    <td>{fmt(h.loyer_avant)}</td>
                    <td style={{ fontWeight: 600, color: '#0F6E56' }}>{fmt(h.loyer_apres)}</td>
                    <td>{fmtPct(h.pourcentage)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
//  COMPOSANT — Tâches récurrentes (entretien chaudière, ramonage…)
// ═══════════════════════════════════════════════════════════════

const MODELES_TACHES = [
  { key: 'chaudiere',  icon: '🔥', freq: 12, titreFr: 'Entretien chaudière',      titreEn: 'Boiler maintenance',     titreNl: 'Onderhoud ketel' },
  { key: 'ramonage',   icon: '🧹', freq: 12, titreFr: 'Ramonage cheminée',        titreEn: 'Chimney sweeping',       titreNl: 'Schoorsteenvegen' },
  { key: 'assurance',  icon: '📋', freq: 12, titreFr: 'Renouvellement assurance', titreEn: 'Insurance renewal',      titreNl: 'Verzekering vernieuwen' },
  { key: 'detecteur',  icon: '🚨', freq: 12, titreFr: 'Détecteurs de fumée',      titreEn: 'Smoke detectors check',  titreNl: 'Rookmelders controleren' },
  { key: 'citerne',    icon: '🛢️', freq: 60, titreFr: 'Contrôle citerne mazout',  titreEn: 'Oil tank inspection',    titreNl: 'Controle stookolietank' },
  { key: 'perso',      icon: '🔧', freq: 12, titreFr: 'Tâche personnalisée',      titreEn: 'Custom task',            titreNl: 'Aangepaste taak' },
]

function modeleTitre(m, lang) {
  return lang === 'en' ? m.titreEn : lang === 'nl' ? m.titreNl : m.titreFr
}

function TachesRecurrentes({ userId, biens, lang = 'fr' }) {
  const [taches, setTaches]     = useState([])
  const [loading, setLoading]   = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editTache, setEditTache] = useState(null)
  const [form, setForm] = useState({
    modele: 'chaudiere', titre: '', description: '', bien_id: '',
    frequence_mois: 12, prochaine_date: '', rappel_jours: 14,
  })

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('taches_recurrentes')
      .select('*').eq('user_id', userId).eq('actif', true)
      .order('prochaine_date', { ascending: true })
    setTaches(data || [])
    setLoading(false)
  }, [userId])

  useEffect(() => { load() }, [load])

  const resetForm = () => {
    setForm({ modele: 'chaudiere', titre: '', description: '', bien_id: '', frequence_mois: 12, prochaine_date: '', rappel_jours: 14 })
    setEditTache(null)
    setShowForm(false)
  }

  const handleModeleChange = (key) => {
    const m = MODELES_TACHES.find(x => x.key === key)
    setForm(f => ({
      ...f,
      modele: key,
      titre: key === 'perso' ? '' : modeleTitre(m, lang),
      frequence_mois: m.freq,
    }))
  }

  const handleSave = async () => {
    const titre = form.titre || modeleTitre(MODELES_TACHES.find(m => m.key === form.modele), lang)
    if (!titre || !form.prochaine_date) return

    const payload = {
      user_id: userId,
      bien_id: form.bien_id || null,
      titre,
      description: form.description || null,
      frequence_mois: parseInt(form.frequence_mois) || 12,
      prochaine_date: form.prochaine_date,
      rappel_jours: parseInt(form.rappel_jours) || 14,
    }

    if (editTache) {
      await supabase.from('taches_recurrentes').update(payload).eq('id', editTache.id)
    } else {
      await supabase.from('taches_recurrentes').insert(payload)
    }
    resetForm()
    load()
  }

  const handleEdit = (t) => {
    setEditTache(t)
    setForm({
      modele: 'perso', titre: t.titre, description: t.description || '',
      bien_id: t.bien_id || '', frequence_mois: t.frequence_mois,
      prochaine_date: t.prochaine_date, rappel_jours: t.rappel_jours,
    })
    setShowForm(true)
  }

  // Marquer comme fait → avance la prochaine date
  const handleComplete = async (t) => {
    const next = new Date(t.prochaine_date)
    next.setMonth(next.getMonth() + t.frequence_mois)
    await supabase.from('taches_recurrentes').update({
      derniere_completion: new Date().toISOString().slice(0,10),
      prochaine_date: next.toISOString().slice(0,10),
    }).eq('id', t.id)
    load()
  }

  const handleDelete = async (tache) => {
    if (!window.confirm((tache.titre || '') + ' ?')) return
    await supabase.from('taches_recurrentes').update({ actif: false }).eq('id', tache.id)
    load()
  }

  const bienNom = (id) => {
    const b = biens.find(x => x.id === id)
    return b ? b.adresse : null
  }

  const joursAvant = (date) => {
    const d = Math.ceil((new Date(date) - new Date()) / (1000*60*60*24))
    return d
  }

  const iconePourTache = (titre) => {
    const m = MODELES_TACHES.find(x => modeleTitre(x, lang) === titre)
    return m ? m.icon : '🔧'
  }

  if (loading) return <div className="empty" style={{ padding: 40 }}>{t(lang,'chargement')}…</div>

  return (
    <div>
      {/* Bouton ajout */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#888' }}>{t(lang,'tr_sous_titre')}</div>
        {!showForm && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
            + {t(lang,'tr_ajouter')}
          </button>
        )}
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="card" style={{ marginBottom: 16, padding: 20 }}>
          <div className="form-grid">
            {!editTache && (
              <div className="form-group full">
                <label>{t(lang,'tr_modele')}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {MODELES_TACHES.map(m => (
                    <button key={m.key} type="button"
                      onClick={() => handleModeleChange(m.key)}
                      style={{
                        padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                        border: form.modele === m.key ? '2px solid #1D9E75' : '1px solid #ddd',
                        background: form.modele === m.key ? '#f0faf6' : '#fff',
                        fontWeight: form.modele === m.key ? 600 : 400,
                      }}>
                      {m.icon} {modeleTitre(m, lang)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group full">
              <label>{t(lang,'tr_titre')} *</label>
              <input value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))}
                placeholder={modeleTitre(MODELES_TACHES.find(m => m.key === form.modele), lang)} />
            </div>

            <div className="form-group">
              <label>{t(lang,'tr_bien')}</label>
              <select value={form.bien_id} onChange={e => setForm(f => ({ ...f, bien_id: e.target.value }))}>
                <option value="">{t(lang,'tr_tous_biens')}</option>
                {biens.map(b => <option key={b.id} value={b.id}>{b.adresse}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>{t(lang,'tr_frequence')}</label>
              <select value={form.frequence_mois} onChange={e => setForm(f => ({ ...f, frequence_mois: e.target.value }))}>
                <option value="1">{t(lang,'tr_mensuel')}</option>
                <option value="3">{t(lang,'tr_trimestriel')}</option>
                <option value="6">{t(lang,'tr_semestriel')}</option>
                <option value="12">{t(lang,'tr_annuel')}</option>
                <option value="24">{t(lang,'tr_2ans')}</option>
                <option value="60">{t(lang,'tr_5ans')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t(lang,'tr_prochaine')} *</label>
              <input type="date" value={form.prochaine_date}
                onChange={e => setForm(f => ({ ...f, prochaine_date: e.target.value }))} />
            </div>

            <div className="form-group">
              <label>{t(lang,'tr_rappel')}</label>
              <select value={form.rappel_jours} onChange={e => setForm(f => ({ ...f, rappel_jours: e.target.value }))}>
                <option value="7">7 {t(lang,'tr_jours_avant')}</option>
                <option value="14">14 {t(lang,'tr_jours_avant')}</option>
                <option value="30">30 {t(lang,'tr_jours_avant')}</option>
                <option value="60">60 {t(lang,'tr_jours_avant')}</option>
              </select>
            </div>

            <div className="form-group full">
              <label>{t(lang,'tr_note')}</label>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={resetForm}>{t(lang,'annuler_btn')}</button>
            <button className="btn btn-primary" onClick={handleSave}>{t(lang,'enregistrer')}</button>
          </div>
        </div>
      )}

      {/* Liste */}
      {taches.length === 0 ? (
        <div className="card"><div className="empty" style={{ padding: 32, textAlign: 'center' }}>
          🔧 {t(lang,'tr_aucune')}
        </div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {taches.map(t2 => {
            const jours = joursAvant(t2.prochaine_date)
            const urgent = jours <= t2.rappel_jours
            return (
              <div key={t2.id} style={{
                background: '#fff', border: `1.5px solid ${urgent ? '#F5D08A' : '#eee'}`,
                borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap'
              }}>
                <div style={{ fontSize: 24 }}>{iconePourTache(t2.titre)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>{t2.titre}</div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                    {bienNom(t2.bien_id) ? `${bienNom(t2.bien_id)} · ` : ''}
                    {t(lang,'tr_prochaine')}: {new Date(t2.prochaine_date).toLocaleDateString(_langLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
                    {t2.description ? ` · ${t2.description}` : ''}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                  background: jours < 0 ? '#faece7' : urgent ? '#faeeda' : '#f0faf6',
                  color: jours < 0 ? '#993C1D' : urgent ? '#854F0B' : '#0F6E56' }}>
                  {jours < 0 ? t(lang,'tr_depassee') : `J-${jours}`}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-sm btn-primary" onClick={() => handleComplete(t2)}>
                    ✓ {t(lang,'tr_fait')}
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => handleEdit(t2)}>{t(lang,'modifier')}</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => handleDelete(t2)} style={{ color: '#993C1D' }}>✕</button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
//  CALENDRIER UNIFIÉ — page dédiée + mini dashboard
//  Sources : fins de bail (echeances renouvellement), indexations
//            (echeances indexation), tâches récurrentes
//  À insérer dans App.js (avant function TodoPage par ex.)
// ═══════════════════════════════════════════════════════════════

// ─── Agrégation des événements en liste datée unifiée ──────────
// Retourne : [{ date:'YYYY-MM-DD', type, icon, label, color, bg, raw }]
function aggregerEvenements({ echeances = [], tachesRec = [], lang = 'fr' }) {
  const events = []

  // Échéances (renouvellement + indexation), non traitées
  for (const e of echeances) {
    if (e.traitee) continue
    const isRenouv = e.type === 'renouvellement'
    events.push({
      date:  e.date_echeance,
      type:  e.type,
      icon:  isRenouv ? '🔄' : '📈',
      label: `${isRenouv ? t(lang,'renouvellement') : t(lang,'indexation')} — ${e.locataires?.prenom || ''} ${e.locataires?.nom?.charAt(0) || ''}.`,
      color: isRenouv ? '#185FA5' : '#0F6E56',
      bg:    isRenouv ? '#eef5fc' : '#e1f5ee',
      raw:   e,
    })
  }

  // Tâches récurrentes actives
  for (const tr of tachesRec) {
    if (!tr.actif) continue
    events.push({
      date:  tr.prochaine_date,
      type:  'tache_rec',
      icon:  '🔧',
      label: tr.titre,
      color: '#854F0B',
      bg:    '#faeeda',
      raw:   tr,
    })
  }

  return events
}

// ─── Composant grille mensuelle réutilisable ───────────────────
function CalendrierGrid({ events, moisCal, setMoisCal, onEventClick, compact = false, lang = 'fr' }) {
  const annee   = moisCal.getFullYear()
  const mois    = moisCal.getMonth()
  const premier = new Date(annee, mois, 1)
  const dernier = new Date(annee, mois + 1, 0)
  const offsetDebut = (premier.getDay() + 6) % 7
  const todayStr = new Date().toISOString().split('T')[0]

  const cases = []
  for (let i = 0; i < offsetDebut; i++) cases.push(<div key={`e-${i}`} />)

  for (let jour = 1; jour <= dernier.getDate(); jour++) {
    const dateStr    = `${annee}-${String(mois+1).padStart(2,'0')}-${String(jour).padStart(2,'0')}`
    const eventsJour = events.filter(ev => ev.date === dateStr)
    const isToday    = dateStr === todayStr

    cases.push(
      <div key={jour} style={{
        minHeight: compact ? 44 : 70,
        border: '1px solid #f0f0f0', borderRadius: 8, padding: compact ? '2px 3px' : '4px 6px',
        background:  isToday ? '#f0faf5' : '#fff',
        borderColor: isToday ? '#9FE1CB' : '#f0f0f0',
      }}>
        <div style={{ fontSize: compact ? 10 : 12, fontWeight: isToday ? 700 : 400, color: isToday ? '#1D9E75' : '#aaa', marginBottom: compact ? 1 : 4 }}>
          {jour}
        </div>
        {eventsJour.slice(0, compact ? 2 : 99).map((ev, idx) => (
          <div key={idx}
            onClick={() => onEventClick && onEventClick(ev)}
            style={{ fontSize: compact ? 8 : 10, fontWeight: 600, padding: compact ? '1px 2px' : '2px 4px', borderRadius: 4, marginBottom: 2,
              background: ev.bg, color: ev.color,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              cursor: onEventClick ? 'pointer' : 'default' }}>
            {ev.icon} {compact ? '' : ev.label}
          </div>
        ))}
        {compact && eventsJour.length > 2 && (
          <div style={{ fontSize: 8, color: '#aaa' }}>+{eventsJour.length - 2}</div>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Navigation mois */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <button className="btn btn-sm btn-ghost" onClick={() => setMoisCal(m => { const d = new Date(m); d.setMonth(d.getMonth()-1); return d })}>←</button>
        <span style={{ fontWeight: 700, textTransform: 'capitalize', fontSize: compact ? 13 : 15 }}>
          {moisCal.toLocaleDateString(_langLocale, { month: 'long', year: 'numeric' })}
        </span>
        <button className="btn btn-sm btn-ghost" onClick={() => setMoisCal(m => { const d = new Date(m); d.setMonth(d.getMonth()+1); return d })}>→</button>
      </div>

      {/* Entêtes jours */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 }}>
        {[t(lang,'lun'),t(lang,'mar'),t(lang,'mer'),t(lang,'jeu'),t(lang,'ven'),t(lang,'sam'),t(lang,'dim')].map(j => (
          <div key={j} style={{ textAlign: 'center', fontSize: compact ? 9 : 11, fontWeight: 700, color: '#aaa', padding: '2px 0' }}>{j}</div>
        ))}
      </div>

      {/* Grille */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>{cases}</div>
    </div>
  )
}

// ─── Légende réutilisable ──────────────────────────────────────
function CalendrierLegende({ lang = 'fr' }) {
  const items = [
    { bg: '#eef5fc', label: t(lang,'renouvellement') },
    { bg: '#e1f5ee', label: t(lang,'indexation') },
    { bg: '#faeeda', label: t(lang,'tr_vue_recurrentes') },
  ]
  return (
    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#888', flexWrap: 'wrap' }}>
      {items.map((it, i) => (
        <span key={i}>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: it.bg, marginRight: 4 }}/>
          {it.label}
        </span>
      ))}
    </div>
  )
}

// ─── PAGE CALENDRIER (menu) ────────────────────────────────────
function CalendrierPage({ userId, biens = [], lang = 'fr', setPage }) {
  const [echeances, setEcheances] = useState([])
  const [tachesRec, setTachesRec] = useState([])
  const [moisCal, setMoisCal]     = useState(new Date())
  const [detail, setDetail]       = useState(null)
  const [loading, setLoading]     = useState(true)

  useEffect(() => {
    const load = async () => {
      const [{ data: ech }, { data: tr }] = await Promise.all([
        supabase.from('echeances').select('*, locataires(prenom, nom, biens(adresse))').eq('user_id', userId).eq('traitee', false),
        supabase.from('taches_recurrentes').select('*').eq('user_id', userId).eq('actif', true),
      ])
      setEcheances(ech || [])
      setTachesRec(tr || [])
      setLoading(false)
    }
    load()
  }, [userId])

  const events = aggregerEvenements({ echeances, tachesRec, lang })

  if (loading) return <div className="empty" style={{ padding: 40 }}>{t(lang,'chargement')}…</div>

  return (
    <div>
      <div className="page-header">
        <h1>📅 {t(lang,'cal_titre')}</h1>
        <p className="page-subtitle">{t(lang,'cal_soustitre')}</p>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <CalendrierGrid
          events={events}
          moisCal={moisCal}
          setMoisCal={setMoisCal}
          onEventClick={setDetail}
          lang={lang}
        />
        <div style={{ marginTop: 16 }}>
          <CalendrierLegende lang={lang} />
        </div>
      </div>

      {/* Modal détail */}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{detail.icon} {detail.label}</div>
            <div style={{ background: '#f0faf5', borderRadius: 10, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
              <div><strong>{t(lang,'date_lbl')}</strong> {new Date(detail.date).toLocaleDateString(_langLocale, { day: 'numeric', month: 'long', year: 'numeric' })}</div>
              {detail.type === 'tache_rec'
                ? <>
                    {(() => { const b = biens.find(x => x.id === detail.raw.bien_id); return b ? <div><strong>{t(lang,'bien_lbl')}</strong> {b.adresse.split(',')[0]}</div> : null })()}
                    {detail.raw.description && <div><strong>{t(lang,'note_lbl')}</strong> {detail.raw.description}</div>}
                  </>
                : <>
                    <div><strong>{t(lang,'locataire_lbl')}</strong> {detail.raw.locataires?.prenom} {detail.raw.locataires?.nom}</div>
                    {detail.raw.locataires?.biens?.adresse && <div><strong>{t(lang,'bien_lbl')}</strong> {detail.raw.locataires.biens.adresse.split(',')[0]}</div>}
                  </>
              }
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setDetail(null)}>{t(lang,'fermer') || 'Fermer'}</button>
              {detail.type === 'tache_rec'
                ? <button className="btn btn-primary" onClick={() => { setDetail(null); setPage('todo') }}>{t(lang,'tr_vue_recurrentes')} →</button>
                : <button className="btn btn-primary" onClick={() => { setDetail(null); setPage('echeances') }}>{t(lang,'echeances_titre') || 'Échéances'} →</button>
              }
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── TODO PAGE ────────────────────────────────────────────────
function TodoPage({ userId, plan, setPage, biens, paiements, lang = 'fr' }) {
  const [tasks, setTasks]           = useState([])
  const [loading, setLoading]       = useState(true)
  const [filtre, setFiltre]         = useState('tous')
  const [confirmingPonto, setConfirmingPonto] = useState(null)
  const [vue, setVue] = useState('taches')  // 'taches' | 'recurrentes'
  const bienIds = biens.map(b => b.id)
  const now = new Date()
  const moisCourant = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`

  const loadTasks = useCallback(async () => {
    setLoading(true)
    const allTasks = []

    // ── Fetches parallèles ──────────────────────────────────
    const [
      { data: echeances },
      { data: conn },
      { data: interventions },
      { data: locatairesActifs },
    ] = await Promise.all([
      supabase.from('echeances')
        .select('*, locataires(prenom, nom, biens(adresse))')
        .eq('user_id', userId).eq('traitee', false)
        .order('date_echeance', { ascending: true }),
      supabase.from('ponto_connections')
        .select('pending_matches').eq('user_id', userId).single(),
      supabase.from('interventions')
        .select('*, locataires(prenom, nom), biens(adresse)')
        .eq('user_id', userId).in('statut', ['nouvelle', 'en_cours'])
        .order('created_at', { ascending: false }),
      supabase.from('locataires')
        .select('id, prenom, nom, biens(adresse)')
        .is('date_sortie', null).in('bien_id', bienIds),
    ])

    // Documents : une seule requête groupée
    const locIds = (locatairesActifs || []).map(l => l.id)
    const { data: docs } = locIds.length
      ? await supabase.from('documents').select('locataire_id').in('locataire_id', locIds)
      : { data: [] }
    const locsAvecDocs = new Set((docs || []).map(d => d.locataire_id))

    // ── 1. Loyers en retard / dépassés ─────────────────────
    for (const p of paiements.filter(p => bienIds.includes(p.bien_id))) {
      const moisPaie = p.mois?.slice(0,7)
      const isLate   = p.statut === 'late'
      const isPast   = p.statut === 'pending' && moisPaie < moisCourant
      if (!isLate && !isPast) continue

      const daysDiff = Math.floor((now - new Date(p.mois)) / (1000*60*60*24))
      allTasks.push({
        id: `loyer-${p.id}`,
        category: 'loyers',
        urgency: daysDiff > 30 ? 'critique' : 'urgent',
        icon: '💶',
        title: `${p.locataires?.prenom} ${p.locataires?.nom}`,
        desc: `${p.biens?.adresse?.split(',')[0]} · ${fmtDate(p.mois, {month:'long',year:'numeric'})} · ${fmt(p.montant)}`,
        ctaLabel: t(lang,'paiements'),
        ctaPage: 'paiements',
      })
    }

    // ── 2. Échéances bail proches (J-60) ───────────────────
    for (const e of (echeances || [])) {
      const diff = Math.ceil((new Date(e.date_echeance) - now) / (1000*60*60*24))
      if (diff > 60 || diff < 0) continue
      allTasks.push({
        id: `ech-${e.id}`,
        category: 'echeances',
        urgency: diff <= 7 ? 'critique' : diff <= 30 ? 'urgent' : 'info',
        icon: e.type === 'renouvellement' ? '🔄' : '📈',
        title: `${e.locataires?.prenom} ${e.locataires?.nom}`,
        desc: `${e.locataires?.biens?.adresse?.split(',')[0]} · ${e.type === 'renouvellement' ? t(lang,'renouvellement') : t(lang,'indexation')} · J-${diff} · ${fmtDate(e.date_echeance)}`,
        ctaLabel: t(lang,'ech_titre'),
        ctaPage: 'echeances',
      })
    }

    // ── 3. Ponto à confirmer ───────────────────────────────
    for (const m of (conn?.pending_matches || [])) {
      allTasks.push({
        id: `ponto-${m.paiementId}`,
        category: 'ponto',
        urgency: 'urgent',
        icon: '🏦',
        title: m.locataire,
        desc: `${m.montant}€ ${t(lang,'ponto_recu_le')} ${m.date} · ${t(lang,'ponto_non_confirme')}`,
        ctaLabel: '✓ Confirmer',
        ctaPage: null,
        pontoData: m,
      })
    }

    // ── 4. Interventions ouvertes ──────────────────────────
    for (const i of (interventions || [])) {
      allTasks.push({
        id: `interv-${i.id}`,
        category: 'interventions',
        urgency: i.urgence === 'urgente' ? 'critique' : i.statut === 'nouvelle' ? 'urgent' : 'info',
        icon: '🔧',
        title: `${i.locataires?.prenom} ${i.locataires?.nom}`,
        desc: `${i.biens?.adresse?.split(',')[0]} · ${i.statut === 'nouvelle' ? t(lang,'interv_nouvelle') : t(lang,'interv_en_cours')}${i.urgence === 'urgente' ? ' · 🔴 Urgente' : ''}`,
        ctaLabel: t(lang,'interv_titre'),
        ctaPage: 'interventions',
      })
    }

    // ── 5. Dossiers sans documents ─────────────────────────
    for (const l of (locatairesActifs || [])) {
      if (!locsAvecDocs.has(l.id)) {
        allTasks.push({
          id: `docs-${l.id}`,
          category: 'documents',
          urgency: 'info',
          icon: '📁',
          title: `${l.prenom} ${l.nom}`,
          desc: `${l.biens?.adresse?.split(',')[0]} · ${t(lang,'todo_aucun_doc')}`,
          ctaLabel: t(lang,'loc_titre'),
          ctaPage: 'locataires',
        })
      }
    }

    // ── Sort par urgence ───────────────────────────────────
    const order = { critique: 0, urgent: 1, info: 2 }
    allTasks.sort((a, b) => order[a.urgency] - order[b.urgency])
    setTasks(allTasks)
    setLoading(false)
  }, [userId, bienIds.join(','), paiements, lang])  // eslint-disable-line

  useEffect(() => { loadTasks() }, [loadTasks])

  const handlePontoConfirm = async (task) => {
  setConfirmingPonto(task.id)
  try {
    const token = await getToken()
    const res = await fetch('/api/router', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action:     'ponto-confirm',
        paiementId: task.pontoData.paiementId,
        date:       task.pontoData.date,
      }),
    })
    if (res.ok) setTasks(t => t.filter(x => x.id !== task.id))
  } catch(e) { console.error(e) }
  setConfirmingPonto(null)
}

  const getUrgencyStyles = (lang) => ({
    critique: { bg: '#faece7', border: '#e57373', color: '#993C1D', dot: '🔴', label: t(lang,'todo_critique') },
    urgent:   { bg: '#fef9f0', border: '#F5D08A', color: '#854F0B', dot: '🟡', label: t(lang,'todo_a_traiter') },
    info:     { bg: '#f0faf5', border: '#9FE1CB', color: '#0F6E56', dot: '🟢', label: t(lang,'todo_informatif')      },
  })

  const URGENCY = getUrgencyStyles(lang) 

  const countBy = (key, val) => tasks.filter(t => t[key] === val).length

  const FILTERS = [
    { key: 'tous',          label: t(lang,'tous'),          count: tasks.length },
    { key: 'loyers',        label: `💶 ${t(lang,'paiements')}`,      count: countBy('category','loyers') },
    { key: 'echeances',     label: `📅 ${t(lang,'ech_titre')}`,   count: countBy('category','echeances') },
    { key: 'ponto',         label: `🏦 Ponto`,       count: countBy('category','ponto') },
    { key: 'interventions', label: `🔧 ${t(lang,'interv_titre')}`,count: countBy('category','interventions') },
    { key: 'documents',     label: `📁 ${t(lang,'documents')}`,    count: countBy('category','documents') },
  ].filter(f => f.key === 'tous' || f.count > 0)

  const URGENCY_KEYS = ['critique', 'urgent', 'info']
  const filteredTasks = filtre === 'tous'
  ? tasks
  : URGENCY_KEYS.includes(filtre)
    ? tasks.filter(t => t.urgency === filtre)
    : tasks.filter(t => t.category === filtre)

  if (loading) return <div className="empty">{t(lang,'chargement')}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Header ── */}
      <div className="page-header-row">
        <div>
          <div className="page-title">
            ✅ {t(lang,'todo')}
            {tasks.length > 0 && <span className="alerte-badge">{tasks.length}</span>}
          </div>
          <div className="page-sub">{t(lang,'todo_sub')}</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={loadTasks}>{t(lang,'actualiser')}</button>
      </div>

      {/* Toggle vue */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #eee' }}>
        <button className={`dossier-tab ${vue === 'taches' ? 'active' : ''}`} onClick={() => setVue('taches')}>
          ✅ {t(lang,'tr_vue_taches')}
        </button>
        <button className={`dossier-tab ${vue === 'recurrentes' ? 'active' : ''}`} onClick={() => setVue('recurrentes')}>
          🔧 {t(lang,'tr_vue_recurrentes')}
        </button>
      </div>

      {vue === 'taches' && (<>
      {/* ── Stats ── */}
      {tasks.length > 0 && (
        <div className="stats-grid">
          {[
            { label: t(lang,'todo_critique'), key: 'critique', cls: 'red'    },
            { label: t(lang,'todo_a_traiter'), key: 'urgent',   cls: 'orange' },
            { label: t(lang,'todo_informatif'), key: 'info',     cls: ''       },
            { label: t(lang,'todo_total'),     key: null,       cls: ''       },
          ].map(s => {
            const n = s.key ? countBy('urgency', s.key) : tasks.length
            return (
              <div key={s.label} className="stat-card"
                style={{ cursor: 'pointer', borderColor: n > 0 && s.key === 'critique' ? '#e57373' : n > 0 && s.key === 'urgent' ? '#F5D08A' : '#eee' }}
                onClick={() => setFiltre(s.key || 'tous')}
              >
                <div className="stat-label">{s.label}</div>
                <div className={`stat-value ${n > 0 ? s.cls : ''}`}>{n}</div>
              </div>
            )
          })}
        </div>
      )}

      {/* ── Filtres ── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button key={f.key}
            className={`btn btn-sm ${filtre === f.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setFiltre(f.key)}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {/* ── Liste ── */}
      {filteredTasks.length === 0 ? (
        <div className="card">
          <div className="empty">
            {tasks.length === 0 ? t(lang,'todo_ok') : t(lang,'todo_vide_cat')}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filteredTasks.map(task => {
            const s = URGENCY[task.urgency]
            return (
              <div key={task.id} style={{ background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 24, flexShrink: 0 }}>{task.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>{task.title}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: s.border, color: s.color }}>
                      {s.dot} {s.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#666', lineHeight: 1.4 }}>{task.desc}</div>
                </div>
                  {task.pontoData ? (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      className="btn btn-sm"
                      style={{ background: '#1D9E75', color: '#fff', borderColor: '#1D9E75' }}
                      disabled={confirmingPonto === task.id}
                      onClick={() => handlePontoConfirm(task)}
                    >
                      {confirmingPonto === task.id ? '⏳' : t(lang,'confirmer_btn')}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      disabled={confirmingPonto === task.id}
                      onClick={async () => {
                        setConfirmingPonto(task.id)
                        try {
                          const token = await getToken()
                          await fetch('/api/router', {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              action:     'ponto-refuse',
                              paiementId: task.pontoData.paiementId,
                            }),
                          })
                          setTasks(t => t.filter(x => x.id !== task.id))
                        } catch(e) { console.error(e) }
                        setConfirmingPonto(null)
                      }}
                    >
                      t(lang,'refuser_btn')
                    </button>
                  </div>
                ) : (
                  <button className="btn btn-sm btn-ghost" style={{ flexShrink: 0 }} onClick={() => setPage(task.ctaPage)}>
                    {task.ctaLabel} →
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
      </>)}

      {vue === 'recurrentes' && (
  plan !== 'free'
    ? <TachesRecurrentes userId={userId} biens={biens} lang={lang} />
    : <ProUpgradePrompt onGoToPricing={() => setPage('pricing')} lang={lang} titleKey="starter_feature_titre" descKey="tr_starter_desc" btnKey="voir_plans" />
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
//  COMPOSANT RÉUTILISABLE — Historique d'évolution du loyer
// ═══════════════════════════════════════════════════════════════

function HistoriqueLoyers({ locationId, bienId, userId, loyerActuel, lang = 'fr' }) {
  const [historique, setHistorique] = useState([])
  const [loading, setLoading]       = useState(true)

  useEffect(() => {
    let annule = false
    const load = async () => {
      let query = supabase.from('loyer_historique').select('*').eq('user_id', userId).order('date_application', { ascending: false })
      if (locationId)      query = query.eq('location_id', locationId)
      else if (bienId)     query = query.eq('bien_id', bienId)
      const { data } = await query
      if (!annule) { setHistorique(data || []); setLoading(false) }
    }
    load()
    return () => { annule = true }
  }, [locationId, bienId, userId])

  const fmtPct = (p) => `${p > 0 ? '+' : ''}${p}%`

  if (loading) return <div className="empty" style={{ padding: 24 }}>{t(lang,'chargement')}…</div>

  if (historique.length === 0) {
    return (
      <div className="empty" style={{ padding: 32, textAlign: 'center', color: '#888' }}>
        📉 {t(lang,'idx_hist_aucun')}
      </div>
    )
  }

  // Loyer initial = loyer_avant de la plus ancienne indexation
  const plusAncienne = historique[historique.length - 1]
  const loyerInitial = plusAncienne ? plusAncienne.loyer_avant : null
  const evolutionTotale = (loyerInitial && loyerActuel)
    ? Math.round(((loyerActuel / loyerInitial - 1) * 100) * 100) / 100
    : null

  return (
    <div>
      {/* Résumé évolution */}
      {evolutionTotale != null && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <div style={{ background: '#f7f7f5', border: '1px solid #eee', borderRadius: 10, padding: '12px 16px', flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t(lang,'idx_hist_initial')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0d2b1f' }}>{fmt(loyerInitial)}</div>
          </div>
          <div style={{ background: '#f7f7f5', border: '1px solid #eee', borderRadius: 10, padding: '12px 16px', flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t(lang,'idx_hist_actuel')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0d2b1f' }}>{fmt(loyerActuel)}</div>
          </div>
          <div style={{ background: '#f0faf6', border: '1px solid #a7f0d4', borderRadius: 10, padding: '12px 16px', flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t(lang,'idx_hist_evolution')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0F6E56' }}>{fmtPct(evolutionTotale)}</div>
          </div>
        </div>
      )}

      {/* Timeline des indexations */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {historique.map(h => (
          <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fff', border: '1px solid #eee', borderRadius: 10 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>
                {fmt(h.loyer_avant)} → {fmt(h.loyer_apres)}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                {new Date(h.date_application).toLocaleDateString(_langLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
                {h.source === 'indexation' && h.indice_depart != null
                  ? ` · ${t(lang,'idx_hist_indice')} ${h.indice_depart} → ${h.indice_nouveau}`
                  : ` · ${t(lang,'idx_hist_manuel')}`}
              </div>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
              background: h.pourcentage >= 0 ? '#f0faf6' : '#faece7',
              color: h.pourcentage >= 0 ? '#0F6E56' : '#993C1D' }}>
              {fmtPct(h.pourcentage)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
//  DÉCOMPTE DE CHARGES — onglet du LocationPanel
//  Saisie des factures, calcul provisions vs charges réelles,
//  génération du solde de régularisation.
// ═══════════════════════════════════════════════════════════════

const CHARGES_CATEGORIES = [
  { key: 'eau',       icon: '💧', fr: 'Eau',               en: 'Water',          nl: 'Water' },
  { key: 'chauffage', icon: '🔥', fr: 'Chauffage',         en: 'Heating',        nl: 'Verwarming' },
  { key: 'electricite',icon:'⚡', fr: 'Électricité communs', en: 'Common electricity', nl: 'Elektriciteit gemeen' },
  { key: 'communs',   icon: '🏢', fr: 'Charges communes',  en: 'Common charges', nl: 'Gemeenschappelijke kosten' },
  { key: 'entretien', icon: '🔧', fr: 'Entretien',         en: 'Maintenance',    nl: 'Onderhoud' },
  { key: 'ordures',   icon: '🗑️', fr: 'Ordures ménagères', en: 'Waste collection', nl: 'Huisvuil' },
  { key: 'autre',     icon: '📄', fr: 'Autre',             en: 'Other',          nl: 'Andere' },
]

function categorieLabel(key, lang) {
  const c = CHARGES_CATEGORIES.find(x => x.key === key)
  if (!c) return key
  return lang === 'en' ? c.en : lang === 'nl' ? c.nl : c.fr
}
function categorieIcon(key) {
  const c = CHARGES_CATEGORIES.find(x => x.key === key)
  return c ? c.icon : '📄'
}

function ChargesDecompte({ location, bien, biens = [], locataire, proprio, userId, lang = 'fr', getToken }) {
  const anneeActuelle = new Date().getFullYear()
  const [annee, setAnnee]         = useState(anneeActuelle - 1) // décompte = année précédente en général
  const [factures, setFactures]   = useState([])
  const [facturesCommunes, setFacturesCommunes] = useState([])
  const [immeuble, setImmeuble]   = useState(null)
  const [loading, setLoading]     = useState(true)
  const [showForm, setShowForm]   = useState(false)
  const [provisionManuelle, setProvisionManuelle] = useState(null)
  const [sending, setSending]     = useState(false)
  const [sent, setSent]           = useState(false)
  const [form, setForm] = useState({
    categorie: 'eau', libelle: '', montant: '', date_facture: '', recuperable: true, annee_rattachement: '',
  })

  const [paiementsAnnee, setPaiementsAnnee] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    const requests = [
      supabase.from('charges_factures').select('*').eq('location_id', location.id).eq('annee', annee).order('date_facture', { ascending: true }),
      supabase.from('paiements').select('charges, statut, montant_paye, montant, mois').eq('bien_id', location.bien_id).eq('locataire_id', location.locataire_id).gte('mois', `${annee}-01-01`).lte('mois', `${annee}-12-31`),
    ]
    // Si le bien appartient à un immeuble, charger ses charges communes + l'immeuble
    if (bien.immeuble_id) {
      requests.push(
        supabase.from('charges_factures').select('*').eq('immeuble_id', bien.immeuble_id).eq('annee', annee).order('date_facture', { ascending: true }),
        supabase.from('immeubles').select('*').eq('id', bien.immeuble_id).single()
      )
    }
    const results = await Promise.all(requests)
    setFactures(results[0].data || [])
    setPaiementsAnnee(results[1].data || [])
    if (bien.immeuble_id) {
      setFacturesCommunes(results[2].data || [])
      setImmeuble(results[3].data || null)
    } else {
      setFacturesCommunes([])
      setImmeuble(null)
    }
    setLoading(false)
  }, [location.id, location.bien_id, location.locataire_id, annee, bien.immeuble_id])

  useEffect(() => { load() }, [load])

  // ── Calcul des provisions versées sur l'année ──
  // Provisions versées = somme des charges des paiements encaissés de l'année
  const calcProvisionsAuto = () => {
    return paiementsAnnee
      .filter(p => p.statut === 'paid' || p.statut === 'partial')
      .reduce((s, p) => s + Number(p.charges || 0), 0)
  }

  const provisionsAuto = calcProvisionsAuto()
  const provisions = provisionManuelle != null ? provisionManuelle : provisionsAuto

  // ── Totaux charges ──
  const chargesRecuperables = factures.filter(f => f.recuperable).reduce((s, f) => s + Number(f.montant), 0)
  const chargesNonRecup     = factures.filter(f => !f.recuperable).reduce((s, f) => s + Number(f.montant), 0)

  // ── Quote-part des charges communes de l'immeuble ──
  // Chaque facture commune peut avoir sa PROPRE clé de répartition
  // (eau=habitants, chauffage=millièmes...). On calcule facture par facture.
  const lotsImmeuble = (immeuble && bien.immeuble_id)
    ? biens.filter(b => b.immeuble_id === bien.immeuble_id)
    : []

  // Fraction de présence du locataire sur l'année du décompte (prorata temporel)
  // Bornée à l'année ; si pas de date_fin (locataire encore présent), fin = 31/12
  const fractionPresence = (() => {
    const anneeDebut = new Date(annee, 0, 1)
    const anneeFin   = new Date(annee, 11, 31)
    const joursAnnee = Math.round((anneeFin - anneeDebut) / 86400000) + 1
    const debut = location.date_debut ? new Date(location.date_debut) : anneeDebut
    const fin   = location.date_fin   ? new Date(location.date_fin)   : anneeFin
    const debutEff = debut > anneeDebut ? debut : anneeDebut
    const finEff   = fin   < anneeFin   ? fin   : anneeFin
    if (finEff < debutEff) return 0
    const jours = Math.round((finEff - debutEff) / 86400000) + 1
    return Math.min(1, jours / joursAnnee)
  })()

  // Détail facture par facture : chaque charge commune avec sa clé propre
  // detailCommunes = [{ id, categorie, libelle, cle, montantTotal, fraction, presence, quotePart }]
  const detailCommunes = (() => {
    if (!immeuble || !bien.immeuble_id) return []
    return facturesCommunes.filter(f => f.recuperable).map(f => {
      const cle = f.cle_repartition || immeuble.cle_repartition
      const fractions = calculerQuotesParts(lotsImmeuble, cle)
      const fraction = fractions[bien.id] || 0
      return {
        id: f.id,
        categorie: f.categorie,
        libelle: f.libelle,
        cle,
        montantTotal: Number(f.montant),
        fraction,
        presence: fractionPresence,
        quotePart: Math.round(Number(f.montant) * fraction * fractionPresence * 100) / 100,
      }
    })
  })()

  const quotePartCommune = Math.round(detailCommunes.reduce((s, d) => s + d.quotePart, 0) * 100) / 100

  // Total récupérable = charges privatives + quote-part des charges communes
  const totalRecuperable = chargesRecuperables + quotePartCommune
  const solde = provisions - totalRecuperable   // + = remboursement au locataire, - = complément dû

  // Regroupement par catégorie pour affichage
  const parCategorie = {}
  for (const f of factures.filter(f => f.recuperable)) {
    parCategorie[f.categorie] = (parCategorie[f.categorie] || 0) + Number(f.montant)
  }

  const handleAddFacture = async () => {
    if (!form.montant || !form.date_facture) return
    await supabase.from('charges_factures').insert({
      user_id: userId,
      bien_id: bien.id,
      location_id: location.id,
      categorie: form.categorie,
      libelle: form.libelle || null,
      montant: parseFloat(form.montant),
      date_facture: form.date_facture,
      annee: parseInt(form.annee_rattachement) || new Date(form.date_facture).getFullYear(),
      recuperable: form.recuperable,
    })
    setForm({ categorie: 'eau', libelle: '', montant: '', date_facture: '', recuperable: true, annee_rattachement: '' })
    setShowForm(false)
    load()
  }

  const handleDeleteFacture = async (id) => {
    await supabase.from('charges_factures').delete().eq('id', id)
    load()
  }

  const fmtE = (n) => Number(n).toFixed(2).replace('.', ',')

  // ── Génère + envoie le décompte au locataire ──
  const handleGenerer = async () => {
    // Enregistre le décompte
    await supabase.from('charges_decomptes').insert({
      user_id: userId,
      bien_id: bien.id,
      location_id: location.id,
      locataire_id: location.locataire_id,
      annee,
      total_provisions: provisions,
      total_charges: totalRecuperable,
      solde,
    })
    setSent('saved')
  }

  const handleEnvoyer = async () => {
    if (!locataire?.email) return
    setSending(true)
    try {
      const token = await getToken()
      const corps = lettreDecompte()
      await fetch('/api/router', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          action: 'send-decompte',
          locataireEmail: locataire.email,
          locataireNom: `${locataire.prenom} ${locataire.nom}`,
          locataireId: location.locataire_id,
          objet: `${t(lang,'ch_mail_objet')} ${annee}`,
          corps,
        }),
      })
      await supabase.from('charges_decomptes')
        .update({ envoye_locataire: true })
        .eq('location_id', location.id).eq('annee', annee)
      setSent('envoye')
    } catch (e) { /* silencieux */ }
    setSending(false)
  }

  const lettreDecompte = () => {
    const L = locataire?.lang || lang
    const soldeAbs = fmtE(Math.abs(solde))
    const enFaveur = solde >= 0
      ? (L === 'nl' ? 'in uw voordeel (terugbetaling)' : L === 'en' ? 'in your favour (refund)' : 'en votre faveur (remboursement)')
      : (L === 'nl' ? 'te betalen (supplement)' : L === 'en' ? 'due (additional payment)' : 'à payer (complément)')

    // Libellé de clé de répartition selon langue
    const cleTxt = (cle) => {
      const map = {
        quotite:   { fr: 'quotités', nl: 'quotiteiten', en: 'shares' },
        surface:   { fr: 'surface',  nl: 'oppervlakte', en: 'surface' },
        habitants: { fr: 'nb habitants', nl: 'aantal bewoners', en: 'occupants' },
        egale:     { fr: 'parts égales', nl: 'gelijke delen', en: 'equal shares' },
      }
      return (map[cle] || map.quotite)[L] || cle
    }

    // Charges privatives détaillées
    const privList = factures.filter(f => f.recuperable).map(f => {
      const lib = `${categorieLabel(f.categorie, L)}${f.libelle ? ` (${f.libelle})` : ''}`
      return `   • ${lib} : ${fmtE(f.montant)} €`
    }).join('\n')

    // Charges communes détaillées (avec clé + prorata présence)
    const presPct = (p) => p < 0.999 ? ` × ${(p * 100).toFixed(1)}% ${L === 'nl' ? 'aanwezigheid' : L === 'en' ? 'occupancy' : 'présence'}` : ''
    const commList = detailCommunes.filter(d => d.quotePart > 0).map(d => {
      const lib = `${categorieLabel(d.categorie, L)}${d.libelle ? ` (${d.libelle})` : ''}`
      return `   • ${lib} : ${fmtE(d.montantTotal)} € × ${(d.fraction * 100).toFixed(1)}% [${cleTxt(d.cle)}]${presPct(d.presence)} = ${fmtE(d.quotePart)} €`
    }).join('\n')

    // Blocs de texte par langue
    const T = {
      fr: {
        hello: `Cher/Chère ${locataire.prenom} ${locataire.nom},`,
        intro: `Veuillez trouver le décompte de charges pour l'année ${annee} concernant le bien ${bien.adresse}.`,
        priv: 'CHARGES PRIVATIVES',
        comm: 'QUOTE-PART CHARGES COMMUNES',
        totalRecup: 'Total charges récupérables',
        prov: 'Provisions versées',
        solde: 'Solde',
        none: '   (aucune)',
        bye: `Bien à vous,\n${proprio?.proprio_nom || ''}`,
      },
      nl: {
        hello: `Beste ${locataire.prenom} ${locataire.nom},`,
        intro: `Hierbij de afrekening van de kosten voor het jaar ${annee} betreffende het pand ${bien.adresse}.`,
        priv: 'PRIVATIEVE KOSTEN',
        comm: 'AANDEEL GEMEENSCHAPPELIJKE KOSTEN',
        totalRecup: 'Totaal recupereerbare kosten',
        prov: 'Betaalde provisies',
        solde: 'Saldo',
        none: '   (geen)',
        bye: `Met vriendelijke groeten,\n${proprio?.proprio_nom || ''}`,
      },
      en: {
        hello: `Dear ${locataire.prenom} ${locataire.nom},`,
        intro: `Please find the charges statement for the year ${annee} regarding the property ${bien.adresse}.`,
        priv: 'PRIVATE CHARGES',
        comm: 'SHARE OF COMMON CHARGES',
        totalRecup: 'Total recoverable charges',
        prov: 'Provisions paid',
        solde: 'Balance',
        none: '   (none)',
        bye: `Kind regards,\n${proprio?.proprio_nom || ''}`,
      },
    }
    const x = T[L] || T.fr

    let corps = `${x.hello}\n\n${x.intro}\n\n`
    corps += `${x.priv}\n${privList || x.none}\n`
    corps += `   ${L === 'fr' ? 'Sous-total privatif' : L === 'nl' ? 'Subtotaal privatief' : 'Private subtotal'} : ${fmtE(chargesRecuperables)} €\n\n`
    if (commList) {
      corps += `${x.comm}\n${commList}\n`
      corps += `   ${L === 'fr' ? 'Sous-total quote-part' : L === 'nl' ? 'Subtotaal aandeel' : 'Share subtotal'} : ${fmtE(quotePartCommune)} €\n\n`
    }
    corps += `${x.totalRecup} : ${fmtE(totalRecuperable)} €\n`
    corps += `${x.prov} : ${fmtE(provisions)} €\n`
    corps += `${x.solde} : ${soldeAbs} € ${enFaveur}\n\n`
    corps += x.bye
    return corps
  }

  if (loading) return <div className="empty" style={{ padding: 32 }}>{t(lang,'chargement')}…</div>

  return (
    <div>
      {/* Sélecteur d'année */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13, color: '#888' }}>{t(lang,'ch_annee')}</label>
          <select value={annee} onChange={e => { setAnnee(parseInt(e.target.value)); setProvisionManuelle(null) }}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd' }}>
            {[anneeActuelle, anneeActuelle-1, anneeActuelle-2, anneeActuelle-3].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        {!showForm && (
          <button className="btn btn-sm btn-primary" onClick={() => setShowForm(true)}>+ {t(lang,'ch_ajouter')}</button>
        )}
      </div>

      {/* Formulaire facture */}
      {showForm && (
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <div className="form-grid">
            <div className="form-group">
              <label>{t(lang,'ch_categorie')}</label>
              <select value={form.categorie} onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}>
                {CHARGES_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {categorieLabel(c.key, lang)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t(lang,'ch_montant')} *</label>
              <input type="number" value={form.montant} onChange={e => setForm(f => ({ ...f, montant: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t(lang,'ch_date')} *</label>
              <input type="date" value={form.date_facture} onChange={e => {
                const y = e.target.value ? new Date(e.target.value).getFullYear() : ''
                setForm(f => ({ ...f, date_facture: e.target.value, annee_rattachement: f.rattach_manuel ? f.annee_rattachement : y }))
              }} />
            </div>
            <div className="form-group">
              <label>{t(lang,'ch_annee_rattach')}</label>
              <select value={form.annee_rattachement} onChange={e => setForm(f => ({ ...f, annee_rattachement: e.target.value, rattach_manuel: true }))}>
                {[anneeActuelle, anneeActuelle-1, anneeActuelle-2, anneeActuelle-3].map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="form-group full">
              <label>{t(lang,'ch_libelle')}</label>
              <input value={form.libelle} onChange={e => setForm(f => ({ ...f, libelle: e.target.value }))} placeholder={t(lang,'ch_libelle_ph')} />
            </div>
            <div className="form-group full">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.recuperable} onChange={e => setForm(f => ({ ...f, recuperable: e.target.checked }))} />
                {t(lang,'ch_recuperable')}
              </label>
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>{t(lang,'annuler_btn')}</button>
            <button className="btn btn-primary" onClick={handleAddFacture}>{t(lang,'ajouter')}</button>
          </div>
        </div>
      )}

      {/* Liste des factures */}
      {factures.length === 0 ? (
        <div className="empty" style={{ padding: 24, textAlign: 'center', color: '#888' }}>
          📄 {t(lang,'ch_aucune')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
          {factures.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#fff', border: '1px solid #eee', borderRadius: 8 }}>
              <span style={{ fontSize: 18 }}>{categorieIcon(f.categorie)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0d2b1f' }}>
                  {categorieLabel(f.categorie, lang)}
                  {!f.recuperable && <span style={{ fontSize: 10, marginLeft: 6, padding: '1px 6px', background: '#f0f0f0', color: '#888', borderRadius: 10 }}>{t(lang,'ch_non_recup')}</span>}
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>{f.libelle || ''} · {new Date(f.date_facture).toLocaleDateString(_langLocale)}</div>
              </div>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>{fmtE(f.montant)} €</span>
              <button onClick={() => handleDeleteFacture(f.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#993C1D', fontSize: 14 }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Synthèse du décompte */}
      <div className="card" style={{ padding: 18, background: '#fafafa' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0d2b1f', marginBottom: 14 }}>{t(lang,'ch_synthese')} {annee}</div>

        {/* Provisions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee' }}>
          <span style={{ fontSize: 13, color: '#555' }}>{t(lang,'ch_provisions')}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" value={provisions}
              onChange={e => setProvisionManuelle(parseFloat(e.target.value) || 0)}
              style={{ width: 90, padding: '4px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, textAlign: 'right' }} />
            <span style={{ fontSize: 13 }}>€</span>
            {provisionManuelle != null && (
              <button onClick={() => setProvisionManuelle(null)} title={t(lang,'ch_reset_auto')}
                style={{ fontSize: 11, background: 'none', border: 'none', color: '#1D9E75', cursor: 'pointer' }}>↺</button>
            )}
          </div>
        </div>

        {/* Charges privatives */}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
          <span style={{ fontSize: 13, color: '#555' }}>{quotePartCommune > 0 ? t(lang,'ch_privatives') : t(lang,'ch_total_recup')}</span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{fmtE(chargesRecuperables)} €</span>
        </div>
        {/* Quote-part charges communes — détail facture par facture */}
        {quotePartCommune > 0 && (
          <div style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>🏢 {t(lang,'ch_quote_part')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 8 }}>
              {detailCommunes.filter(d => d.quotePart > 0).map(d => (
                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                  <span style={{ color: '#888', flex: 1, minWidth: 0 }}>
                    {categorieIcon(d.categorie)} {categorieLabel(d.categorie, lang)}
                    {d.libelle ? ` · ${d.libelle}` : ''}
                    <span style={{ color: '#aaa' }}> · {fmtE(d.montantTotal)} € × {(d.fraction * 100).toFixed(1)}%{d.presence < 0.999 ? ` × ${(d.presence * 100).toFixed(1)}% ${t(lang,'ch_presence')}` : ''}</span>
                  </span>
                  <span style={{ fontWeight: 600, color: '#0d2b1f', marginLeft: 8 }}>{fmtE(d.quotePart)} €</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Total récupérable (si charges communes) */}
        {quotePartCommune > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0d2b1f' }}>{t(lang,'ch_total_recup')}</span>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{fmtE(totalRecuperable)} €</span>
          </div>
        )}
        {/* Charges non récupérables (info) */}
        {chargesNonRecup > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <span style={{ fontSize: 13, color: '#aaa' }}>{t(lang,'ch_non_recup')}</span>
            <span style={{ fontSize: 13, color: '#aaa' }}>{fmtE(chargesNonRecup)} €</span>
          </div>
        )}

        {/* Solde */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0 4px' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#0d2b1f' }}>{t(lang,'ch_solde')}</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: solde >= 0 ? '#0F6E56' : '#993C1D' }}>
            {solde >= 0 ? '+' : ''}{fmtE(solde)} €
          </span>
        </div>
        <div style={{ fontSize: 12, color: '#888', textAlign: 'right' }}>
          {solde >= 0 ? t(lang,'ch_remboursement') : t(lang,'ch_complement')}
        </div>

        {/* Actions */}
        {factures.length > 0 && (
          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={handleGenerer} style={{ fontSize: 13 }}>
              💾 {t(lang,'ch_enregistrer')}
            </button>
            {locataire?.email && (
              <button className="btn" disabled={sending}
                onClick={handleEnvoyer}
                style={{ fontSize: 13, background: sent === 'envoye' ? '#e1f5ee' : '#f0f0f0', color: sent === 'envoye' ? '#0F6E56' : '#333' }}>
                {sent === 'envoye' ? `✓ ${t(lang,'ch_envoye')}` : `✉️ ${t(lang,'ch_envoyer')}`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}


function LocationPanel({ location, biens, locataires, typesBail, userId, proprio, getToken, plan, onClose, onRefresh, lang = 'fr' }) {
  const [onglet, setOnglet] = useState('infos')
  const [form, setForm] = useState({
    loyer:            location.loyer,
    charges:          location.charges || 0,
    garantie:         location.garantie || '',
    date_debut:       location.date_debut || '',
    date_fin:         location.date_fin || '',
    statut:           location.statut,
    notes:            location.notes || '',
  })
  const [bail, setBail] = useState({
    type_bail_id:     location.type_bail_id || '',
    date_signature:   location.date_signature || '',
    indice_reference: location.indice_reference || '',
    indice_nom:       location.indice_nom || '',
    irl_trimestre_ref: location.irl_trimestre_ref || '',
    irl_annee_ref:    location.irl_annee_ref || '',
    zone_tendue:      location.zone_tendue || false,
    bail_url:         location.bail_url || null,
    clauses:          location.clauses || '',
  })
  const [echeances, setEcheances]   = useState([])
  const [paiements, setPaiements]   = useState([])
  const [saving, setSaving]         = useState(false)
  const [saved, setSaved]           = useState(false)
  const [uploadingBail, setUploadingBail] = useState(false)
  const [generatingEch, setGeneratingEch] = useState(false)

  const bien      = biens.find(b => b.id === location.bien_id)
  const locataire = locataires.find(l => l.id === location.locataire_id)

  const loadEcheances = useCallback(async () => {
    const { data } = await supabase
      .from('echeances')
      .select('*')
      .eq('locataire_id', location.locataire_id)
      .order('date_echeance', { ascending: true })
    setEcheances(data || [])

  }, [location.locataire_id])

  const loadPaiements = useCallback(async () => {
    const { data } = await supabase
      .from('paiements')
      .select('*')
      .eq('locataire_id', location.locataire_id)
      .eq('bien_id', location.bien_id)
      .order('mois', { ascending: false })
    setPaiements(data || [])
  }, [location.locataire_id, location.bien_id])

  useEffect(() => {
    if (onglet === 'echeances') loadEcheances()
    if (onglet === 'paiements') loadPaiements()
  }, [onglet, loadEcheances, loadPaiements])

  const handleSaveInfos = async () => {
    setSaving(true)
    const nouveauLoyer = parseFloat(form.loyer)
    const ancienLoyer = parseFloat(location.loyer)

    await supabase.from('locations').update({
      loyer:      nouveauLoyer,
      charges:    parseFloat(form.charges) || 0,
      garantie:   form.garantie ? parseFloat(form.garantie) : null,
      date_debut: form.date_debut,
      date_fin:   form.date_fin || null,
      statut:     form.statut,
      notes:      form.notes || null,
      updated_at: new Date().toISOString(),
    }).eq('id', location.id)

    // Tracer le changement manuel de loyer (si modifié)
    if (!isNaN(nouveauLoyer) && nouveauLoyer !== ancienLoyer) {
      const pct = ancienLoyer > 0 ? Math.round(((nouveauLoyer / ancienLoyer - 1) * 100) * 100) / 100 : null
      await supabase.from('loyer_historique').insert({
        user_id:       userId,
        locataire_id:  location.locataire_id,
        bien_id:       location.bien_id,
        location_id:   location.id,
        loyer_avant:   ancienLoyer,
        loyer_apres:   nouveauLoyer,
        pourcentage:   pct,
        source:        'manuel',
      })
      // Cohérence : mettre aussi à jour le loyer du bien
      if (location.bien_id) {
        await supabase.from('biens').update({ loyer: nouveauLoyer }).eq('id', location.bien_id)
      }
    }

    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2500)
    onRefresh()
  }

  const handleSaveBail = async () => {
    setSaving(true)
    await supabase.from('locations').update({
      type_bail_id:     bail.type_bail_id || null,
      date_signature:   bail.date_signature || null,
      indice_reference: bail.indice_reference ? parseFloat(bail.indice_reference) : null,
      indice_nom:       bail.indice_nom || null,
      irl_trimestre_ref: bail.irl_trimestre_ref ? parseInt(bail.irl_trimestre_ref) : null,
      irl_annee_ref:    bail.irl_annee_ref ? parseInt(bail.irl_annee_ref) : null,
      zone_tendue:      bail.zone_tendue || false,
      bail_url:         bail.bail_url || null,
      clauses:          bail.clauses || null,
      updated_at:       new Date().toISOString(),
    }).eq('id', location.id)
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2500)
    onRefresh()
  }

  const handleUploadBail = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingBail(true)
    const path = `${userId}/locations/${location.id}/bail.pdf`
    await supabase.storage.from('documents').upload(path, file, { upsert: true })
    const { data: urlData } = supabase.storage.from('documents').getPublicUrl(path)
    setBail(b => ({ ...b, bail_url: urlData.publicUrl }))
    setUploadingBail(false)
  }

  const handleGenererEcheances = async () => {
    if (!bail.type_bail_id || !bail.date_signature) {
      alert(t(lang,'loc_bail_requis'))
      return
    }
    setGeneratingEch(true)
    const { data: typeBail } = await supabase.from('types_bail').select('*').eq('id', bail.type_bail_id).single()
    if (typeBail) {
      const locFull = {
        id:               location.locataire_id,
        date_signature:   bail.date_signature,
        indice_reference: bail.indice_reference,
        indice_nom:       bail.indice_nom,
      }
      await generateEcheancesForLocataire(locFull, typeBail, userId, lang)
      await loadEcheances()
      setOnglet('echeances')
    }
    setGeneratingEch(false)
  }

  const totalLoyers   = paiements.reduce((s, p) => p.statut === 'paid' ? s + Number(p.montant) : s, 0)
  const loyersEnRetard = paiements.filter(p => p.statut === 'late').length

  return (
    <div className="dossier-overlay" onClick={onClose}>
      <div className="dossier-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="dossier-header">
          <div>
            <div className="dossier-header-name">
              {locataire?.prenom} {locataire?.nom}
            </div>
            <div className="dossier-header-sub">
              {bien?.adresse?.split(',')[0]} · {t(lang,'depuis')} {fmtDate(location.date_debut)}
            </div>
          </div>
          <button className="dossier-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div className="dossier-tabs">
          <button className={`dossier-tab ${onglet === 'infos' ? 'active' : ''}`} onClick={() => setOnglet('infos')}>
            📋 {t(lang,'loc_panel_infos')}
          </button>
          <button className={`dossier-tab ${onglet === 'bail' ? 'active' : ''}`} onClick={() => setOnglet('bail')}>
            📄 {t(lang,'loc_panel_bail')}
          </button>
          <button className={`dossier-tab ${onglet === 'echeances' ? 'active' : ''}`} onClick={() => setOnglet('echeances')}>
            📅 {t(lang,'loc_panel_echeances')} {echeances.filter(e => !e.traitee).length > 0 && `(${echeances.filter(e => !e.traitee).length})`}
          </button>
          <button className={`dossier-tab ${onglet === 'paiements' ? 'active' : ''}`} onClick={() => setOnglet('paiements')}>
            💶 {t(lang,'loc_panel_paiements')}
          </button>
          <button className={`dossier-tab ${onglet === 'historique' ? 'active' : ''}`} onClick={() => setOnglet('historique')}>
            📈 {t(lang,'idx_hist_onglet')}
          </button>
          <button className={`dossier-tab ${onglet === 'charges' ? 'active' : ''}`} onClick={() => setOnglet('charges')}>
            🧾 {t(lang,'ch_onglet')}
            {plan !== 'pro' && plan !== 'enterprise' && <span style={{ fontSize: 8, fontWeight: 700, background: '#B8860B', color: '#fff', padding: '1px 5px', borderRadius: 4, marginLeft: 4, letterSpacing: 0.5 }}>PRO</span>}
          </button>
        </div>

        {/* Body */}
        <div className="dossier-body">

          {/* ── INFOS ── */}
          {onglet === 'infos' && (
            <>
              {/* Stats rapides */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div style={{ background: '#f0faf5', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{t(lang,'loyer')}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#0d2b1f' }}>{fmt(location.loyer)}</div>
                </div>
                <div style={{ background: '#f0faf5', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{t(lang,'garantie_lbl')}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#0d2b1f' }}>{location.garantie ? fmt(location.garantie) : '—'}</div>
                </div>
                <div style={{ background: location.statut === 'active' ? '#f0faf5' : '#fef9f0', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{t(lang,'statut')}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: location.statut === 'active' ? '#0F6E56' : '#854F0B' }}>
                    {location.statut === 'active' ? `✓ ${t(lang,'actifs_tab')}` : location.statut === 'terminee' ? t(lang,'loc_terminee') : t(lang,'loc_archivee')}
                  </div>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>{t(lang,'loyer')}</label>
                  <input type="number" value={form.loyer} onChange={e => setForm(f => ({ ...f, loyer: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t(lang,'charges')}</label>
                  <input type="number" value={form.charges} onChange={e => setForm(f => ({ ...f, charges: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t(lang,'garantie_lbl')}</label>
                  <input type="number" value={form.garantie} onChange={e => setForm(f => ({ ...f, garantie: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t(lang,'statut')}</label>
                  <select value={form.statut} onChange={e => setForm(f => ({ ...f, statut: e.target.value }))}>
                    <option value="active">{t(lang,'loc_active')}</option>
                    <option value="terminee">{t(lang,'loc_terminee')}</option>
                    <option value="archivee">{t(lang,'loc_archivee')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t(lang,'loc_debut')}</label>
                  <input type="date" value={form.date_debut} onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t(lang,'loc_fin')}</label>
                  <input type="date" value={form.date_fin || ''} onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))} />
                </div>
                <div className="form-group full">
                  <label>{t(lang,'notes')}</label>
                  <input value={form.notes} placeholder={t(lang,'ph_notes')} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                {saved && <span style={{ fontSize: 13, color: '#1D9E75', fontWeight: 600 }}>{t(lang,'enregistre')}</span>}
                <button className="btn btn-primary" onClick={handleSaveInfos} disabled={saving}>
                  {saving ? t(lang,'enreg_en_cours') : t(lang,'enregistrer')}
                </button>
              </div>
            </>
          )}

          {/* ── BAIL ── */}
          {onglet === 'bail' && (
            <>
              <div className="form-grid">
                <div className="form-group full">
                  <label>{t(lang,'type_bail')}</label>
                  <select value={bail.type_bail_id || ''} onChange={e => setBail(b => ({ ...b, type_bail_id: e.target.value || null }))}>
                    <option value="">{t(lang,'aucun')}</option>
                    {typesBail.map(tb => <option key={tb.id} value={tb.id}>{tb.nom}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t(lang,'date_sign')}</label>
                  <input type="date" value={bail.date_signature || ''} onChange={e => setBail(b => ({ ...b, date_signature: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t(lang,'indice_ref')}</label>
                  <input type="number" placeholder={t(lang,'ph_indice_ref')} value={bail.indice_reference || ''} onChange={e => setBail(b => ({ ...b, indice_reference: e.target.value }))} />
                </div>
                <div className="form-group full">
                  <label>{t(lang,'nom_indice')}</label>
                  <input placeholder={t(lang,'ph_indice_nom')} value={bail.indice_nom || ''} onChange={e => setBail(b => ({ ...b, indice_nom: e.target.value }))} />
                </div>
                {bien?.pays === 'FR' && (
                  <>
                    <div className="form-group">
                      <label>{t(lang,'irl_trimestre')}</label>
                      <select value={bail.irl_trimestre_ref || ''} onChange={e => setBail(b => ({ ...b, irl_trimestre_ref: e.target.value }))}>
                        <option value="">{t(lang,'irl_auto')}</option>
                        <option value="1">T1</option><option value="2">T2</option>
                        <option value="3">T3</option><option value="4">T4</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t(lang,'irl_annee')}</label>
                      <input type="number" placeholder="2024" value={bail.irl_annee_ref || ''} onChange={e => setBail(b => ({ ...b, irl_annee_ref: e.target.value }))} />
                    </div>
                    <div className="form-group full">
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={bail.zone_tendue || false} onChange={e => setBail(b => ({ ...b, zone_tendue: e.target.checked }))} style={{ width: 'auto' }} />
                        {t(lang,'irl_zone_tendue')}
                      </label>
                    </div>
                  </>
                )}
                <div className="form-group full">
                  <label>{t(lang,'clauses_part')}</label>
                  <textarea
                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 13, outline: 'none', resize: 'vertical', minHeight: 100 }}
                    placeholder={t(lang,'ph_clauses')}
                    value={bail.clauses || ''}
                    onChange={e => setBail(b => ({ ...b, clauses: e.target.value }))}
                  />
                </div>
              </div>

              {/* Upload bail PDF */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t(lang,'loc_bail_signe')}</div>
                {bail.bail_url ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f0faf5', border: '1px solid #9FE1CB', borderRadius: 8, padding: '10px 14px' }}>
                    <span style={{ fontSize: 13, color: '#0F6E56', flex: 1 }}>{t(lang,'loc_bail_attache')}</span>
                    <a href={bail.bail_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-ghost">⬇️ Télécharger</a>
                    <button className="btn btn-sm btn-danger" onClick={() => setBail(b => ({ ...b, bail_url: null }))}>🗑️</button>
                  </div>
                ) : (
                  <label style={{ cursor: uploadingBail ? 'wait' : 'pointer' }}>
                    <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleUploadBail} />
                    <div className="upload-zone">
                      {uploadingBail
                        ? <div style={{ fontSize: 13, color: '#1D9E75' }}>{t(lang,'envoi_cours')}</div>
                        : <>
                            <div style={{ fontSize: 24, marginBottom: 6 }}>📎</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>{t(lang,'loc_attacher_bail')}</div>
                            <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>{t(lang,'pdf_uniquement')}</div>
                          </>
                      }
                    </div>
                  </label>
                )}
              </div>

              {/* Générer les échéances */}
              <div style={{ background: '#f0faf5', border: '1.5px solid #9FE1CB', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, fontSize: 13, color: '#0F6E56' }}>
                  {t(lang,'loc_generation_ech_info')}
                </div>
                <button className="btn btn-primary" onClick={handleGenererEcheances} disabled={generatingEch || !bail.type_bail_id || !bail.date_signature}>
                  {generatingEch ? `⏳ ${t(lang,'traitement_en_cours')}` : t(lang,'loc_generer_ech')}
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                {saved && <span style={{ fontSize: 13, color: '#1D9E75', fontWeight: 600 }}>{t(lang,'enregistre')}</span>}
                <button className="btn btn-primary" onClick={handleSaveBail} disabled={saving}>
                  {saving ? t(lang,'enreg_en_cours') : t(lang,'enregistrer')}
                </button>
              </div>
            </>
          )}

          {/* ── ÉCHÉANCES ── */}
          {onglet === 'echeances' && (
            <>
              {echeances.length === 0
                ? <div className="empty">{t(lang,'loc_aucune_ech')}</div>
                : echeances.map(e => {
                    const diff  = Math.ceil((new Date(e.date_echeance) - new Date()) / (1000*60*60*24))
                    const color = diff < 0 ? '#aaa' : diff <= 30 ? '#D85A30' : diff <= 60 ? '#854F0B' : '#0F6E56'
                    return (
                      <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <div style={{ fontSize: 22 }}>{e.type === 'renouvellement' ? '🔄' : '📈'}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#0d2b1f' }}>
                            {e.type === 'renouvellement' ? 'Renouvellement' : 'Indexation'}
                          </div>
                          <div style={{ fontSize: 12, color: '#aaa' }}>{fmtDate(e.date_echeance)}</div>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: e.traitee ? '#f0f0f0' : '#f0faf5', color: e.traitee ? '#aaa' : color }}>
                          {e.traitee ? `✓ ${t(lang,'traitee_lbl')}` : diff < 0 ? t(lang,'depassee_lbl') : `J-${diff}`}
                        </span>
                      </div>
                    )
                  })
              }
            </>
          )}
          {onglet === 'historique' && (
            <HistoriqueLoyers locationId={location.id} userId={userId} loyerActuel={parseFloat(form.loyer)} lang={lang} />
          )}
                  {onglet === 'charges' && (
            (plan === 'pro' || plan === 'enterprise')
            ? <ChargesDecompte
                location={location}
                bien={biens.find(b => b.id === location.bien_id) || {}}
                biens={biens}
                locataire={locataires.find(l => l.id === location.locataire_id) || {}}
                proprio={proprio}
                userId={userId}
                lang={lang}
                getToken={getToken}
              />
            : <div style={{ padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>⭐</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#0d2b1f', marginBottom: 8 }}>{t(lang,'ch_pro_titre')}</div>
                <div style={{ fontSize: 14, color: '#888', maxWidth: 360, margin: '0 auto' }}>{t(lang,'ch_pro_desc')}</div>
              </div>
          )}

          {/* ── PAIEMENTS ── */}
          {onglet === 'paiements' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
                <div style={{ background: '#f0faf5', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{t(lang,'loc_total_encaisse')}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1D9E75' }}>{fmt(totalLoyers)}</div>
                </div>
                <div style={{ background: loyersEnRetard > 0 ? '#faece7' : '#f0faf5', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{t(lang,'retard_tab')}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: loyersEnRetard > 0 ? '#D85A30' : '#0F6E56' }}>{loyersEnRetard}</div>
                </div>
              </div>
              {paiements.length === 0
                ? <div className="empty">{t(lang,'loc_aucun_paiement')}</div>
                : paiements.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{fmtDate(p.mois, { month: 'long', year: 'numeric' })}</div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{fmt(p.montant)}</div>
                      <Badge statut={p.statut} montant={p.montant} montantPaye={p.montant_paye} lang={lang} />
                    </div>
                  ))
              }
            </>
          )}

        </div>
      </div>
    </div>
  )
}

function LocationsPage({ userId, biens, locataires, proprio, getToken, plan, onRefresh, lang = 'fr' }) {
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [filtre, setFiltre] = useState('actives')
  const [form, setForm] = useState({
    bien_id: '', locataire_id: '', date_debut: '', date_fin: '',
    loyer: '', charges: 0, garantie: '', type_bail_id: '', notes: ''
  })
  const [saving, setSaving] = useState(false)
  const [typesBail, setTypesBail] = useState([])
  const [editLocation, setEditLocation] = useState(null)
  const [editForm, setEditForm] = useState(null)
  const [editSaving, setEditSaving] = useState(false)
  const [panelLocation, setPanelLocation] = useState(null)

  const loadData = useCallback(async () => {
    const { data } = await supabase
      .from('locations')
      .select('*, biens(adresse, type_bien), locataires(prenom, nom, email), types_bail(nom)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    setLocations(data || [])
    setLoading(false)
  }, [userId])

  useEffect(() => { loadData() }, [loadData])

  useEffect(() => {
    supabase.from('types_bail').select('id, nom').eq('user_id', userId)
      .then(({ data }) => setTypesBail(data || []))
  }, [userId])

  const locatairesActifs = locataires.filter(l => !l.date_sortie)

  const handleBienChange = (bienId) => {
    const bien = biens.find(b => b.id === bienId)
    setForm(f => ({ ...f, bien_id: bienId, loyer: bien?.loyer || '', charges: bien?.charges || 0 }))
  }

  const handleSave = async () => {
    if (!form.bien_id || !form.locataire_id || !form.date_debut || !form.loyer) return
    setSaving(true)
    await supabase.from('locations').insert({
      user_id:      userId,
      bien_id:      form.bien_id,
      locataire_id: form.locataire_id,
      date_debut:   form.date_debut,
      date_fin:     form.date_fin || null,
      loyer:        parseFloat(form.loyer),
      charges:      parseFloat(form.charges) || 0,
      garantie:     form.garantie ? parseFloat(form.garantie) : null,
      type_bail_id: form.type_bail_id || null,
      notes:        form.notes || null,
      statut:       'active',
    })
    setSaving(false)
    setShowForm(false)
    setForm({ bien_id: '', locataire_id: '', date_debut: '', date_fin: '', loyer: '', charges: 0, garantie: '', type_bail_id: '', notes: '' })
    loadData()
  }

  const handleTerminer = async (loc) => {
    if (!window.confirm(`Terminer la location de ${loc.locataires?.prenom} ${loc.locataires?.nom} ?`)) return
    await supabase.from('locations').update({
      statut:   'terminee',
      date_fin: new Date().toISOString().split('T')[0],
      updated_at: new Date().toISOString(),
    }).eq('id', loc.id)
    loadData()
  }

  const handleArchiver = async (loc) => {
    await supabase.from('locations').update({ statut: 'archivee' }).eq('id', loc.id)
    loadData()
  }

  const handleReactiver = async (loc) => {
    await supabase.from('locations').update({ statut: 'active', date_fin: null }).eq('id', loc.id)
    loadData()
  }

  const locationsFiltrees = locations.filter(l => {
    if (filtre === 'actives')  return l.statut === 'active'
    if (filtre === 'terminees') return l.statut === 'terminee'
    if (filtre === 'archivees') return l.statut === 'archivee'
    return true
  })

  const duree = (loc) => {
    const debut = new Date(loc.date_debut)
    const fin   = loc.date_fin ? new Date(loc.date_fin) : new Date()
    const mois  = Math.round((fin - debut) / (1000 * 60 * 60 * 24 * 30))
    return mois < 12 ? `${mois} mois` : `${Math.floor(mois/12)} an${Math.floor(mois/12) > 1 ? 's' : ''}`
  }

  if (loading) return <div className="empty">{t(lang,'chargement')}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {editLocation && editForm && (
  <div className="modal-overlay" onClick={() => setEditLocation(null)}>
    <div className="modal" style={{ maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
      <div className="modal-title">{t(lang,'loc_modifier_titre')}</div>

      <div className="form-grid">
        <div className="form-group">
          <label>{t(lang,'bien_lbl')}</label>
          <select value={editForm.bien_id} onChange={e => {
            const bien = biens.find(b => b.id === e.target.value)
            setEditForm(f => ({ ...f, bien_id: e.target.value, loyer: bien?.loyer || f.loyer }))
          }}>
            {biens.map(b => <option key={b.id} value={b.id}>{b.adresse.split(',')[0]}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>{t(lang,'locataire_lbl')}</label>
          <select value={editForm.locataire_id} onChange={e => setEditForm(f => ({ ...f, locataire_id: e.target.value }))}>
            {locataires.map(l => <option key={l.id} value={l.id}>{l.prenom} {l.nom}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>{t(lang,'loc_debut')}</label>
          <input type="date" value={editForm.date_debut} onChange={e => setEditForm(f => ({ ...f, date_debut: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>{t(lang,'loc_fin')}</label>
          <input type="date" value={editForm.date_fin || ''} onChange={e => setEditForm(f => ({ ...f, date_fin: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>{t(lang,'loyer')}</label>
          <input type="number" value={editForm.loyer} onChange={e => setEditForm(f => ({ ...f, loyer: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>{t(lang,'charges')}</label>
          <input type="number" value={editForm.charges} onChange={e => setEditForm(f => ({ ...f, charges: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>{t(lang,'garantie_lbl')}</label>
          <input type="number" value={editForm.garantie || ''} onChange={e => setEditForm(f => ({ ...f, garantie: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>{t(lang,'type_bail')}</label>
          <select value={editForm.type_bail_id || ''} onChange={e => setEditForm(f => ({ ...f, type_bail_id: e.target.value || null }))}>
            <option value="">{t(lang,'aucun')}</option>
            {typesBail.map(tb => <option key={tb.id} value={tb.id}>{tb.nom}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>{t(lang,'date_sign')}</label>
          <input type="date" value={editForm.date_signature || ''} onChange={e => setEditForm(f => ({ ...f, date_signature: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>{t(lang,'indice_ref')}</label>
          <input type="number" placeholder={t(lang,'ph_indice_ref')} value={editForm.indice_reference || ''} onChange={e => setEditForm(f => ({ ...f, indice_reference: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>{t(lang,'nom_indice')}</label>
          <input placeholder={t(lang,'ph_indice_nom')} value={editForm.indice_nom || ''} onChange={e => setEditForm(f => ({ ...f, indice_nom: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>{t(lang,'statut')}</label>
          <select value={editForm.statut} onChange={e => setEditForm(f => ({ ...f, statut: e.target.value }))}>
            <option value="active">{t(lang,'loc_active')}</option>
            <option value="terminee">{t(lang,'loc_terminee')}</option>
            <option value="archivee">{t(lang,'loc_archivee')}</option>
          </select>
        </div>
        <div className="form-group full">
          <label>{t(lang,'notes')}</label>
          <input value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes internes..." />
        </div>
      </div>

      {/* ── Section bail PDF ── */}
      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0d2b1f' }}>{t(lang,'loc_bail_signe')}</div>
        {editLocation.bail_url ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f0faf5', border: '1px solid #9FE1CB', borderRadius: 8, padding: '10px 14px' }}>
            <span style={{ fontSize: 13, color: '#0F6E56', flex: 1 }}>{t(lang,'loc_bail_attache')}</span>
            <a href={editLocation.bail_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-ghost">⬇️ Télécharger</a>
            <button className="btn btn-sm btn-danger" onClick={() => setEditForm(f => ({ ...f, bail_url: null }))}>🗑️</button>
          </div>
        ) : (
          <label style={{ cursor: 'pointer' }}>
            <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={async (e) => {
              const file = e.target.files?.[0]
              if (!file) return
              const path = `${userId}/locations/${editLocation.id}/bail.pdf`
              const { error } = await supabase.storage.from('documents').upload(path, file, { upsert: true })
              if (!error) {
                const { data: urlData } = supabase.storage.from('documents').getPublicUrl(path)
                setEditForm(f => ({ ...f, bail_url: urlData.publicUrl }))
              }
            }} />
            <div style={{ border: '2px dashed #d0e8df', borderRadius: 8, padding: '14px', textAlign: 'center', fontSize: 13, color: '#888' }}>
              `📎 {t(lang,'loc_attacher_bail')}`
            </div>
          </label>
        )}
      </div>

      <div className="modal-actions">
        <button className="btn btn-ghost" onClick={() => setEditLocation(null)}>{t(lang,'annuler_btn')}</button>
        <button className="btn btn-primary" disabled={editSaving} onClick={async () => {
          setEditSaving(true)
          await supabase.from('locations').update({
            bien_id:          editForm.bien_id,
            locataire_id:     editForm.locataire_id,
            date_debut:       editForm.date_debut,
            date_fin:         editForm.date_fin || null,
            loyer:            parseFloat(editForm.loyer),
            charges:          parseFloat(editForm.charges) || 0,
            garantie:         editForm.garantie ? parseFloat(editForm.garantie) : null,
            type_bail_id:     editForm.type_bail_id || null,
            date_signature:   editForm.date_signature || null,
            indice_reference: editForm.indice_reference ? parseFloat(editForm.indice_reference) : null,
            indice_nom:       editForm.indice_nom || null,
            statut:           editForm.statut,
            notes:            editForm.notes || null,
            bail_url:         editForm.bail_url || null,
            updated_at:       new Date().toISOString(),
          }).eq('id', editLocation.id)
          setEditSaving(false)
          setEditLocation(null)
          loadData()
        }}>
          {editSaving ? t(lang,'enreg_en_cours') : t(lang,'enregistrer')}
        </button>
            </div>
          </div>
        </div>
      )}
      <div className="page-header-row">
        <div>
          <div className="page-title">📋 {t(lang,'locations')}</div>
          <div className="page-sub">{locations.filter(l => l.statut === 'active').length} {t(lang,'actifs')} · {locations.filter(l => l.statut === 'terminee').length} {t(lang,'loc_terminee')}</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? t(lang,'annuler') : t(lang,'loc_nouvelle')}
        </button>
      </div>
       {panelLocation && (
        <LocationPanel
          location={panelLocation}
          biens={biens}
          locataires={locataires}
          typesBail={typesBail}
          userId={userId}
          proprio={proprio}
          getToken={getToken}
          plan={plan}
          onClose={() => setPanelLocation(null)}
          onRefresh={() => { loadData(); onRefresh() }}
          lang={lang}
        />
      )}

      {/* Formulaire */}
      {showForm && (
        <div className="form-card green">
          <h3>📋 {t(lang,'loc_nouvelle').replace('+ ','')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Bien *</label>
              <select value={form.bien_id} onChange={e => handleBienChange(e.target.value)}>
                <option value="">{t(lang,'selectionner')}</option>
                {biens.map(b => <option key={b.id} value={b.id}>{b.adresse.split(',')[0]}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Locataire *</label>
              <select value={form.locataire_id} onChange={e => setForm(f => ({ ...f, locataire_id: e.target.value }))}>
                <option value="">{t(lang,'selectionner')}</option>
                {locatairesActifs.map(l => <option key={l.id} value={l.id}>{l.prenom} {l.nom}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t(lang,'loc_debut')} *</label>
              <input type="date" value={form.date_debut} onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t(lang,'loc_fin')}</label>
              <input type="date" value={form.date_fin} onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t(lang,'loyer')} *</label>
              <input type="number" value={form.loyer} onChange={e => setForm(f => ({ ...f, loyer: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t(lang,'charges')}</label>
              <input type="number" value={form.charges} onChange={e => setForm(f => ({ ...f, charges: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t(lang,'garantie_lbl')}</label>
              <input type="number" value={form.garantie} placeholder={`${(parseFloat(form.loyer)||0) * 2}`} onChange={e => setForm(f => ({ ...f, garantie: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t(lang,'type_bail')}</label>
              <select value={form.type_bail_id} onChange={e => setForm(f => ({ ...f, type_bail_id: e.target.value }))}>
                <option value="">{t(lang,'aucun')}</option>
                {typesBail.map(tb => <option key={tb.id} value={tb.id}>{tb.nom}</option>)}
              </select>
            </div>
            <div className="form-group full">
              <label>{t(lang,'notes')}</label>
              <input placeholder={t(lang,'ph_notes')} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.bien_id || !form.locataire_id || !form.date_debut || !form.loyer}>
              {saving ? t(lang,'enreg_en_cours') : t(lang,'loc_creer')}
            </button>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="tabs">
        <button className={`tab ${filtre === 'actives' ? 'active' : ''}`} onClick={() => setFiltre('actives')}>
          {t(lang,'actives_tab')} ({locations.filter(l => l.statut === 'active').length})
        </button>
        <button className={`tab ${filtre === 'terminees' ? 'active' : ''}`} onClick={() => setFiltre('terminees')}>
          {t(lang,'loc_terminees')} ({locations.filter(l => l.statut === 'terminee').length})
        </button>
        <button className={`tab ${filtre === 'archivees' ? 'active' : ''}`} onClick={() => setFiltre('archivees')}>
          {t(lang,'loc_archivees')} ({locations.filter(l => l.statut === 'archivee').length})
        </button>
        <button className={`tab ${filtre === 'toutes' ? 'active' : ''}`} onClick={() => setFiltre('toutes')}>
          {t(lang,'toutes')} ({locations.length})
        </button>
      </div>

      {/* Liste */}
      {locationsFiltrees.length === 0
        ? <div className="card"><div className="empty">{t(lang,'loc_aucune')}</div></div>
        : locationsFiltrees.map(loc => (
          <div key={loc.id} className="card">
            <div className="card-header">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="card-title">
                    {loc.locataires?.prenom} {loc.locataires?.nom}
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20,
                    background: loc.statut === 'active' ? '#e1f5ee' : loc.statut === 'terminee' ? '#faeeda' : '#f0f0f0',
                    color:      loc.statut === 'active' ? '#0F6E56' : loc.statut === 'terminee' ? '#854F0B' : '#999',
                  }}>
                    {loc.statut === 'active' ? `✓ ${t(lang,'actifs_tab')}` : loc.statut === 'terminee' ? t(lang,'loc_terminee') : t(lang,'loc_archivee')}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#888' }}>
                  🏠 {loc.biens?.adresse?.split(',')[0]}
                  {loc.types_bail && <span style={{ marginLeft: 8 }}>· 📋 {loc.types_bail.nom}</span>}
                </div>
              </div>
              <div className="row-actions">
                <button className="btn btn-sm btn-ghost" style={{ color: '#185FA5', borderColor: '#B5D4F4' }}
                  onClick={() => setPanelLocation(loc)}>
                  {t(lang,'loc_ouvrir')}
                </button>
                {loc.statut === 'active' && (
                  <button className="btn btn-sm btn-ghost" style={{ color: '#854F0B', borderColor: '#F5D08A' }} onClick={() => handleTerminer(loc)}>
                    {t(lang,'loc_terminer')}
                  </button>
                )}
                {loc.statut === 'terminee' && (
                  <>
                    <button className="btn btn-sm btn-ghost" onClick={() => handleReactiver(loc)}>{t(lang,'loc_reactiver')}</button>
                    <button className="btn btn-sm btn-ghost" style={{ color: '#aaa' }} onClick={() => handleArchiver(loc)}>{t(lang,'loc_archiver')}</button>
                  </>
                )}
                <button className="btn btn-sm btn-ghost" onClick={() => {
                  setEditLocation(loc)
                  setEditForm({
                    bien_id:          loc.bien_id,
                    locataire_id:     loc.locataire_id,
                    date_debut:       loc.date_debut,
                    date_fin:         loc.date_fin || '',
                    loyer:            loc.loyer,
                    charges:          loc.charges,
                    garantie:         loc.garantie || '',
                    type_bail_id:     loc.type_bail_id || '',
                    date_signature:   loc.date_signature || '',
                    indice_reference: loc.indice_reference || '',
                    indice_nom:       loc.indice_nom || '',
                    statut:           loc.statut,
                    notes:            loc.notes || '',
                    bail_url:         loc.bail_url || null,
                  })
                }}>✏️</button>
              </div>
            </div>

            <div style={{ padding: '16px 22px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t(lang,'loc_loyer_charges')}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0d2b1f' }}>{fmt(loc.loyer)}</div>
                {loc.charges > 0 && <div style={{ fontSize: 12, color: '#aaa' }}>+ {fmt(loc.charges)} charges</div>}
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t(lang,'loc_debut')}</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{fmtDate(loc.date_debut)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  {loc.date_fin ? t(lang,'loc_fin') : t(lang,'loc_duree')}
                </div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  {loc.date_fin ? fmtDate(loc.date_fin) : duree(loc)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t(lang,'garantie_lbl')}</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{loc.garantie ? fmt(loc.garantie) : '—'}</div>
              </div>
            </div>

            {loc.notes && (
              <div style={{ padding: '0 22px 16px', fontSize: 13, color: '#888', fontStyle: 'italic' }}>
                📝 {loc.notes}
              </div>
            )}
          </div>
        ))
      }
    </div>
    
  )
}


function CorbeillePage({ userId, onRefresh, lang = 'fr' }) {
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(true)
  const [filtre, setFiltre]   = useState('tous')
  const [confirmDelete, setConfirmDelete] = useState(null)

  const loadData = useCallback(async () => {
    const [{ data: biens }, { data: locataires }, { data: locations }] = await Promise.all([
      supabase.from('biens').select('*').eq('user_id', userId).not('deleted_at', 'is', null),
      supabase.from('locataires').select('*').not('deleted_at', 'is', null),
      supabase.from('locations').select('*, biens(adresse), locataires(prenom, nom)').eq('user_id', userId).not('deleted_at', 'is', null),
    ])
    const all = [
      ...(biens      || []).map(b => ({ ...b, _type: 'bien',      _label: b.adresse,                                         _icon: '🏠' })),
      ...(locataires || []).map(l => ({ ...l, _type: 'locataire', _label: `${l.prenom} ${l.nom}`,                            _icon: '👤' })),
      ...(locations  || []).map(l => ({ ...l, _type: 'location',  _label: `${l.locataires?.prenom} ${l.locataires?.nom} · ${l.biens?.adresse?.split(',')[0]}`, _icon: '📋' })),
    ].sort((a, b) => new Date(b.deleted_at) - new Date(a.deleted_at))
    setItems(all)
    setLoading(false)
  }, [userId])

  useEffect(() => { loadData() }, [loadData])

  const daysLeft = (deletedAt) => {
    const diff = 30 - Math.floor((Date.now() - new Date(deletedAt)) / (1000*60*60*24))
    return Math.max(0, diff)
  }

  const handleRestore = async (item) => {
    await supabase.from(
      item._type === 'bien' ? 'biens' : item._type === 'locataire' ? 'locataires' : 'locations'
    ).update({ deleted_at: null }).eq('id', item.id)
    onRefresh()
    loadData()
  }

  const handleDeletePermanent = async (item) => {
    await supabase.from(
      item._type === 'bien' ? 'biens' : item._type === 'locataire' ? 'locataires' : 'locations'
    ).delete().eq('id', item.id)
    setConfirmDelete(null)
    loadData()
  }

  const filtered = filtre === 'tous' ? items : items.filter(i => i._type === filtre)

  const counts = {
    tous:       items.length,
    bien:       items.filter(i => i._type === 'bien').length,
    locataire:  items.filter(i => i._type === 'locataire').length,
    location:   items.filter(i => i._type === 'location').length,
  }

  if (loading) return <div className="empty">{t(lang,'chargement')}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{t(lang,'corbeille_suppr_def')}</div>
            <div className="modal-confirm-text">
              {t(lang,'corbeille_suppr_confirm')} <strong>{confirmDelete._label}</strong> {t(lang,'corbeille_irrev')}
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>{t(lang,'annuler')}</button>
              <button className="btn btn-danger" onClick={() => handleDeletePermanent(confirmDelete)}>{t(lang,'corbeille_suppr_btn')}</button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header-row">
        <div>
          <div className="page-title">🗑️ {t(lang,'corbeille')}</div>
          <div className="page-sub">{t(lang,'corbeille_sous_titre')}</div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card">
          <div className="empty">{t(lang,'corbeille_vide')}</div>
        </div>
      ) : (
        <>
          <div className="tabs">
            {[
              { key: 'tous',      label: t(lang,'tous'), count: counts.tous },
              { key: 'bien',      label: `🏠 ${t(lang,'corbeille_biens')}`, count: counts.bien },
              { key: 'locataire', label: `👤 ${t(lang,'corbeille_locataires')}`, count: counts.locataire },
              { key: 'location',  label: `📋 ${t(lang,'corbeille_locations')}`, count: counts.location },
            ].filter(f => f.key === 'tous' || f.count > 0).map(f => (
              <button key={f.key}
                className={`tab ${filtre === f.key ? 'active' : ''}`}
                onClick={() => setFiltre(f.key)}
              >
                {f.label} ({f.count})
              </button>
            ))}
          </div>

          <div className="card">
            {filtered.map(item => {
              const days = daysLeft(item.deleted_at)
              return (
                <div className="row" key={`${item._type}-${item.id}`}>
                  <div style={{ fontSize: 24, flexShrink: 0 }}>{item._icon}</div>
                  <div className="row-main">
                    <div className="row-name">{item._label}</div>
                    <div className="row-sub">
                      Supprimé le {new Date(item.deleted_at).toLocaleDateString(_langLocale)}
                      {' · '}
                      <span style={{ color: days <= 5 ? '#D85A30' : '#aaa' }}>
                        {days > 0
    ? `${t(lang, 'corbeille_dans')} ${days} jour${days > 1 ? 's' : ''}`
    : t(lang, 'corbeille_imminente')}
                      </span>
                    </div>
                  </div>
                  <div className="row-actions">
                    <button className="btn btn-sm btn-ghost" style={{ color: '#1D9E75', borderColor: '#9FE1CB' }} onClick={() => handleRestore(item)}>
                      {t(lang,'corbeille_restaurer')}
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => setConfirmDelete(item)}>
                      🗑️ {t(lang,'suppr_btn')}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// ─── APP ─────────────────────────────────────────────────────
  export default function App() {
  const [user, setUser]             = useState(null)
  const [page, setPage]             = useState('dashboard')
  const [biens, setBiens]           = useState([])
  const [paiements, setPaiements]   = useState([])
  const [locataires, setLocataires] = useState([])
  const [plan, setPlan]             = useState('free')
  const [planCustomLabel, setPlanCustomLabel] = useState(null)
  const [planCustomBiens, setPlanCustomBiens] = useState(null)
  const [proprio, setProprio]       = useState(null)
  const [lang, setLang] = useState(() => {
    const urlLang = new URLSearchParams(window.location.search).get('lang')
    if (urlLang && ['fr','en','nl'].includes(urlLang)) return urlLang
    const saved = localStorage.getItem('domavix_lang')
    if (saved && ['fr','en','nl'].includes(saved)) return saved
    return 'fr'
  })
  const [nonLusTotal, setNonLusTotal] = useState(0)
  const [alertesCount, setAlertesCount] = useState(0)
  const [loading, setLoading]       = useState(true)
  const interventionsEnabled = proprio?.interventions_enabled !== false
  const moisCourant = `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}`
  const todoUrgent = paiements.filter(p =>
    p.statut === 'late' ||
    (p.statut === 'pending' && p.mois?.slice(0,7) < moisCourant)
  ).length

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => { setUser(session?.user ?? null); setLoading(false) })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null)

      // Écrire cgu_accepted_at après confirmation email
  if (session?.user && _e === 'SIGNED_IN') {
    const pending = localStorage.getItem('pending_cgu')
    if (pending) {
      const { cguTs, lang: pendingLang } = JSON.parse(pending)
      supabase.from('profiles')
        .update({ cgu_accepted_at: cguTs, lang: pendingLang })
        .eq('id', session.user.id)
        .then(() => localStorage.removeItem('pending_cgu'))
    }
  }
      if (session?.user) {
        const savedLang = localStorage.getItem('domavix_lang')
        if (savedLang && ['fr','en','nl'].includes(savedLang)) {
          setLang(savedLang)
        }
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  const loadPlan = async (userId) => {
  const { data } = await supabase.from('profiles')
    .select('id, plan, plan_cancel_at, proprio_nom, proprio_adresse, proprio_ville, proprio_telephone, currency, locale, lang, interventions_enabled, plan_custom, plan_custom_biens, plan_custom_label, plan_custom_prix')
    .eq('id', userId).single()
  if (data) {
    if (data.plan_cancel_at && new Date(data.plan_cancel_at) < new Date() && data.plan !== 'free') {
      await supabase.from('profiles').update({ plan: 'free', plan_cancel_at: null }).eq('id', userId)
      data.plan = 'free'
      data.plan_cancel_at = null
    }
    if (data.plan_custom) {
      setPlan('enterprise')
      setPlanCustomLabel(data.plan_custom_label || 'Enterprise')
      setPlanCustomBiens(data.plan_custom_biens || Infinity)
    } else if (data.plan) {
      setPlan(data.plan)
    }
    if (data.lang) setLang(data.lang)
    setProprio(data)
    setCurrencyContext(data.currency || 'EUR', data.locale || 'fr-FR')
  }
  }
 
  const loadData = async () => {
    const [{ data: b }, { data: p }, { data: l }] = await Promise.all([
      supabase.from('biens').select('*').is('deleted_at', null).order('created_at', { ascending: false }),
      supabase.from('paiements').select('*, bien_id, biens(adresse, jour_echeance), locataires(nom, prenom)').order('mois', { ascending: false }),
      supabase.from('locataires').select('*').is('deleted_at', null).order('created_at', { ascending: false }),
    ])
    const biensList = b || []
    const locList   = l || []
    setBiens(biensList); setLocataires(locList); setPaiements(p || [])

    // Compter les messages non lus
    const { count } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('proprio_user_id', (await supabase.auth.getUser()).data.user?.id)
      .eq('expediteur', 'locataire')
      .eq('lu', false)
    setNonLusTotal(count || 0)

    // Compter les alertes échéances non lues
    const { count: ac } = await supabase
      .from('alertes')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
      .eq('lu', false)
    setAlertesCount(ac || 0)

    // Génération silencieuse au 1er du mois
    const actifs = locList.filter(loc => !loc.date_sortie)
    await generateMissingPaiements(actifs, biensList)

    // Rechargement après génération
    const { data: p2 } = await supabase
      .from('paiements')
      .select('*, biens(adresse, jour_echeance), locataires(nom, prenom)')
      .order('mois', { ascending: false })
    setPaiements(p2 || [])
  }

  useEffect(() => { if (user) { loadPlan(user.id); loadData() } }, [user])

  // Marquer payé ou en retard
  const handleStatutChange = async (id, s) => {
    await supabase.from('paiements').update({
      statut:        s,
      montant_paye:  null,
      date_paiement: s === 'paid' ? new Date().toISOString().split('T')[0] : null,
    }).eq('id', id)
    loadData()
  }

  // Détecter retour OAuth Ponto
  useEffect(() => {
    if (!user) return
    const urlParams = new URLSearchParams(window.location.search)
    const pontoStatus = urlParams.get('ponto')
    if (pontoStatus === 'success') {
      setPage('parametres')
      window.history.replaceState({}, '', '/')
    } else if (pontoStatus === 'error') {
      alert(`${t(lang,'erreur_generique')} Ponto : ${urlParams.get('msg')}`)
      window.history.replaceState({}, '', '/')
    }
  }, [user, lang])

  // Paiement partiel
  const handlePartialChange = async (id, montantPaye) => {
    await supabase.from('paiements').update({
      statut:        'partial',
      montant_paye:  montantPaye,
      date_paiement: new Date().toISOString().split('T')[0],
    }).eq('id', id)
    loadData()
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setUser(null); setBiens([]); setPaiements([]); setLocataires([]); setPlan('free')
  }

  const [resetSent, setResetSent] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)

  const handleResetPassword = async () => {
    setResetLoading(true)
    await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: 'https://domavix.com/app',
    })
    setResetLoading(false)
    setResetSent(true)
    setTimeout(() => setResetSent(false), 4000)
  }

  // Tab bar mobile — 4 onglets principaux + "Plus"
  const MOBILE_TABS = [
  { key: 'dashboard',  icon: '📊', label: t(lang,'nav_accueil') },
  { key: 'todo',       icon: '✅', label: t(lang,'todo') },
  { key: 'paiements',  icon: '💶', label: t(lang,'nav_loyers') },
  { key: 'locataires', icon: '👤', label: t(lang,'locataires') },
  { key: 'more',       icon: '☰',  label: t(lang,'nav_plus') },
]

  const [showMoreMenu, setShowMoreMenu] = useState(false)

  const MORE_PAGES = [
  { key: 'biens',         icon: '🏠', label: t(lang,'mes_biens') },
  { key: 'locations',     icon: '📋', label: t(lang,'locations') },
  { key: 'messagerie',    icon: '💬', label: t(lang,'nav_messages') },
  { key: 'echeances',     icon: '📅', label: t(lang,'ech_titre') },
  { key: 'notifications', icon: '🔔', label: t(lang,'nav_notifications') },
  { key: 'interventions', icon: '🔧', label: t(lang,'interv_titre') },
  { key: 'parametres',    icon: '⚙️', label: t(lang,'param_titre') },
  { key: 'corbeille',     icon: '🗑️', label: t(lang,'corbeille') },
  { key: 'pricing',       icon: '⭐', label: t(lang,'nav_abonnement') },
].filter(p => plan === 'pro' || plan === 'enterprise' || !PRO_ONLY_PAGES.includes(p.key))
 .filter(p => p.key !== 'interventions' || interventionsEnabled)

  if (loading) return <div className="loading">{t(lang,'chargement')}</div>

  return (
    <>
      <style>{STYLES}</style>
      {!user ? <AuthPage onAuth={setUser} /> : (
      <div className="app">
        <div className="sidebar">
          <div className="logo"><h1>Domavix</h1><p>{t(lang,'gestion_loc')}</p><PlanBadge plan={plan} lang={lang} /></div>
        {PAGES.map((p, i) => {
          const isProLocked = plan !== 'pro' && plan !== 'enterprise' && PRO_ONLY_PAGES.includes(p)
          const isStarterLocked = plan === 'free' && STARTER_PLUS_PAGES.includes(p)
          if (p === 'interventions' && !interventionsEnabled) return null
          if (p === 'indexation' && !biens.some(b => PAYS_INDEXATION.includes(b.pays || 'BE'))) return null
          return (
            <div key={p} className={`nav-item ${page === p ? 'active' : ''}`} onClick={() => setPage(p)} style={isProLocked ? { opacity: 0.55 } : {}}>
              <span>{PAGE_ICONS[i]}</span> {t(lang, PAGE_LABEL_KEYS[i])}
              {isProLocked && (
                <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 700, background: '#B8860B', color: '#fff', padding: '2px 6px', borderRadius: 4, letterSpacing: 0.5 }}>PRO</span>
              )}
              {isStarterLocked && (
                <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 700, background: '#0F6E56', color: '#fff', padding: '2px 6px', borderRadius: 4, letterSpacing: 0.5 }}>STARTER</span>
              )}
              {p === 'messagerie' && nonLusTotal > 0 && (
                <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, background: '#D85A30', color: '#fff', padding: '2px 7px', borderRadius: 20 }}>{nonLusTotal}</span>
              )}
              {p === 'echeances' && alertesCount > 0 && (
                <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, background: '#854F0B', color: '#fff', padding: '2px 7px', borderRadius: 20 }}>{alertesCount}</span>
              )}
              {p === 'todo' && todoUrgent > 0 && (
                <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, background: '#D85A30', color: '#fff', padding: '2px 7px', borderRadius: 20 }}>{todoUrgent}</span>
              )}
            </div>
          )
        })}
        
          <div className="sidebar-footer">
            <div className="user-email">{user.email}</div>
           <a href={lang === 'en' ? 'https://tally.so/r/44z1Wd' : lang === 'nl' ? 'https://tally.so/r/44z1Wd' : 'https://tally.so/r/44z1Wd'}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 11,
              color: 'rgba(255,255,255,0.4)',
              textDecoration: 'none',
              marginTop: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              borderRadius: 8,
              background: 'rgba(255,255,255,0.05)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
          >
            <span>💬</span>
            <span>{t(lang,'contact_support')}</span>
          </a>
            <div style={{ display: 'flex', gap: 4, marginTop: 10, marginBottom: 4 }}>
            {LANGS.map(l => {
  const handleLangClick = async () => {
  setLang(l.code)
  setLangLocale(l.code)
  const localeMap = { fr: 'fr-FR', en: 'en-GB', nl: 'nl-BE' }
  const newLocale = localeMap[l.code] || 'fr-FR'
  await supabase.from('profiles').update({ lang: l.code, locale: newLocale }).eq('id', user.id)
  }
  return (
    <button key={l.code}
      onClick={handleLangClick}
      style={{
        fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
        cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
        textTransform: 'uppercase', letterSpacing: '0.06em', border: 'none',
        background: lang === l.code ? 'rgba(29,158,117,0.25)' : 'rgba(255,255,255,0.06)',
        color: lang === l.code ? '#1D9E75' : 'rgba(255,255,255,0.35)',
      }}
    >
      {l.code.toUpperCase()}
    </button>
  )
})}
          </div>

            {resetSent
            ? <div style={{ fontSize: 11, color: '#1D9E75', marginTop: 6 }}>{t(lang, 'email_envoye')}</div>
            : <button className="logout-btn" onClick={handleResetPassword} disabled={resetLoading}>
                {resetLoading ? '...' : t(lang, 'chgt_mdp')}
              </button>
            }
            <button className="logout-btn" onClick={handleLogout}>{t(lang, 'deconnexion')}</button>
            <a href="/legal.html" target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', marginTop: 12, display: 'block', textDecoration: 'none' }}>CGV · Mentions légales · RGPD</a>
          </div>
        </div>
        <div className="main">
          {/* Header mobile */}
          <div className="mobile-header">
            <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, color: '#0d2b1f', lineHeight: 1.1 }}>Dom<em style={{ color: '#1D9E75', fontStyle: 'italic' }}>avix</em></div>
            <PlanBadge plan={plan} customLabel={planCustomLabel} lang={lang} />
          </div>
          {page === 'dashboard' && <Dashboard biens={biens} paiements={paiements} locataires={locataires} setPage={setPage} plan={plan} lang={lang} userId={user.id} />}
          {page === 'todo' && <TodoPage userId={user.id} plan={plan} setPage={setPage} biens={biens} paiements={paiements} lang={lang} />}
          {page === 'calendrier' && (plan !== 'free' ? <CalendrierPage userId={user.id} biens={biens} lang={lang} setPage={setPage} /> : <ProUpgradePrompt onGoToPricing={() => setPage('pricing')} lang={lang} titleKey="starter_feature_titre" descKey="cal_starter_desc" btnKey="voir_plans" />)}
          {page === 'biens'          && <Biens biens={biens} planCustomBiens={planCustomBiens} onRefresh={loadData} userId={user.id} plan={plan} userEmail={user.email} onGoToPricing={() => setPage('pricing')} lang={lang} />}
          {page === 'locataires' && <Locataires locataires={locataires} biens={biens} onRefresh={loadData} plan={plan} planCustomBiens={planCustomBiens} userEmail={user.email} userId={user.id} proprio={proprio} onGoToPricing={() => setPage('pricing')} lang={lang} />}
          {page === 'locations' && <LocationsPage userId={user.id} biens={biens} locataires={locataires} proprio={proprio} getToken={getToken} plan={plan} onRefresh={loadData} lang={lang} />}
          {page === 'paiements'      && <Paiements paiements={paiements} biens={biens} locataires={locataires} onPaiementAjoute={loadData} onStatutChange={handleStatutChange} onPartialChange={handlePartialChange} proprio={proprio} plan={plan} lang={lang} getToken={getToken} />}
          {page === 'messagerie'     && ((plan === 'pro' || plan === 'enterprise') ? <MessageriePage userId={user.id} locataires={locataires} lang={lang} /> : <ProUpgradePrompt onGoToPricing={() => setPage('pricing')} lang={lang} />)}
          {page === 'echeances'      && ((plan === 'pro' || plan === 'enterprise') ? <EcheancesPage userId={user.id} locataires={locataires} lang={lang} /> : <ProUpgradePrompt onGoToPricing={() => setPage('pricing')} lang={lang} />)}
          {page === 'indexation' && (plan !== 'free'
            ? <IndexationsPage userId={user.id} locataires={locataires} biens={biens} proprio={proprio} lang={lang} getToken={getToken} onRefresh={loadData}/>
            : <ProUpgradePrompt onGoToPricing={() => setPage('pricing')} lang={lang} titleKey="starter_feature_titre" descKey="idx_starter_desc" btnKey="voir_plans" />)}
          {page === 'baux'           && ((plan === 'pro' || plan === 'enterprise') ? <TypesBauxPage userId={user.id} locataires={locataires} onEcheancesUpdated={loadData} lang={lang} /> : <ProUpgradePrompt onGoToPricing={() => setPage('pricing')} lang={lang} />)}
          {page === 'notifications'  && ((plan === 'pro' || plan === 'enterprise') ? <NotificationsPage userId={user.id} lang={lang} /> : <ProUpgradePrompt onGoToPricing={() => setPage('pricing')} lang={lang} />)}
          {page === 'interventions' && ((plan === 'pro' || plan === 'enterprise' || plan === 'starter') && interventionsEnabled
              ? <InterventionsPage userId={user.id} locataires={locataires} biens={biens} lang={lang} />
              : <ProUpgradePrompt onGoToPricing={() => setPage('pricing')} lang={lang} titleKey="starter_feature_titre" descKey="starter_feature_desc" btnKey="voir_plans" />)}
          {page === 'parametres' && <ParametresPage userId={user.id} userEmail={user.email} lang={lang} onLangChange={setLang} plan={plan} setPage={setPage} />}
          {page === 'corbeille' && <CorbeillePage userId={user.id} onRefresh={loadData} lang={lang} />}
          {page === 'pricing' && <PricingPage userEmail={user.email} userId={user.id} currentPlan={plan} lang={lang} proprio={proprio} />}
        </div>

        {/* ── TAB BAR MOBILE ── */}
        <nav className="mobile-nav">
          {MOBILE_TABS.map(t => (
            <button
              key={t.key}
              className={`mobile-nav-item ${page === t.key || (t.key === 'more' && showMoreMenu) ? 'active' : ''}`}
              onClick={() => {
                if (t.key === 'more') { setShowMoreMenu(m => !m) }
                else { setPage(t.key); setShowMoreMenu(false) }
              }}
            >
              {t.key === 'messagerie' && nonLusTotal > 0 && <span className="mobile-nav-badge" />}
              <span className="nav-icon">{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </nav>

        {/* Menu "Plus" */}
        {showMoreMenu && (
          <div style={{
            position: 'fixed', bottom: 70, left: 0, right: 0, zIndex: 199,
            background: '#fff', borderTop: '1px solid #eee',
            borderRadius: '20px 20px 0 0',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.12)',
            padding: '8px 0 16px',
            maxHeight: 'calc(100vh - 140px)',
            overflowY: 'auto',
          }} onClick={() => setShowMoreMenu(false)}>
            <div style={{ width: 40, height: 4, background: '#e0e0e0', borderRadius: 2, margin: '8px auto 12px' }} />
            <div style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {MORE_PAGES.map(p => (
                <button key={p.key}
                  onClick={() => { setPage(p.key); setShowMoreMenu(false) }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '14px 16px', border: 'none', background: page === p.key ? '#f0faf5' : 'none',
                    borderRadius: 12, cursor: 'pointer', width: '100%',
                    font: "500 15px 'DM Sans', sans-serif", color: '#0d2b1f',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ fontSize: 22 }}>{p.icon}</span>
                  {p.label}
                </button>
              ))}
              <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />

              <a href={lang === 'en' ? 'https://tally.so/r/44z1Wd' : lang === 'nl' ? 'https://tally.so/r/44z1Wd' : 'https://tally.so/r/44z1Wd'}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px', textDecoration: 'none',
                borderRadius: 12, color: 'rgba(0,0,0,0.5)', fontSize: 15,
                fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
              }}
            >
              <span style={{ fontSize: 22 }}>💬</span>
              {t(lang,'contact_support')}
            </a>
              <button
                onClick={() => { handleLogout(); setShowMoreMenu(false) }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 16px', border: 'none', background: 'none',
                  borderRadius: 12, cursor: 'pointer', width: '100%',
                  font: "500 15px 'DM Sans', sans-serif", color: '#D85A30',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 22 }}>→</span>
                {t(lang, 'deconnexion')}
              </button>
            </div>
          </div>
        )}
        {showMoreMenu && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 198, background: 'rgba(0,0,0,0.3)' }}
            onClick={() => setShowMoreMenu(false)} />
        )}
      </div>
      )}
    </>
  )
}